import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaFileInvoice,
  FaSearch,
  FaEye,
  FaPrint,
  FaEnvelope,
  FaMoneyBill,
  FaDigitalOcean,
  FaDownload,
  FaTimes,
  FaCheckCircle,
  FaClock,
  FaTable,
  FaBox,
  FaUser,
  FaCalendarAlt,
  FaCreditCard,
  FaChevronLeft,
  FaChevronRight,
  FaBuilding,
  FaSync,
  FaHotel,
  FaEdit,
  FaUtensils,
  FaExchangeAlt,
  FaReceipt,
  FaLayerGroup,
  FaChevronDown,
  FaChevronUp,
  FaUserTie,
} from 'react-icons/fa';
import { MdTableBar } from 'react-icons/md';
import { RiBillLine } from 'react-icons/ri';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// ── AUTH ── read staff user id from localStorage (saved by OrderFood page)
const USER_ID_STORAGE_KEY = 'aq_user_id';
const TOKEN_STORAGE_KEYS = ['access_token', 'token', 'jwt', 'auth_token'];

const getStoredToken = () => {
  try {
    for (const k of TOKEN_STORAGE_KEYS) {
      const v = window.localStorage.getItem(k);
      if (v && v.length > 10) return v;
    }
  } catch (e) {
    /* noop */
  }
  return null;
};

const getStoredUserId = () => {
  try {
    const v = window.localStorage.getItem(USER_ID_STORAGE_KEY);
    if (!v) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch (e) {
    return null;
  }
};

const getAuthHeaders = () => {
  const token = getStoredToken();
  const headers = { accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const uid = getStoredUserId();
  if (uid) headers['X-User-Id'] = String(uid);
  return headers;
};

const getAuthJsonHeaders = () => {
  const headers = getAuthHeaders();
  headers['Content-Type'] = 'application/json';
  return headers;
};

// Helper: normalize an item's cancelled flag
const normalizeItemCancelled = (item) =>
  item.is_cancelled === true ||
  item.status === 'cancelled' ||
  item.status === 'Cancelled' ||
  item.cancelled === true;

// Map frontend date filter values to backend-expected values
const mapDateFilterForApi = (f) => {
  if (f === 'weekly' || f === 'monthly' || f === 'lastMonth') return 'custom';
  return f;
};

function OrderInvoicing() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterInhouse, setFilterInhouse] = useState('all'); // 'all' | 'inhouse' | 'exclude'
  const [filterDate, setFilterDate] = useState('today');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPrintingKOT, setIsPrintingKOT] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isRoomOrder, setIsRoomOrder] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  // Company search
  const [companies, setCompanies] = useState([]);
  const [companySearchText, setCompanySearchText] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const companyDropdownRef = useRef(null);

  // Edit guest
  const [showEditGuestModal, setShowEditGuestModal] = useState(false);
  const [editGuestData, setEditGuestData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    gst_number: '',
  });
  const [isUpdatingGuest, setIsUpdatingGuest] = useState(false);

  // Transfer
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferOrder, setTransferOrder] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferRoomNumber, setTransferRoomNumber] = useState('');
  const [transferBookingId, setTransferBookingId] = useState('');
  const [transferNewTable, setTransferNewTable] = useState('');
  const [transferMergeFrom, setTransferMergeFrom] = useState('');
  const [transferMergeTo, setTransferMergeTo] = useState('');
  const [transferActionType, setTransferActionType] = useState(null);

  // ── ROOM TRANSFER (room → room) ──
  const [transferToRoomNumber, setTransferToRoomNumber] = useState('');
  const [transferToRoomReason, setTransferToRoomReason] = useState('');

  // ── PARCEL MERGE (parcel → parcel) ──
  const [parcelMergeFrom, setParcelMergeFrom] = useState('');
  const [parcelMergeTo, setParcelMergeTo] = useState('');

  // ── AUDIT LOG ──
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [auditFilterEventType, setAuditFilterEventType] = useState('all');

  // ============ ROOM FOLIO POPUP STATE ============
  const [showRoomFolioModal, setShowRoomFolioModal] = useState(false);
  const [folioSearchQuery, setFolioSearchQuery] = useState('');
  const [folioFilterDate, setFolioFilterDate] = useState('today');
  const [folioCustomFrom, setFolioCustomFrom] = useState('');
  const [folioCustomTo, setFolioCustomTo] = useState('');
  const [expandedFolioKey, setExpandedFolioKey] = useState(null);
  const folioSearchInputRef = useRef(null);

  const [apiParams, setApiParams] = useState({
    property_id: null,
    order_type: null,
    order_status: null,
    date_from: null,
    date_to: null,
  });

  // ============ DATE HELPERS ============
  const getBackendDateKey = (dateString) => {
    if (!dateString) return null;
    if (typeof dateString === 'string') {
      const m = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${y}-${mo}-${da}`;
    }
    if (dateString instanceof Date) {
      if (isNaN(dateString.getTime())) return null;
      const y = dateString.getFullYear();
      const mo = String(dateString.getMonth() + 1).padStart(2, '0');
      const da = String(dateString.getDate()).padStart(2, '0');
      return `${y}-${mo}-${da}`;
    }
    return null;
  };

  const getLocalTodayKey = () => {
    const now = new Date();
    const y = now.getFullYear();
    const mo = String(now.getMonth() + 1).padStart(2, '0');
    const da = String(now.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}`;
  };

  const getLocalYesterdayKey = () => {
    const now = new Date();
    now.setDate(now.getDate() - 1);
    const y = now.getFullYear();
    const mo = String(now.getMonth() + 1).padStart(2, '0');
    const da = String(now.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}`;
  };

  const addDaysToKey = (key, delta) => {
    const [y, m, d] = key.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + delta);
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  const getConsolidatedDateRange = () => {
    const todayKey = getLocalTodayKey();
    const yesterdayKey = getLocalYesterdayKey();
    const weekStartKey = addDaysToKey(todayKey, -6);
    const monthStartKey = todayKey.slice(0, 8) + '01';

    const [yStr, mStr] = todayKey.split('-');
    const y = parseInt(yStr, 10);
    const m = parseInt(mStr, 10);
    const prevMonthYear = m === 1 ? y - 1 : y;
    const prevMonth = m === 1 ? 12 : m - 1;
    const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
    const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
    const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
      lastMonthEndDate.getMonth() + 1
    ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

    switch (folioFilterDate) {
      case 'today':
        return { filter: 'today', from: null, to: null };
      case 'yesterday':
        return { filter: 'yesterday', from: null, to: null };
      case 'weekly':
        return { filter: 'custom', from: weekStartKey, to: todayKey };
      case 'monthly':
        return { filter: 'custom', from: monthStartKey, to: todayKey };
      case 'lastMonth':
        return { filter: 'custom', from: lastMonthStartKey, to: lastMonthEndKey };
      case 'custom':
        return {
          filter: 'custom',
          from: folioCustomFrom || null,
          to: folioCustomTo || null,
        };
      case 'all':
      default:
        return { filter: 'all', from: null, to: null };
    }
  };

  // Razorpay
  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => toast.error('Error loading Razorpay script.');
      document.body.appendChild(script);
    };
    loadRazorpayScript();
  }, []);

  // Companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          setCompanies(data.companies || []);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    fetchCompanies();
  }, []);

  // Persist current user id so transfers/merges can be attributed in audit log
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (response.ok) {
          const userData = await response.json();
          if (userData.id) {
            try {
              window.localStorage.setItem(USER_ID_STORAGE_KEY, String(userData.id));
            } catch (e) {
              /* localStorage unavailable */
            }
          }
        }
      } catch (err) {
        console.warn('Could not fetch current user:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (companySearchText && companySearchText.length > 1) {
      const filtered = companies.filter((c) =>
        c.companyname.toLowerCase().includes(companySearchText.toLowerCase())
      );
      setFilteredCompanies(filtered);
      setShowCompanyDropdown(true);
    } else {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
    }
  }, [companySearchText, companies]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchAllOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatOrderDate = (dateString) => {
    if (!dateString) return { date: '—', time: '—' };
    try {
      const match = String(dateString).match(
        /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
      );
      if (match) {
        const [, y, mo, d, hh, mm] = match;
        const months = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const dateLabel = `${parseInt(d, 10)} ${months[parseInt(mo, 10) - 1]} ${y}`;
        let hour = parseInt(hh, 10);
        const minute = mm;
        const ampm = hour >= 12 ? 'pm' : 'am';
        hour = hour % 12;
        if (hour === 0) hour = 12;
        return {
          date: dateLabel,
          time: `${String(hour).padStart(2, '0')}:${minute} ${ampm}`,
        };
      }
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return { date: '—', time: '—' };
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      let hour = d.getHours();
      const minute = String(d.getMinutes()).padStart(2, '0');
      const ampm = hour >= 12 ? 'pm' : 'am';
      hour = hour % 12;
      if (hour === 0) hour = 12;
      return {
        date: `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`,
        time: `${String(hour).padStart(2, '0')}:${minute} ${ampm}`,
      };
    } catch {
      return { date: '—', time: '—' };
    }
  };

  const getLocalDateKey = (dateString) => getBackendDateKey(dateString) || 'Unknown Date';

  const groupOrdersByDate = (ordersList) => {
    const groups = {};
    ordersList.forEach((order) => {
      const key = getLocalDateKey(order.ordered_at);
      if (!groups[key]) groups[key] = [];
      groups[key].push(order);
    });
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) =>
        String(b.ordered_at || '').localeCompare(String(a.ordered_at || ''))
      );
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unknown Date') return 1;
      if (b === 'Unknown Date') return -1;
      return b.localeCompare(a);
    });
    return sortedKeys.map((key) => ({ dateKey: key, orders: groups[key] }));
  };

  const getDateHeaderLabel = (dateKey) => {
    if (dateKey === 'Unknown Date') return 'Unknown Date';
    const todayKey = getLocalTodayKey();
    const yesterdayKey = getLocalYesterdayKey();
    if (dateKey === todayKey) return 'Today';
    if (dateKey === yesterdayKey) return 'Yesterday';
    const [y, m, d] = dateKey.split('-');
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
  };

  const getOrderInvoiceNumber = (order) => {
    if (!order) return null;
    return (
      order.invoice_number ||
      order.bill_number ||
      order.billing?.invoice_number ||
      order.billing?.bill_number ||
      order.billings?.[0]?.invoice_number ||
      order.billings?.[0]?.bill_number ||
      null
    );
  };

  // ============ FETCHERS ============
  const fetchRestaurantOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (apiParams.property_id) params.append('property_id', apiParams.property_id);
      if (apiParams.order_type) params.append('order_type', apiParams.order_type);
      if (apiParams.order_status) params.append('order_status', apiParams.order_status);
      if (apiParams.date_from) params.append('date_from', apiParams.date_from);
      if (apiParams.date_to) params.append('date_to', apiParams.date_to);

      const url = `${BQ_BASE_URL}/bq/api/restaurant/billing/orders/details?${params.toString()}`;
      const response = await fetch(url, { headers: getAuthHeaders() });

      if (response.ok) {
        const data = await response.json();
        const transformedOrders = (data.orders || []).map((order) => {
          let paymentMethod = 'Not Paid';
          if (order.billings && order.billings.length > 0) {
            const billing = order.billings[0];
            if (billing.payments && billing.payments.length > 0) {
              const payment = billing.payments[0];
              paymentMethod = payment.method || payment.payment_method || 'Not Paid';
              paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
            } else if (billing.is_paid) paymentMethod = 'Paid';
          }
          const orderedAt =
            order.ordered_at || order.billings?.[0]?.created_date || new Date().toISOString();
          const normalizedItems = (order.order_details || []).map((it) => ({
            ...it,
            is_cancelled: normalizeItemCancelled(it),
          }));
          const cancelled =
            order.is_cancelled === true ||
            order.order_status === 'cancelled' ||
            order.order_status === 'Cancelled';

          return {
            order_id: order.order_id,
            serial_number: order.serial_number != null ? String(order.serial_number) : null,
            order_type: order.order_type,
            order_status: order.order_status,
            is_cancelled: cancelled,
            customer_name: order.customer_name || 'Guest',
            customer_phone: order.customer_phone || null,
            customer_email: order.customer_email || null,
            gst_number: order.gst_number || null,
            platform: order.platform || 'direct',
            total_amount: order.financials?.total_amount || order.net_payable || 0,
            subtotal: order.financials?.subtotal || 0,
            tax_amount: order.financials?.tax_amount || 0,
            discount_amount: order.financials?.discount_amount || 0,
            service_charge: order.financials?.service_charge || 0,
            total_paid: order.total_paid || 0,
            net_payable: order.net_payable || 0,
            is_fully_paid: order.is_fully_paid || false,
            is_paid: order.is_fully_paid || false,
            ordered_at: orderedAt,
            items: normalizedItems,
            billings: order.billings || [],
            billing_id: order.billings?.[0]?.billing_id || null,
            billing_data: order,
            financials: order.financials,
            payment_method: paymentMethod,
            room_number: order.room_number || null,
            table_number: order.table_number || null,
            parcel_number: order.parcel_number || null,
            booking_id: order.booking_id || null,
            kot_number: order.kot_number || null,
            has_billing: true,
            invoice_number: order.billings?.[0]?.invoice_number || null,
            // ── IN-HOUSE ──
            is_in_house: !!order.is_in_house,
            in_house_reason: order.in_house_reason || null,
          };
        });
        return { orders: transformedOrders };
      }
      return null;
    } catch (error) {
      console.error('Error fetching restaurant orders:', error);
      return null;
    }
  };

  const fetchRoomOrders = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return data
          .filter((order) => order.order_type === 'room')
          .map((order) => {
            const hasBilling = order.billing !== null;
            const isPaid = hasBilling ? order.billing.is_paid : false;
            const billingId = hasBilling ? order.billing.billing_id : null;
            let paymentMethod = 'Not Paid';
            if (order.payments && order.payments.length > 0) {
              paymentMethod = order.payments[0].payment_method || 'Not Paid';
              paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
            } else if (isPaid) paymentMethod = 'Paid';

            const orderedAt = order.created_at || order.ordered_at || new Date().toISOString();
            const normalizedItems = (order.items || []).map((it) => ({
              ...it,
              is_cancelled: normalizeItemCancelled(it),
            }));
            const cancelled =
              order.is_cancelled === true ||
              order.status === 'cancelled' ||
              order.status === 'Cancelled';

            return {
              order_id: order.id,
              serial_number: order.serial_number != null ? String(order.serial_number) : null,
              order_type: 'room',
              order_status: order.status || 'pending',
              is_cancelled: cancelled,
              customer_name: order.customer_name || 'Guest',
              customer_phone: order.phone_number || null,
              customer_email: null,
              table_number: order.table_number || null,
              parcel_number: order.parcel_number || null,
              room_number: order.room_number || null,
              kot_number: null,
              total_amount: order.totalprice || 0,
              is_paid: isPaid,
              ordered_at: orderedAt,
              items: normalizedItems,
              billing: order.billing || null,
              billing_id: billingId,
              booking_id: order.booking_id || null,
              special_request: order.special_request || null,
              gst_number: null,
              has_billing: hasBilling,
              payment_method: paymentMethod,
              billings: order.billing ? [order.billing] : [],
              payments: order.payments || [],
              invoice_number: order.bill_number || null,
              // ── IN-HOUSE ──
              is_in_house: !!order.is_in_house,
              in_house_reason: order.in_house_reason || null,
            };
          });
      }
      return [];
    } catch (error) {
      console.error('Error fetching room orders:', error);
      return [];
    }
  };

  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      const restaurantResult = await fetchRestaurantOrders();
      const restaurantOrders = restaurantResult?.orders || [];
      const roomOrders = await fetchRoomOrders();
      const allOrders = [...restaurantOrders, ...roomOrders];
      setOrders(allOrders);
      setFilteredOrders(allOrders);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // ============ MAIN TABLE FILTERS ============
  useEffect(() => {
    let filtered = orders;

    if (searchQuery) {
      const query = String(searchQuery).toLowerCase();
      filtered = filtered.filter((order) => {
        const serial = order.serial_number != null ? String(order.serial_number).toLowerCase() : '';
        const orderId = order.order_id != null ? String(order.order_id).toLowerCase() : '';
        const customerName =
          order.customer_name != null ? String(order.customer_name).toLowerCase() : '';
        const tableNumber =
          order.table_number != null ? String(order.table_number).toLowerCase() : '';
        const parcelNumber =
          order.parcel_number != null ? String(order.parcel_number).toLowerCase() : '';
        const kotNumber = order.kot_number != null ? String(order.kot_number).toLowerCase() : '';
        const roomNumber = order.room_number != null ? String(order.room_number).toLowerCase() : '';
        const bookingId = order.booking_id != null ? String(order.booking_id).toLowerCase() : '';
        const invoiceNo = String(getOrderInvoiceNumber(order) || '').toLowerCase();
        return (
          orderId.includes(query) ||
          serial.includes(query) ||
          customerName.includes(query) ||
          tableNumber.includes(query) ||
          parcelNumber.includes(query) ||
          kotNumber.includes(query) ||
          roomNumber.includes(query) ||
          bookingId.includes(query) ||
          invoiceNo.includes(query)
        );
      });
    }

    // ── TYPE FILTER (only dining / parcel / room — in-house handled below) ──
    if (filterType === 'dining' || filterType === 'parcel' || filterType === 'room') {
      filtered = filtered.filter((o) => o.order_type === filterType);
    }

    if (filterStatus !== 'all') {
      if (filterStatus === 'pending') {
        filtered = filtered.filter(
          (o) =>
            !o.is_cancelled &&
            ['pending', 'Pending', 'accepted', 'confirmed', 'preparing', 'ready'].includes(
              o.order_status
            )
        );
      } else if (filterStatus === 'served') {
        filtered = filtered.filter(
          (o) => !o.is_cancelled && (o.order_status === 'served' || o.order_status === 'Served')
        );
      } else if (filterStatus === 'paid') filtered = filtered.filter((o) => o.is_paid === true);
      else if (filterStatus === 'unpaid') filtered = filtered.filter((o) => o.is_paid === false);
      else if (filterStatus === 'cancelled') {
        filtered = filtered.filter(
          (o) =>
            o.is_cancelled === true ||
            o.order_status === 'cancelled' ||
            o.order_status === 'Cancelled'
        );
      }
    }

    // ── IN-HOUSE GUEST FILTER ──
    if (filterInhouse === 'inhouse') {
      filtered = filtered.filter((o) => o.is_in_house === true);
    } else if (filterInhouse === 'exclude') {
      filtered = filtered.filter((o) => !o.is_in_house);
    }

    if (filterDate !== 'all') {
      const todayKey = getLocalTodayKey();
      const yesterdayKey = getLocalYesterdayKey();
      const weekStartKey = addDaysToKey(todayKey, -6);
      const monthStartKey = todayKey.slice(0, 8) + '01';

      const [yStr, mStr] = todayKey.split('-');
      const y = parseInt(yStr, 10);
      const m = parseInt(mStr, 10);
      const prevMonthYear = m === 1 ? y - 1 : y;
      const prevMonth = m === 1 ? 12 : m - 1;
      const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
      const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
      const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
        lastMonthEndDate.getMonth() + 1
      ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

      filtered = filtered.filter((order) => {
        if (filterDate === 'today') {
          const orderDateStr = getBackendDateKey(order.ordered_at);
          return orderDateStr === todayKey;
        }
        if (!order.ordered_at) return false;
        const orderDateStr = getBackendDateKey(order.ordered_at);
        if (!orderDateStr) return false;
        if (filterDate === 'yesterday') return orderDateStr === yesterdayKey;
        if (filterDate === 'weekly')
          return orderDateStr >= weekStartKey && orderDateStr <= todayKey;
        if (filterDate === 'monthly')
          return orderDateStr >= monthStartKey && orderDateStr <= todayKey;
        if (filterDate === 'lastMonth')
          return orderDateStr >= lastMonthStartKey && orderDateStr <= lastMonthEndKey;
        if (filterDate === 'custom') {
          if (!customDateFrom && !customDateTo) return true;
          let ok = true;
          if (customDateFrom) ok = ok && orderDateStr >= customDateFrom;
          if (customDateTo) ok = ok && orderDateStr <= customDateTo;
          return ok;
        }
        return true;
      });
    }

    setFilteredOrders(filtered);
    const groupCount = groupOrdersByDate(filtered).length;
    setTotalPages(Math.max(1, Math.ceil(groupCount / 5)));
    setCurrentPage(1);
  }, [
    searchQuery,
    filterType,
    filterStatus,
    filterDate,
    customDateFrom,
    customDateTo,
    orders,
    filterInhouse,
  ]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getCurrentPageGroups = () => {
    const allGroups = groupOrdersByDate(filteredOrders);
    const GROUPS_PER_PAGE = 5;
    const startIndex = (currentPage - 1) * GROUPS_PER_PAGE;
    return allGroups.slice(startIndex, startIndex + GROUPS_PER_PAGE);
  };

  // ============ ROOM FOLIOS BUILDER ============
  const buildRoomFolios = (sourceOrders, opts = {}) => {
    const { searchQuery = '', dateFilter = 'all', customFrom = '', customTo = '' } = opts;

    const folioMap = {};

    sourceOrders
      .filter((o) => o.order_type === 'room' && !o.is_cancelled)
      .forEach((o) => {
        const roomKey = o.room_number || 'NOROOM';
        const bookingKey = o.booking_id || 'NOBOOKING';
        const key = `${roomKey}__${bookingKey}`;

        if (!folioMap[key]) {
          folioMap[key] = {
            key,
            room_number: o.room_number,
            booking_id: o.booking_id,
            customer_name: o.customer_name || 'Guest',
            customer_phone: o.customer_phone || null,
            orders: [],
            total_amount: 0,
            earliest_time: o.ordered_at,
            latest_time: o.ordered_at,
            invoice_numbers: [],
          };
        }

        const f = folioMap[key];
        f.orders.push(o);
        f.total_amount += parseFloat(o.total_amount) || 0;

        if (String(o.ordered_at) < String(f.earliest_time)) f.earliest_time = o.ordered_at;
        if (String(o.ordered_at) > String(f.latest_time)) f.latest_time = o.ordered_at;

        if (o.customer_name && o.customer_name !== 'Guest') f.customer_name = o.customer_name;
        if (o.customer_phone) f.customer_phone = o.customer_phone;

        if (o.invoice_number) {
          f.invoice_numbers.push(String(o.invoice_number));
        } else if (o.bill_number) {
          f.invoice_numbers.push(String(o.bill_number));
        } else if (o.billing && (o.billing.invoice_number || o.billing.bill_number)) {
          f.invoice_numbers.push(String(o.billing.invoice_number || o.billing.bill_number));
        } else if (
          o.billings &&
          o.billings[0] &&
          (o.billings[0].invoice_number || o.billings[0].bill_number)
        ) {
          f.invoice_numbers.push(String(o.billings[0].invoice_number || o.billings[0].bill_number));
        }
      });

    let folios = Object.values(folioMap).map((f) => {
      f.orders.sort((a, b) => String(a.ordered_at || '').localeCompare(String(b.ordered_at || '')));
      f.order_count = f.orders.length;

      const invs = f.invoice_numbers.filter(Boolean);
      if (invs.length === 0) {
        f.invoice_label = null;
        f.invoice_range = null;
      } else if (invs.length === 1) {
        f.invoice_label = invs[0];
        f.invoice_range = invs[0];
      } else {
        const parsed = invs
          .map((i) => {
            const m = String(i).match(/(\d+)$/);
            return m ? { full: i, num: parseInt(m[1], 10) } : null;
          })
          .filter(Boolean);

        if (parsed.length >= 2) {
          parsed.sort((a, b) => a.num - b.num);
          const first = parsed[0].full;
          const last = parsed[parsed.length - 1].full;
          const prefixMatch = first.match(/^(.*?)(\d+)$/);
          if (prefixMatch) {
            const prefix = prefixMatch[1];
            const label = `${prefix}${parsed[0].num} → ${prefix}${parsed[parsed.length - 1].num}`;
            f.invoice_label = label;
            f.invoice_range = { first, last, prefix };
          } else {
            f.invoice_label = `${first} → ${last}`;
            f.invoice_range = { first, last };
          }
        } else {
          f.invoice_label = `${invs[0]} → ${invs[invs.length - 1]}`;
          f.invoice_range = { first: invs[0], last: invs[invs.length - 1] };
        }
      }

      return f;
    });

    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      folios = folios.filter((f) => {
        if (
          String(f.room_number || '')
            .toLowerCase()
            .includes(q)
        )
          return true;
        if (
          String(f.booking_id || '')
            .toLowerCase()
            .includes(q)
        )
          return true;
        if (
          String(f.customer_name || '')
            .toLowerCase()
            .includes(q)
        )
          return true;
        if (
          String(f.customer_phone || '')
            .toLowerCase()
            .includes(q)
        )
          return true;
        if (
          String(f.invoice_label || '')
            .toLowerCase()
            .includes(q)
        )
          return true;
        return f.orders.some((o) =>
          String(o.order_id || '')
            .toLowerCase()
            .includes(q)
        );
      });
    }

    if (dateFilter !== 'all') {
      const todayKey = getLocalTodayKey();
      const yesterdayKey = getLocalYesterdayKey();
      const weekStartKey = addDaysToKey(todayKey, -6);
      const monthStartKey = todayKey.slice(0, 8) + '01';

      const [yStr, mStr] = todayKey.split('-');
      const y = parseInt(yStr, 10);
      const m = parseInt(mStr, 10);
      const prevMonthYear = m === 1 ? y - 1 : y;
      const prevMonth = m === 1 ? 12 : m - 1;
      const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
      const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
      const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
        lastMonthEndDate.getMonth() + 1
      ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

      folios = folios
        .map((f) => {
          const inRangeOrders = f.orders.filter((o) => {
            const orderDateStr = getBackendDateKey(o.ordered_at);
            if (!orderDateStr) return false;
            if (dateFilter === 'today') return orderDateStr === todayKey;
            if (dateFilter === 'yesterday') return orderDateStr === yesterdayKey;
            if (dateFilter === 'weekly')
              return orderDateStr >= weekStartKey && orderDateStr <= todayKey;
            if (dateFilter === 'monthly')
              return orderDateStr >= monthStartKey && orderDateStr <= todayKey;
            if (dateFilter === 'lastMonth')
              return orderDateStr >= lastMonthStartKey && orderDateStr <= lastMonthEndKey;
            if (dateFilter === 'custom') {
              let ok = true;
              if (customFrom) ok = ok && orderDateStr >= customFrom;
              if (customTo) ok = ok && orderDateStr <= customTo;
              return ok;
            }
            return true;
          });

          if (inRangeOrders.length === 0) return null;

          const total = inRangeOrders.reduce((s, o) => s + (parseFloat(o.total_amount) || 0), 0);

          const earliest = inRangeOrders.reduce(
            (min, o) => (String(o.ordered_at) < String(min) ? o.ordered_at : min),
            inRangeOrders[0].ordered_at
          );
          const latest = inRangeOrders.reduce(
            (max, o) => (String(o.ordered_at) > String(max) ? o.ordered_at : max),
            inRangeOrders[0].ordered_at
          );

          return {
            ...f,
            orders: inRangeOrders,
            order_count: inRangeOrders.length,
            total_amount: total,
            earliest_time: earliest,
            latest_time: latest,
          };
        })
        .filter(Boolean);
    }

    folios.sort((a, b) => {
      const ra = String(a.room_number || '').padStart(10, '0');
      const rb = String(b.room_number || '').padStart(10, '0');
      return ra.localeCompare(rb);
    });

    return folios;
  };

  const roomFolios = useMemo(
    () =>
      buildRoomFolios(orders, {
        searchQuery: folioSearchQuery,
        dateFilter: folioFilterDate,
        customFrom: folioCustomFrom,
        customTo: folioCustomTo,
      }),
    [orders, folioSearchQuery, folioFilterDate, folioCustomFrom, folioCustomTo]
  );

  const folioSummary = useMemo(() => {
    const total = roomFolios.reduce((s, f) => s + f.total_amount, 0);
    const rooms = roomFolios.length;
    const ordersCount = roomFolios.reduce((s, f) => s + f.order_count, 0);
    const invoicesCount = roomFolios.reduce((s, f) => s + f.invoice_numbers.length, 0);
    return { total, rooms, ordersCount, invoicesCount };
  }, [roomFolios]);

  // ============ EDIT GUEST ============
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setCompanySearchText(company.companyname);
    setEditGuestData((prev) => ({
      ...prev,
      customer_name: company.companyname,
      gst_number: company.gstnumber || '',
    }));
    setShowCompanyDropdown(false);
  };

  const handleOpenEditGuest = (order) => {
    if (order.order_type === 'room') {
      toast.info('Edit guest details is only available for restaurant and parcel orders');
      return;
    }
    setSelectedOrder(order);
    setEditGuestData({
      customer_name: order.customer_name || '',
      customer_phone: order.customer_phone || '',
      customer_email: order.customer_email || '',
      gst_number: order.gst_number || '',
    });
    setCompanySearchText('');
    setSelectedCompany(null);
    setShowCompanyDropdown(false);
    setShowEditGuestModal(true);
  };

  const handleEditGuestChange = (e) => {
    const { name, value } = e.target;
    setEditGuestData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateGuestDetails = async () => {
    if (!selectedOrder) return;
    setIsUpdatingGuest(true);
    try {
      const url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${selectedOrder.order_id}/customer-details`;
      const payload = {
        customer_name: editGuestData.customer_name || 'Guest',
        customer_phone: editGuestData.customer_phone || '',
        customer_email: editGuestData.customer_email || '',
        gst_number: editGuestData.gst_number || '',
      };
      const response = await fetch(url, {
        method: 'PATCH',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Customer details updated successfully!');
        setShowEditGuestModal(false);
        await fetchAllOrders();
      } else {
        throw new Error(data.message || data.detail || 'Failed to update customer details');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Failed to update customer details');
    } finally {
      setIsUpdatingGuest(false);
    }
  };

  // ============ INVOICE ============
  const fetchDetailedInvoiceData = async (orderId) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/billing/order/${orderId}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) return await response.json();
      return null;
    } catch (error) {
      console.error('Error fetching detailed billing data:', error);
      return null;
    }
  };

  const fetchInvoiceData = async (order) => {
    setIsLoadingInvoice(true);
    try {
      if (order.order_type === 'room') {
        const url = `${BQ_BASE_URL}/bq/api/food-invoice/data/${order.order_id}`;
        const response = await fetch(url, { headers: getAuthHeaders() });
        if (response.ok) {
          const data = await response.json();
          const rawItems = data.items || data.order_details || [];
          const normalizedItems = rawItems.map((it) => ({
            ...it,
            is_cancelled: normalizeItemCancelled(it),
          }));
          return {
            ...data,
            items: normalizedItems,
            room_number: order.room_number || data.room_number || null,
            table_number: order.table_number || data.table_number || null,
            booking_id: order.booking_id || data.booking_id || null,
            kot_number: order.kot_number || data.kot_number || null,
            parcel_number: order.parcel_number || data.parcel_number || null,
          };
        }
        toast.error('Failed to fetch room invoice data');
        return null;
      }

      // ── Restaurant / parcel: use the invoice endpoint so source_footnotes
      //    and per-item source_order_label are included.
      const invRes = await fetch(
        `${BQ_BASE_URL}/bq/api/restaurant/invoice/data/${order.order_id}`,
        { headers: getAuthHeaders() }
      );

      if (invRes.ok) {
        const inv = await invRes.json();
        const rawItems = inv.items || [];
        const normalizedItems = rawItems.map((it) => ({
          ...it,
          is_cancelled: normalizeItemCancelled(it),
        }));
        return {
          invoice_number: inv.invoice_number,
          invoice_date: inv.invoice_date,
          order_id: inv.order_id,
          order_type: inv.order_type,
          is_cancelled:
            inv.is_in_house === false && order.is_cancelled === true
              ? true
              : order.is_cancelled === true,
          is_in_house: !!inv.is_in_house,
          customer_name: inv.customer_name || 'Guest',
          customer_phone: inv.customer_phone || '-',
          customer_email: inv.customer_email || '-',
          customer_gst: inv.customer_gst || '-',
          items: normalizedItems,
          subtotal: inv.subtotal || 0,
          total_tax: inv.total_tax || 0,
          total_discount: inv.total_discount || 0,
          total_service: inv.total_service || 0,
          grand_total: inv.grand_total || 0,
          total_paid: inv.total_paid || 0,
          balance_due: inv.balance_due || 0,
          amount_in_words: inv.amount_in_words || '',
          payment_method: inv.payment_method || 'Not Paid Yet',
          // ── PROVENANCE ──
          has_merged_items: !!inv.has_merged_items,
          source_footnotes: inv.source_footnotes || [],
          property_name: inv.property_name || 'R&T INTERNATIONAL',
          property_address: inv.property_address || '',
          property_contact: inv.property_contact || '',
          property_email: inv.property_email || '',
          property_gst: inv.property_gst || '',
          billing_id: order.billing_id || order.billings?.[0]?.billing_id || null,
          billings: order.billings || [],
          payments: order.billings?.[0]?.payments || [],
          financials: order.financials,
          room_number: order.room_number || null,
          table_number: order.table_number || null,
          parcel_number: order.parcel_number || null,
          booking_id: order.booking_id || null,
          kot_number: order.kot_number || null,
        };
      }

      // Fallback: legacy billing endpoint (no footnotes, but keeps UI working)
      const billingData = await fetchDetailedInvoiceData(order.order_id);
      if (!billingData) {
        toast.error('Failed to fetch billing details');
        return null;
      }
      const allBillings = billingData.billings || [];
      const primaryBilling = allBillings.find((b) => b.invoice_number) || allBillings[0] || {};
      const rawItems = billingData.order_details || [];
      const normalizedItems = rawItems.map((it) => ({
        ...it,
        is_cancelled: normalizeItemCancelled(it),
      }));
      return {
        invoice_number: primaryBilling.invoice_number ?? 'Not yet issued',
        invoice_date:
          primaryBilling.generated_at || primaryBilling.paid_at || new Date().toISOString(),
        order_id: billingData.order_id,
        order_type: billingData.order_type,
        is_cancelled:
          billingData.is_cancelled === true ||
          billingData.order_status === 'cancelled' ||
          billingData.order_status === 'Cancelled',
        is_in_house: !!billingData.is_in_house,
        customer_name: billingData.customer_name || 'Guest',
        customer_phone: billingData.customer_phone || '-',
        customer_email: billingData.customer_email || '-',
        customer_gst: billingData.gst_number || '-',
        items: normalizedItems,
        subtotal: billingData.financials?.subtotal || 0,
        total_tax: billingData.financials?.tax_amount || 0,
        total_discount: billingData.financials?.discount_amount || 0,
        total_service: billingData.financials?.service_charge || 0,
        grand_total: billingData.financials?.total_amount || billingData.net_payable || 0,
        total_paid: billingData.total_paid || 0,
        balance_due: billingData.net_payable || 0,
        amount_in_words: `${billingData.net_payable || 0} Rupees Only`,
        payment_method: billingData.billings?.[0]?.payments?.[0]?.method || 'Not Paid Yet',
        has_merged_items: false,
        source_footnotes: [],
        property_name: billingData.property?.name || 'R&T INTERNATIONAL',
        property_address:
          billingData.property?.address || 'Taluka Khed Chiplun Area, Lote, Maharashtra, 415722',
        property_contact: billingData.property?.contact || '8698732336',
        property_email: billingData.property?.email || 'reception@hotelpagoda.com',
        property_gst: billingData.property?.gst || '27AAAFR6160L1ZZ',
        billing_id: billingData.billings?.[0]?.billing_id || null,
        billings: billingData.billings || [],
        payments: billingData.billings?.[0]?.payments || [],
        financials: billingData.financials,
        room_number: order.room_number || billingData.room_number || null,
        table_number: order.table_number || billingData.table_number || null,
        parcel_number: order.parcel_number || billingData.parcel_number || null,
        booking_id: order.booking_id || billingData.booking_id || null,
        kot_number: order.kot_number || billingData.kot_number || null,
      };
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to load invoice');
      return null;
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const handleViewInvoice = async (order) => {
    setInvoiceData(null);
    setShowInvoiceModal(false);
    setIsRoomOrder(false);
    setSelectedOrder(order);
    try {
      const data = await fetchInvoiceData(order);
      if (data) {
        setInvoiceData(data);
        setIsRoomOrder(order.order_type === 'room');
        setTimeout(() => setShowInvoiceModal(true), 100);
      } else {
        toast.error('Could not load invoice data');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error loading invoice');
    }
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setTimeout(() => {
      setInvoiceData(null);
      setIsRoomOrder(false);
      setSelectedOrder(null);
    }, 300);
  };

  // ============ PAYMENT ============
  const fetchBillingInfo = async (orderId) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/billing/order/${orderId}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.billings && data.billings.length > 0) {
          const billing = data.billings[0];
          return {
            billing_id: billing.billing_id,
            billing_data: data,
            final_amount: billing.final_amount || data.net_payable || 0,
            is_paid: billing.is_paid || false,
          };
        }
        if (data.financials) {
          return {
            billing_id: null,
            billing_data: data,
            final_amount: data.net_payable || data.financials.total_amount || 0,
            is_paid: false,
            needs_billing_creation: true,
          };
        }
        return null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching billing info:', error);
      return null;
    }
  };

  const handleDownloadPDF = async (order) => {
    try {
      let url;
      if (order.order_type === 'room')
        url = `${BQ_BASE_URL}/bq/api/food-invoice/pdf/${order.order_id}`;
      else url = `${BQ_BASE_URL}/bq/api/restaurant/invoice/pdf/${order.order_id}`;

      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const blob = await response.blob();
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        const contentDisposition = response.headers.get('content-disposition');
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1].replace(/"/g, '')
          : `invoice_${order.order_id}.pdf`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(urlObj);
        document.body.removeChild(a);
        toast.success('Invoice downloaded successfully');
      } else {
        toast.error('Failed to download invoice');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to download invoice');
    }
  };

  // ============ KOT ============
  const handlePrintKOTRoom = async (order) => {
    if (!order.order_id) return toast.error('Order ID not found');
    setIsPrintingKOT(true);
    try {
      const url = `${BQ_BASE_URL}/bq/api/food/print/kot/${order.order_id}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: '',
      });
      const data = await response.json();
      if (response.ok) toast.success(`KOT sent to kitchen printer! KOT: ${data.kot_number || ''}`);
      else toast.error(data.message || 'Failed to print KOT');
    } catch (error) {
      console.error(error);
      toast.error('Failed to print KOT');
    } finally {
      setIsPrintingKOT(false);
    }
  };

  const handlePrintKOTRestaurant = async (order) => {
    if (!order.order_id) return toast.error('Order ID not found');
    setIsPrintingKOT(true);
    try {
      const url = `${BQ_BASE_URL}/bq/api/restaurant/print/kot/${order.order_id}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: '',
      });
      const data = await response.json();
      if (response.ok) {
        const batchInfo =
          data.batches && data.batches.length > 0
            ? `KOT: ${data.batches.map((b) => b.kot_number).join(', ')}`
            : '';
        toast.success(`KOT sent to kitchen printer! ${batchInfo}`);
      } else toast.error(data.message || 'Failed to print KOT');
    } catch (error) {
      console.error(error);
      toast.error('Failed to print KOT');
    } finally {
      setIsPrintingKOT(false);
    }
  };

  const handlePrintKOT = async (order) => {
    if (order.order_type === 'room') await handlePrintKOTRoom(order);
    else await handlePrintKOTRestaurant(order);
  };

  const handlePrintInvoice = async (order) => {
    setIsPrinting(true);
    try {
      let url;
      if (order.order_type === 'room') url = `${BQ_BASE_URL}/bq/api/food/print/${order.order_id}`;
      else url = `${BQ_BASE_URL}/bq/api/restaurant/print/${order.order_id}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: '',
      });
      const data = await response.json();
      if (response.ok) toast.success('Invoice sent to printer successfully!');
      else toast.error(data.message || 'Failed to print invoice');
    } catch (error) {
      console.error(error);
      toast.error('Failed to print invoice');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSendEmail = async (order) => {
    if (!emailRecipient) return toast.error('Please enter recipient email');
    setIsSendingEmail(true);
    try {
      let url;
      if (order.order_type === 'room') {
        url = `${BQ_BASE_URL}/bq/api/food-invoice/email/${order.order_id}?recipient_email=${encodeURIComponent(
          emailRecipient
        )}&cc_hotel=true`;
      } else {
        url = `${BQ_BASE_URL}/bq/api/restaurant/invoice/email/${order.order_id}?recipient_email=${encodeURIComponent(
          emailRecipient
        )}&cc_hotel=true`;
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: '',
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Invoice email sent successfully!');
        setShowEmailModal(false);
        setEmailRecipient('');
      } else toast.error(data.message || 'Failed to send email');
    } catch (error) {
      console.error(error);
      toast.error('Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleRoomCashPayment = async (order) => {
    if (order.is_paid) {
      toast.info('This order is already paid');
      setShowPaymentModal(false);
      setSelectedPaymentMethod(null);
      return;
    }
    if (!order.booking_id) return toast.error('Booking ID not found for this room order');
    if (!order.has_billing && !order.billing_id)
      return toast.error('No billing record found. Please bill the order first.');

    let billingId = order.billing?.billing_id || order.billing_id;
    if (!billingId) {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          const orderData = data.find((o) => o.id === order.order_id);
          if (orderData && orderData.billing) {
            billingId = orderData.billing.billing_id;
            order.billing = orderData.billing;
            order.billing_id = billingId;
            order.has_billing = true;
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
    if (!billingId) return toast.error('No billing ID found.');

    setIsProcessingPayment(true);
    try {
      const paymentData = {
        booking_id: order.booking_id,
        billing_id: billingId,
        amount: order.total_amount,
      };
      const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(paymentData),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Payment successful!');
        setShowPaymentModal(false);
        setSelectedPaymentMethod(null);
        await fetchAllOrders();
        handleCloseInvoiceModal();
      } else throw new Error(data.detail || 'Payment failed');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Failed to process cash payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleRoomRazorpayPayment = async (order) => {
    if (!razorpayLoaded) return toast.error('Razorpay script is not loaded yet.');
    if (order.is_paid) {
      toast.info('This order is already paid');
      setShowPaymentModal(false);
      setSelectedPaymentMethod(null);
      return;
    }
    if (!order.booking_id) return toast.error('Booking ID not found for this room order');
    if (!order.has_billing && !order.billing_id)
      return toast.error('No billing record found. Please bill the order first.');

    let billingId = order.billing?.billing_id || order.billing_id;
    if (!billingId) {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          const orderData = data.find((o) => o.id === order.order_id);
          if (orderData && orderData.billing) {
            billingId = orderData.billing.billing_id;
            order.billing = orderData.billing;
            order.billing_id = billingId;
            order.has_billing = true;
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
    if (!billingId) return toast.error('No billing ID found.');

    setIsProcessingPayment(true);
    try {
      const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${order.booking_id}&strict_target=true`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: '',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create payment order');
      }
      const orderData = await response.json();

      if (orderData && orderData.razorpay_order_id) {
        const options = {
          key: REACT_APP_RAZORPAY_KEY,
          amount: orderData.amount,
          currency: 'INR',
          name: 'Hotel Restaurant',
          description: `Room Order - Room ${order.room_number || 'N/A'}`,
          order_id: orderData.razorpay_order_id,
          handler: async function (response) {
            const paymentDetails = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };
            try {
              const verificationResponse = await fetch(
                `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
                {
                  method: 'POST',
                  headers: getAuthJsonHeaders(),
                  body: JSON.stringify(paymentDetails),
                }
              );
              if (verificationResponse.ok) {
                toast.success('Payment successful and verified!');
                setShowPaymentModal(false);
                setSelectedPaymentMethod(null);
                await fetchAllOrders();
                handleCloseInvoiceModal();
              } else {
                const errorData = await verificationResponse.json();
                toast.error(errorData.detail || 'Payment verification failed.');
              }
            } catch (error) {
              console.error(error);
              toast.error('Error verifying payment.');
            }
          },
          prefill: {
            name: order.customer_name || 'Guest',
            email: order.customer_email || 'guest@example.com',
            contact: order.customer_phone || '9999999999',
          },
          theme: { color: '#3399cc' },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else throw new Error('No Razorpay order ID received');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error processing payment.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleRestaurantCashPayment = async (order) => {
    setIsProcessingPayment(true);
    try {
      const billingInfo = await fetchBillingInfo(order.order_id);
      if (!billingInfo) {
        toast.error('Could not retrieve billing information');
        return;
      }
      if (!billingInfo.billing_id) {
        toast.error('No billing record found.');
        return;
      }
      if (billingInfo.is_paid) {
        toast.info('This order is already paid');
        setShowPaymentModal(false);
        setSelectedPaymentMethod(null);
        await fetchAllOrders();
        return;
      }
      const paymentData = {
        order_id: order.order_id,
        billing_id: billingInfo.billing_id,
        amount: billingInfo.final_amount,
        notes: `Cash payment for order ${order.order_id}`,
      };
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/payments/cash`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(paymentData),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Payment successful! Change due: ₹${data.change_due || 0}`);
        setShowPaymentModal(false);
        setSelectedPaymentMethod(null);
        await fetchAllOrders();
        handleCloseInvoiceModal();
      } else throw new Error(data.detail || 'Payment failed');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Failed to process cash payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleRestaurantRazorpayPayment = async (order) => {
    if (!razorpayLoaded) return toast.error('Razorpay script is not loaded yet.');
    setIsProcessingPayment(true);
    try {
      const billingInfo = await fetchBillingInfo(order.order_id);
      if (!billingInfo || !billingInfo.billing_id) {
        toast.error('No billing record found.');
        return;
      }
      if (billingInfo.is_paid) {
        toast.info('This order is already paid');
        setShowPaymentModal(false);
        setSelectedPaymentMethod(null);
        await fetchAllOrders();
        return;
      }
      const apiUrl = `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/create-order`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify({
          order_id: order.order_id,
          billing_id: billingInfo.billing_id,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create payment order');
      }
      const orderData = await response.json();

      if (orderData && orderData.razorpay_order_id) {
        const options = {
          key: REACT_APP_RAZORPAY_KEY,
          amount: orderData.amount,
          currency: 'INR',
          name: 'Restaurant',
          description: `Order ${order.order_id}`,
          order_id: orderData.razorpay_order_id,
          handler: async function (response) {
            const paymentDetails = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };
            try {
              const verificationResponse = await fetch(
                `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/verify`,
                {
                  method: 'POST',
                  headers: getAuthJsonHeaders(),
                  body: JSON.stringify(paymentDetails),
                }
              );
              if (verificationResponse.ok) {
                toast.success('Payment successful and verified!');
                setShowPaymentModal(false);
                setSelectedPaymentMethod(null);
                await fetchAllOrders();
                handleCloseInvoiceModal();
              } else toast.error('Payment verification failed.');
            } catch (error) {
              console.error(error);
              toast.error('Error verifying payment.');
            }
          },
          prefill: {
            name: order.customer_name || 'Guest',
            email: order.customer_email || 'guest@example.com',
            contact: order.customer_phone || '9999999999',
          },
          theme: { color: '#3399cc' },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error processing payment.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentOption = (method) => {
    setSelectedPaymentMethod(method);
    setShowPaymentModal(true);
  };

  const confirmPayment = async () => {
    if (!selectedOrder) return;
    if (selectedOrder.is_paid) {
      toast.info('This order is already paid');
      setShowPaymentModal(false);
      setSelectedPaymentMethod(null);
      return;
    }
    if (selectedOrder.order_type === 'room') {
      if (selectedPaymentMethod === 'cash') await handleRoomCashPayment(selectedOrder);
      else if (selectedPaymentMethod === 'razorpay') await handleRoomRazorpayPayment(selectedOrder);
    } else {
      if (selectedPaymentMethod === 'cash') await handleRestaurantCashPayment(selectedOrder);
      else if (selectedPaymentMethod === 'razorpay')
        await handleRestaurantRazorpayPayment(selectedOrder);
    }
  };

  // ============ TRANSFER (table → room / parcel → room) ============
  const handleTransferToRoomSubmit = async () => {
    if (!transferRoomNumber) return toast.error('Please enter room number');
    setIsTransferring(true);
    try {
      const payload = { room_number: transferRoomNumber };
      if (transferBookingId && transferBookingId.trim() !== '')
        payload.booking_id = transferBookingId.trim();
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/transfer-to-room`,
        {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(`✅ ${data.message || 'Transferred to room successfully!'}`);
        setShowTransferModal(false);
        setTransferActionType(null);
        setTransferRoomNumber('');
        setTransferBookingId('');
        setTransferOrder(null);
        fetchAllOrders();
      } else toast.error(data.detail || data.message || 'Transfer failed');
    } catch (error) {
      toast.error(error.message || 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleParcelTransferToRoomSubmit = async () => {
    if (!transferRoomNumber) return toast.error('Please enter room number');
    setIsTransferring(true);
    try {
      const payload = { room_number: transferRoomNumber };
      if (transferBookingId && transferBookingId.trim() !== '')
        payload.booking_id = transferBookingId.trim();
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/parcel-transfer-to-room`,
        {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(`✅ ${data.message || 'Parcel transferred to room successfully!'}`);
        setShowTransferModal(false);
        setTransferActionType(null);
        setTransferRoomNumber('');
        setTransferBookingId('');
        setTransferOrder(null);
        fetchAllOrders();
      } else toast.error(data.detail || data.message || 'Transfer failed');
    } catch (error) {
      toast.error(error.message || 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleTransferToTableSubmit = async () => {
    if (!transferNewTable) return toast.error('Please enter a table number');
    setIsTransferring(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/transfer-order`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify({
          order_id: transferOrder.order_id,
          from_table_number: transferOrder.table_number || transferOrder.room_number,
          to_table_number: transferNewTable,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`✅ ${data.message || 'Transferred to table successfully!'}`);
        setShowTransferModal(false);
        setTransferActionType(null);
        setTransferNewTable('');
        setTransferOrder(null);
        fetchAllOrders();
      } else toast.error(data.message || 'Transfer failed');
    } catch (error) {
      toast.error(error.message || 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleParcelTransferToTableSubmit = async () => {
    if (!transferNewTable) return toast.error('Please enter a table number');
    setIsTransferring(true);
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/parcel-transfer-to-table`,
        {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify({ table_number: transferNewTable }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        if (data.mode === 'merged') {
          toast.success(
            `✅ Parcel merged into Table ${transferNewTable}. ` +
              `Invoice ${data.source_invoice_number || '—'} voided, ` +
              `items now on ${data.target_invoice_number || 'table bill'}.`
          );
        } else {
          toast.success(
            `✅ Parcel transferred to Table ${transferNewTable} ` +
              `(invoice ${data.invoice_number || '—'}).`
          );
        }
        setShowTransferModal(false);
        setTransferActionType(null);
        setTransferNewTable('');
        setTransferOrder(null);
        fetchAllOrders();
      } else {
        toast.error(data.detail || data.message || 'Transfer failed');
      }
    } catch (error) {
      toast.error(error.message || 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleMergeBillsSubmit = async () => {
    if (!transferMergeFrom || !transferMergeTo) return toast.error('Please fill in all fields');
    if (transferMergeFrom === transferMergeTo)
      return toast.error('Source and target tables cannot be same');

    setIsTransferring(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/merge`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify({
          from_table_number: transferMergeFrom,
          to_table_number: transferMergeTo,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`✅ ${data.message || 'Merged successfully!'}`);
        setShowTransferModal(false);
        setTransferActionType(null);
        setTransferMergeFrom('');
        setTransferMergeTo('');
        setTransferOrder(null);
        fetchAllOrders();
      } else toast.error(data.message || 'Merge failed');
    } catch (error) {
      toast.error(error.message || 'Merge failed');
    } finally {
      setIsTransferring(false);
    }
  };

  // ── ROOM → ROOM TRANSFER ──
  const handleRoomToRoomTransferSubmit = async () => {
    if (!transferToRoomNumber || !transferToRoomNumber.trim()) {
      toast.error('Please enter destination room number');
      return;
    }
    if (String(transferToRoomNumber).trim() === String(transferOrder?.room_number || '').trim()) {
      toast.error('Destination room is the same as source room');
      return;
    }

    setIsTransferring(true);
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/orders/room/${transferOrder.order_id}/transfer-to-room`,
        {
          method: 'POST',
          headers: getAuthJsonHeaders(),
          body: JSON.stringify({
            to_room_number: String(transferToRoomNumber).trim(),
            to_booking_id: null,
            reason: transferToRoomReason.trim() || null,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(`✅ ${data.message || 'Room transfer successful!'}`);
        setShowTransferModal(false);
        setTransferActionType(null);
        setTransferToRoomNumber('');
        setTransferToRoomReason('');
        setTransferOrder(null);
        await fetchAllOrders();
      } else {
        toast.error(data.detail || data.message || 'Transfer failed');
      }
    } catch (error) {
      console.error('Room transfer error:', error);
      toast.error(error.message || 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  };

  // ── PARCEL → PARCEL MERGE ──
  const handleParcelMergeSubmit = async () => {
    if (!parcelMergeFrom || !parcelMergeTo) {
      toast.error('Please fill in both parcel numbers');
      return;
    }
    if (parcelMergeFrom === parcelMergeTo) {
      toast.error('Source and target parcels cannot be the same');
      return;
    }

    setIsTransferring(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/parcels/merge`, {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify({
          from_parcel_number: parcelMergeFrom,
          to_parcel_number: parcelMergeTo,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`✅ ${data.message || 'Parcels merged successfully!'}`);
        setShowTransferModal(false);
        setTransferActionType(null);
        setParcelMergeFrom('');
        setParcelMergeTo('');
        setTransferOrder(null);
        await fetchAllOrders();
      } else {
        toast.error(data.detail || data.message || 'Merge failed');
      }
    } catch (error) {
      console.error('Parcel merge error:', error);
      toast.error(error.message || 'Merge failed');
    } finally {
      setIsTransferring(false);
    }
  };

  // ── AUDIT LOG FETCH ──
  const fetchAuditLogs = async (eventType = 'all') => {
    setIsLoadingAudit(true);
    try {
      const params = new URLSearchParams({ take: '100' });
      if (eventType && eventType !== 'all') {
        params.append('event_type', eventType);
      }
      const response = await fetch(`${BQ_BASE_URL}/bq/api/audit/logs?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.data || []);
      } else {
        toast.error('Failed to load audit logs');
        setAuditLogs([]);
      }
    } catch (error) {
      console.error('Audit fetch error:', error);
      toast.error('Failed to load audit logs');
      setAuditLogs([]);
    } finally {
      setIsLoadingAudit(false);
    }
  };

  // ============ BADGES ============
  const getStatusBadge = (order) => {
    const cancelled =
      order.is_cancelled === true ||
      order.order_status === 'cancelled' ||
      order.order_status === 'Cancelled';
    if (cancelled) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
          <FaTimes className="mr-1" size={10} /> Cancelled
        </span>
      );
    }
    if (order.is_paid) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
          <FaCheckCircle className="mr-1" size={10} /> Paid
        </span>
      );
    }
    if (order.order_status === 'served' || order.order_status === 'Served') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
          <FaClock className="mr-1" size={10} /> Served
        </span>
      );
    }
    const openStatuses = ['pending', 'Pending', 'accepted', 'confirmed', 'preparing', 'ready'];
    if (openStatuses.includes(order.order_status)) {
      const label = order.order_status
        ? order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)
        : 'Pending';
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
          <FaClock className="mr-1" size={10} /> {label}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
        <FaClock className="mr-1" size={10} /> Pending
      </span>
    );
  };

  const getOrderTypeBadge = (type) => {
    if (type === 'dining') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
          <FaTable className="mr-1" size={10} /> Dining
        </span>
      );
    } else if (type === 'parcel') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
          <FaBox className="mr-1" size={10} /> Parcel
        </span>
      );
    } else if (type === 'room') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200">
          <FaHotel className="mr-1" size={10} /> Room
        </span>
      );
    }
    return null;
  };

  const getPaymentMethodBadge = (method) => {
    if (!method || method === 'Not Paid' || method === 'Not Paid Yet') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
          <FaMoneyBill className="mr-1" size={10} /> Not Paid
        </span>
      );
    }
    const methodColors = {
      Cash: 'bg-green-100 text-green-800 border-green-200',
      Netbanking: 'bg-blue-100 text-blue-800 border-blue-200',
      Card: 'bg-purple-100 text-purple-800 border-purple-200',
      UPI: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      Razorpay: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      Paid: 'bg-green-100 text-green-800 border-green-200',
    };
    const colorClass = methodColors[method] || 'bg-gray-100 text-gray-700 border-gray-200';
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}
      >
        <FaMoneyBill className="mr-1" size={10} /> {method}
      </span>
    );
  };

  // ============ LOADING ============
  const LoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              {[...Array(10)].map((_, i) => (
                <th key={i} className="px-6 py-3">
                  <div className="h-4 bg-gray-200 rounded"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i} className="border-t border-gray-100">
                {[...Array(10)].map((_, j) => (
                  <td key={j} className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============ PAGINATION ============
  const Pagination = () => {
    if (totalPages <= 1) return null;
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage < maxVisiblePages - 1)
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl mt-4">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span> of{' '}
              <span className="font-medium">{totalPages}</span> (date groups)
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium bg-white border border-gray-300 disabled:opacity-50"
              >
                <FaChevronLeft className="h-4 w-4" />
              </button>
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
                    currentPage === page
                      ? 'z-10 bg-black text-white border-black'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium bg-white border border-gray-300 disabled:opacity-50"
              >
                <FaChevronRight className="h-4 w-4" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // ============ ROOM FOLIO POPUP COMPONENT ====================
  // ============================================================
  const RoomFolioModal = () => {
    if (!showRoomFolioModal) return null;

    const toggleExpand = (key) => setExpandedFolioKey((prev) => (prev === key ? null : key));

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-gray-50 rounded-2xl w-full max-w-6xl max-h-[95vh] flex flex-col shadow-2xl overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-rose-500 to-rose-700 rounded-lg text-white">
                <FaLayerGroup />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Room Folio — Consolidated Billing
                </h2>
                <p className="text-xs text-gray-500">
                  All room food orders grouped by Room + Booking
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowRoomFolioModal(false);
                setExpandedFolioKey(null);
                if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
                setFolioSearchQuery('');
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-all"
            >
              <FaTimes className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="bg-white border-b border-gray-200 px-6 py-3 shrink-0">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                <div className="relative flex-1">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    ref={folioSearchInputRef}
                    type="text"
                    placeholder="Search room, booking, guest, invoice, order id..."
                    defaultValue=""
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setFolioSearchQuery(e.currentTarget.value);
                      }
                    }}
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-black focus:ring-1 focus:ring-black"
                  />
                </div>
                <button
                  onClick={() => {
                    const val = folioSearchInputRef.current?.value || '';
                    setFolioSearchQuery(val);
                  }}
                  className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium flex items-center gap-1.5 shrink-0"
                >
                  <FaSearch size={12} /> Search
                </button>
              </div>

              <div className="relative">
                <select
                  value={folioFilterDate}
                  onChange={(e) => setFolioFilterDate(e.target.value)}
                  className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm appearance-none pr-8 focus:border-black focus:ring-1 focus:ring-black"
                >
                  <option value="all">📅 All Time</option>
                  <option value="today">📆 Today</option>
                  <option value="yesterday">📆 Yesterday</option>
                  <option value="weekly">📅 This Week</option>
                  <option value="monthly">📅 This Month</option>
                  <option value="lastMonth">📅 Last Month</option>
                  <option value="custom">🗓️ Custom Range</option>
                </select>
                <FaCalendarAlt
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  size={12}
                />
              </div>

              {folioFilterDate === 'custom' && (
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                  <label className="text-xs text-gray-500">From</label>
                  <input
                    type="date"
                    value={folioCustomFrom}
                    onChange={(e) => setFolioCustomFrom(e.target.value)}
                    className="bg-transparent text-sm focus:outline-none"
                  />
                  <span className="text-gray-400">—</span>
                  <label className="text-xs text-gray-500">To</label>
                  <input
                    type="date"
                    value={folioCustomTo}
                    onChange={(e) => setFolioCustomTo(e.target.value)}
                    className="bg-transparent text-sm focus:outline-none"
                  />
                </div>
              )}

              <button
                onClick={() => {
                  if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
                  setFolioSearchQuery('');
                  setFolioFilterDate('today');
                  setFolioCustomFrom('');
                  setFolioCustomTo('');
                }}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700"
              >
                Clear
              </button>

              <button
                onClick={fetchAllOrders}
                className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm"
              >
                <FaSync className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              <div className="bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase text-rose-600 font-semibold">Rooms</p>
                <p className="text-lg font-bold text-rose-900">{folioSummary.rooms}</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase text-blue-600 font-semibold">Total Orders</p>
                <p className="text-lg font-bold text-blue-900">{folioSummary.ordersCount}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase text-emerald-600 font-semibold">Total Amount</p>
                <p className="text-lg font-bold text-emerald-900">
                  ₹{folioSummary.total.toFixed(2)}
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase text-amber-600 font-semibold">Total Invoices</p>
                <p className="text-lg font-bold text-amber-900">{folioSummary.invoicesCount}</p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                ))}
              </div>
            ) : roomFolios.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <FaHotel className="text-5xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-1">No Room Folios Found</h3>
                <p className="text-sm text-gray-400">No room orders match the selected filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {roomFolios.map((folio) => {
                  const isOpen = expandedFolioKey === folio.key;

                  return (
                    <div
                      key={folio.key}
                      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div
                        onClick={() => toggleExpand(folio.key)}
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-all"
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500 to-rose-700 text-white flex flex-col items-center justify-center shrink-0 shadow-sm">
                            <FaHotel size={12} />
                            <span className="text-sm font-bold leading-tight">
                              {folio.room_number || '—'}
                            </span>
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-gray-900">
                                Room {folio.room_number || 'N/A'}
                              </span>
                              {folio.booking_id && (
                                <span className="text-[11px] text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                                  {folio.booking_id}
                                </span>
                              )}
                            </div>

                            <div className="text-sm text-gray-600 truncate mt-0.5">
                              {folio.customer_name}
                              {folio.customer_phone && (
                                <span className="text-gray-400"> · {folio.customer_phone}</span>
                              )}
                            </div>

                            {folio.invoice_label && (
                              <div className="mt-1 flex items-center gap-1 text-[11px]">
                                <FaFileInvoice className="text-gray-400" size={10} />
                                <span className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
                                  {folio.invoice_label}
                                </span>
                                <span className="text-gray-400">
                                  ({folio.invoice_numbers.length} invoice
                                  {folio.invoice_numbers.length !== 1 ? 's' : ''})
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-5 shrink-0">
                          <div className="text-right hidden sm:block">
                            <div className="text-xs text-gray-500">
                              {folio.order_count} order{folio.order_count !== 1 ? 's' : ''}
                            </div>
                            <div className="text-[11px] text-gray-400">
                              {formatOrderDate(folio.earliest_time).time} —{' '}
                              {formatOrderDate(folio.latest_time).time}
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              ₹{folio.total_amount.toFixed(2)}
                            </div>
                          </div>

                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform ${
                              isOpen
                                ? 'bg-black text-white rotate-180'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <FaChevronDown size={12} />
                          </div>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="border-t border-gray-100 bg-gray-50/70">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
                                    #
                                  </th>
                                  <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
                                    Time
                                  </th>
                                  <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
                                    Order ID
                                  </th>
                                  <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
                                    Invoice No.
                                  </th>
                                  <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
                                    Items
                                  </th>
                                  <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase">
                                    Amount
                                  </th>
                                  <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-600 uppercase">
                                    Status
                                  </th>
                                  <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-600 uppercase">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white">
                                {folio.orders.map((o, idx) => {
                                  const invNo =
                                    o.invoice_number ||
                                    o.bill_number ||
                                    o.billing?.invoice_number ||
                                    o.billing?.bill_number ||
                                    o.billings?.[0]?.invoice_number ||
                                    o.billings?.[0]?.bill_number ||
                                    '—';
                                  return (
                                    <tr key={o.order_id} className="border-t border-gray-100">
                                      <td className="px-3 py-2 text-xs text-gray-500">{idx + 1}</td>
                                      <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
                                        {formatOrderDate(o.ordered_at).time}
                                      </td>
                                      <td className="px-3 py-2 text-xs font-mono text-gray-600">
                                        {String(o.order_id).slice(0, 14)}…
                                      </td>
                                      <td className="px-3 py-2 text-xs font-mono text-gray-700">
                                        {invNo !== '—' ? (
                                          <span className="bg-gray-100 px-1.5 py-0.5 rounded">
                                            {invNo}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">Not issued</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-gray-600">
                                        {o.items?.filter((it) => !normalizeItemCancelled(it))
                                          .length || 0}{' '}
                                        item
                                        {o.items?.filter((it) => !normalizeItemCancelled(it))
                                          .length !== 1
                                          ? 's'
                                          : ''}
                                      </td>
                                      <td className="px-3 py-2 text-xs font-semibold text-right text-gray-900">
                                        ₹{(parseFloat(o.total_amount) || 0).toFixed(2)}
                                      </td>
                                      <td className="px-3 py-2 text-center">{getStatusBadge(o)}</td>
                                      <td className="px-3 py-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownloadPDF(o);
                                            }}
                                            className="p-1.5 rounded hover:bg-purple-50 text-purple-600"
                                            title="Download Invoice"
                                          >
                                            <FaDownload size={13} />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePrintInvoice(o);
                                            }}
                                            className="p-1.5 rounded hover:bg-amber-50 text-amber-600"
                                            title="Print Invoice"
                                          >
                                            <FaPrint size={13} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="px-3 py-3 text-right text-sm font-bold text-gray-800"
                                  >
                                    Consolidated Total ({folio.order_count} orders)
                                  </td>
                                  <td className="px-3 py-3 text-right text-base font-bold text-gray-900">
                                    ₹{folio.total_amount.toFixed(2)}
                                  </td>
                                  <td colSpan={2}></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>

                          <div className="flex flex-wrap gap-2 p-3 border-t border-gray-200 bg-white">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const range = getConsolidatedDateRange();
                                  const params = new URLSearchParams({
                                    booking_id: folio.booking_id,
                                    date_filter: mapDateFilterForApi(range.filter),
                                  });
                                  if (folio.room_number)
                                    params.append('room_number', folio.room_number);

                                  if (range.filter === 'custom') {
                                    if (range.from)
                                      params.append('date_from', `${range.from}T00:00:00`);
                                    if (range.to) params.append('date_to', `${range.to}T23:59:59`);
                                  }

                                  const url = `${BQ_BASE_URL}/bq/api/food-invoice/consolidated/pdf?${params.toString()}`;
                                  const res = await fetch(url, { headers: getAuthHeaders() });
                                  if (!res.ok) {
                                    const err = await res.json().catch(() => ({}));
                                    throw new Error(
                                      err.detail || 'Failed to generate consolidated PDF'
                                    );
                                  }
                                  const blob = await res.blob();
                                  const a = document.createElement('a');
                                  a.href = window.URL.createObjectURL(blob);
                                  a.download = `Consolidated_Invoice_Room${folio.room_number || ''}_${folio.booking_id}_${folioFilterDate}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(a.href);
                                  document.body.removeChild(a);
                                  toast.success('Consolidated invoice downloaded');
                                } catch (err) {
                                  console.error(err);
                                  toast.error(err.message);
                                }
                              }}
                              className="px-3 py-2 bg-black text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1.5"
                            >
                              <FaFileInvoice size={12} /> Download Consolidated Invoice
                            </button>

                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const range = getConsolidatedDateRange();
                                  const params = new URLSearchParams({
                                    booking_id: folio.booking_id,
                                    date_filter: mapDateFilterForApi(range.filter),
                                  });
                                  if (folio.room_number)
                                    params.append('room_number', folio.room_number);

                                  if (range.filter === 'custom') {
                                    if (range.from)
                                      params.append('date_from', `${range.from}T00:00:00`);
                                    if (range.to) params.append('date_to', `${range.to}T23:59:59`);
                                  }

                                  const url = `${BQ_BASE_URL}/bq/api/food/print/consolidated/print?${params.toString()}`;
                                  const res = await fetch(url, {
                                    method: 'POST',
                                    headers: getAuthHeaders(),
                                  });
                                  const data = await res.json();

                                  if (res.ok && data.queued) {
                                    toast.success(
                                      `Consolidated slip sent to printer (${data.bills_included?.length || 0} bills)`
                                    );
                                  } else {
                                    toast.error(data.detail || 'Print failed');
                                  }
                                } catch (err) {
                                  console.error(err);
                                  toast.error('Failed to print consolidated invoice');
                                }
                              }}
                              className="px-3 py-2 border border-gray-200 rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1.5"
                            >
                              <FaPrint size={12} /> Print Consolidated
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white border-t border-gray-200 px-6 py-3 flex justify-between items-center shrink-0">
            <p className="text-xs text-gray-500">
              Showing <span className="font-semibold">{roomFolios.length}</span> room folio
              {roomFolios.length !== 1 ? 's' : ''}
            </p>
            <button
              onClick={() => {
                setShowRoomFolioModal(false);
                setExpandedFolioKey(null);
                if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
                setFolioSearchQuery('');
              }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // ============ AUDIT LOG MODAL COMPONENT =====================
  // ============================================================
  const AuditLogModal = () => {
    if (!showAuditModal) return null;

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg text-white">
                <FaExchangeAlt />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Audit Log</h2>
                <p className="text-xs text-gray-500">
                  Who did what, when, and why — for accountability
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAuditModal(false)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <FaTimes className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="bg-white border-b border-gray-200 px-6 py-3 shrink-0 flex flex-wrap gap-3 items-center">
            <select
              value={auditFilterEventType}
              onChange={(e) => {
                setAuditFilterEventType(e.target.value);
                fetchAuditLogs(e.target.value);
              }}
              className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-purple-500"
            >
              <option value="all">All Events</option>
              <option value="room_order_transferred">Room → Room Transfer</option>
              <option value="inhouse_order_created">In-House Orders</option>
              <option value="parcel_orders_merged">Parcel Merges</option>
              <option value="parcel_to_table_converted">Parcel → Table (Converted)</option>
              <option value="parcel_to_table_merged">Parcel → Table (Merged)</option>
              <option value="table_orders_merged">Table Merges</option>
              <option value="restaurant_bill_transferred_to_room">Table → Room Transfer</option>
              <option value="parcel_bill_transferred_to_room">Parcel → Room Transfer</option>
              <option value="table_bill_transferred_to_room">Table → Room (by Table No.)</option>
              <option value="table_order_moved">Table → Table Move</option>
            </select>
            <button
              onClick={() => fetchAuditLogs(auditFilterEventType)}
              className="px-4 py-2 bg-black text-white rounded-lg text-sm flex items-center gap-2"
            >
              <FaSync className={isLoadingAudit ? 'animate-spin' : ''} />
              Refresh
            </button>
            <span className="text-xs text-gray-500 ml-auto">
              Showing {auditLogs.length} entries
            </span>
          </div>

          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            {isLoadingAudit ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <FaExchangeAlt className="text-5xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-1">No Audit Entries</h3>
                <p className="text-sm text-gray-400">No events match the selected filter</p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log) => (
                  <div
                    key={log.id}
                    className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-gray-900">
                            {log.action || log.event_type || 'Unknown action'}
                          </span>
                          {log.is_transfer && (
                            <span className="text-[10px] uppercase bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">
                              Transfer
                            </span>
                          )}
                          {log.is_in_house && (
                            <span className="text-[10px] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
                              In-House
                            </span>
                          )}
                          {log.is_parcel_merge && (
                            <span className="text-[10px] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
                              Parcel Merge
                            </span>
                          )}
                          {log.is_parcel_to_table && (
                            <span
                              className={`text-[10px] uppercase px-2 py-0.5 rounded-full font-bold ${
                                log.mode === 'merged'
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-teal-100 text-teal-700'
                              }`}
                            >
                              {log.mode === 'merged' ? 'Parcel → Table (Merged)' : 'Parcel → Table'}
                            </span>
                          )}
                        </div>

                        <div className="text-xs text-gray-500 space-y-0.5">
                          {log.from_room && log.to_room && (
                            <div>
                              <span className="text-gray-400">Route:</span>{' '}
                              <span className="font-mono font-semibold text-gray-700">
                                Room {log.from_room} → Room {log.to_room}
                              </span>
                            </div>
                          )}
                          {log.from_parcel_number && log.to_parcel_number && (
                            <div>
                              <span className="text-gray-400">Parcels:</span>{' '}
                              <span className="font-mono font-semibold text-gray-700">
                                {log.from_parcel_number} → {log.to_parcel_number}
                              </span>
                            </div>
                          )}
                          {log.is_parcel_to_table && log.source_parcel_number && (
                            <div>
                              <span className="text-gray-400">Route:</span>{' '}
                              <span className="font-mono font-semibold text-gray-700">
                                {log.source_parcel_number} → Table {log.table_number}
                              </span>
                              {log.items_moved > 0 && (
                                <span className="text-gray-400"> · {log.items_moved} item(s)</span>
                              )}
                            </div>
                          )}
                          {log.is_parcel_to_table &&
                            log.mode === 'merged' &&
                            log.source_amount > 0 && (
                              <div>
                                <span className="text-gray-400">Amounts:</span>{' '}
                                <span className="font-semibold text-gray-700">
                                  ₹{log.source_amount.toFixed(2)} → ₹
                                  {log.target_amount_after_merge.toFixed(2)}
                                </span>
                              </div>
                            )}
                          {log.source_amount > 0 &&
                            log.target_amount_after_merge > 0 &&
                            !log.is_parcel_to_table && (
                              <div>
                                <span className="text-gray-400">Amounts:</span>{' '}
                                <span className="font-semibold text-gray-700">
                                  ₹{log.source_amount.toFixed(2)} → ₹
                                  {log.target_amount_after_merge.toFixed(2)}
                                </span>
                              </div>
                            )}
                          {log.is_parcel_to_table && log.mode === 'merged' && (
                            <div>
                              <span className="text-gray-400">Invoices:</span>{' '}
                              <span className="font-mono text-rose-600 line-through">
                                {log.source_invoice_number || '—'}
                              </span>
                              <span className="text-gray-400 mx-1">→</span>
                              <span className="font-mono text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                {log.target_invoice_number || '—'}
                              </span>
                            </div>
                          )}
                          {log.is_parcel_to_table &&
                            log.mode === 'converted' &&
                            log.invoice_number && (
                              <div>
                                <span className="text-gray-400">Invoice:</span>{' '}
                                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                                  {log.invoice_number}
                                </span>
                              </div>
                            )}
                          {log.invoice_number && !log.is_parcel_to_table && (
                            <div>
                              <span className="text-gray-400">Bill:</span>{' '}
                              <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                                {log.invoice_number}
                              </span>
                            </div>
                          )}
                          {log.reason && (
                            <div>
                              <span className="text-gray-400">Reason:</span> {log.reason}
                            </div>
                          )}
                          {log.comment && <div className="italic">{log.comment}</div>}
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <div className="text-xs text-gray-500 mb-1">{log.occurred_at}</div>
                        {log.created_by_user ? (
                          <div className="flex items-center gap-1.5 justify-end">
                            <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[10px] font-bold">
                              {(log.created_by_user.full_name || '?').charAt(0).toUpperCase()}
                            </div>
                            <div className="text-right">
                              <div className="text-xs font-semibold text-gray-700">
                                {log.created_by_user.full_name || 'Unknown'}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {log.created_by_user.role || ''}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 italic">System</div>
                        )}
                        {log.amount > 0 && (
                          <div className="text-sm font-bold text-gray-900 mt-1">
                            ₹{log.amount.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border-t border-gray-200 px-6 py-3 flex justify-end shrink-0">
            <button
              onClick={() => setShowAuditModal(false)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .action-btn {
          padding: 6px;
          border-radius: 6px;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          cursor: pointer;
          position: relative;
        }
        .action-btn:hover { background: #f3f4f6; transform: scale(1.1); }
        .action-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        .action-btn.view-btn { color: #3b82f6; }
        .action-btn.download-btn { color: #8b5cf6; }
        .action-btn.print-btn { color: #f59e0b; }
        .action-btn.kot-btn { color: #8B5CF6; }
        .action-btn.email-btn { color: #ef4444; }
        .action-btn.payment-btn { color: #10b981; }
        .action-btn.edit-btn { color: #6b7280; }
        .action-btn.transfer-btn { color: #8B5CF6; }
        .action-btn.merge-btn { color: #EC4899; }
      `}</style>
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <header className="flex justify-between items-center mb-3 bg-white p-3">
          <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
          <div className="flex space-x-6">
            <button
              onClick={() =>
                navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')
              }
              className="btn"
            >
              Order Overview
            </button>
            <button
              onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
              className="btn"
            >
              Order Food
            </button>
            <button
              onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
              className="btn"
            >
              Dining Tables
            </button>
            <button
              onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
              className="btn"
            >
              Kitchen Display System(KDS)
            </button>
            <button
              onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
              className="btn font-bold text-black"
            >
              Billing & Invoicing
            </button>
          </div>
        </header>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">
              ₹{filteredOrders.reduce((s, o) => s + (o.total_amount || 0), 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Total Orders</p>
            <p className="text-2xl font-bold text-gray-900">{filteredOrders.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-amber-600">
              {
                filteredOrders.filter(
                  (o) =>
                    !o.is_cancelled &&
                    !o.is_paid &&
                    (o.order_status === 'pending' || o.order_status === 'Pending')
                ).length
              }
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Served</p>
            <p className="text-2xl font-bold text-blue-600">
              {
                filteredOrders.filter(
                  (o) =>
                    !o.is_cancelled && (o.order_status === 'served' || o.order_status === 'Served')
                ).length
              }
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Paid</p>
            <p className="text-2xl font-bold text-green-600">
              {filteredOrders.filter((o) => o.is_paid).length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Serial No, Order ID, Invoice No, Customer, Table, Room, Parcel, KOT, Booking..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <button
                onClick={() => {
                  setShowRoomFolioModal(true);
                  setExpandedFolioKey(null);
                  setFolioSearchQuery('');
                }}
                className="px-4 py-2 bg-gradient-to-r from-rose-600 to-rose-700 text-white rounded-lg hover:from-rose-700 hover:to-rose-800 transition-all flex items-center gap-2 text-sm font-semibold shadow-sm"
                title="Open consolidated room billing"
              >
                <FaLayerGroup />
                Room Folio
                <span className="bg-white/25 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {orders.filter((o) => o.order_type === 'room').length}
                </span>
              </button>

              <div className="relative">
                <select
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm min-w-[150px]"
                >
                  <option value="all">📅 All Time</option>
                  <option value="today">📆 Today</option>
                  <option value="yesterday">📆 Yesterday</option>
                  <option value="weekly">📅 This Week</option>
                  <option value="monthly">📅 This Month</option>
                  <option value="lastMonth">📅 Last Month</option>
                  <option value="custom">🗓️ Custom Range</option>
                </select>
                <FaCalendarAlt
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  size={12}
                />
              </div>

              {filterDate === 'custom' && (
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                  <label className="text-xs text-gray-500 font-medium">From</label>
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="bg-transparent text-sm focus:outline-none"
                  />
                  <span className="text-gray-400">—</span>
                  <label className="text-xs text-gray-500 font-medium">To</label>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="bg-transparent text-sm focus:outline-none"
                  />
                </div>
              )}

              {/* ── ALL TYPES (with In-House options) ── */}
              <select
                value={filterType}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilterType(val);
                  // sync in-house filter with the type dropdown
                  if (val === 'in_house_only') {
                    setFilterInhouse('inhouse');
                  } else if (val === 'exclude_in_house') {
                    setFilterInhouse('exclude');
                  } else {
                    setFilterInhouse('all');
                  }
                }}
                className={`px-4 py-2 border rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm font-medium ${
                  filterType === 'in_house_only'
                    ? 'bg-amber-50 border-amber-300 text-amber-800'
                    : filterType === 'exclude_in_house'
                      ? 'bg-gray-100 border-gray-400 text-gray-700'
                      : 'bg-gray-50 border-gray-200'
                }`}
              >
                <option value="all">All Types</option>
                <option value="dining">Dining</option>
                <option value="parcel">Parcel</option>
                <option value="room">Room</option>
                <option disabled>──────────────</option>
                <option value="in_house_only">In-House Only</option>
                <option value="exclude_in_house">Exclude In-House</option>
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="served">Served</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                  setFilterStatus('all');
                  setFilterDate('today');
                  setFilterInhouse('all'); // ← reset in-house too
                  setCustomDateFrom('');
                  setCustomDateTo('');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700"
              >
                Clear
              </button>

              <button
                onClick={fetchAllOrders}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm"
              >
                <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <LoadingSkeleton />
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
            <FaFileInvoice className="text-5xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Orders Found</h3>
            <p className="text-gray-400">No orders match your current filters</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['S.No', 'OrderID / Invoice No.', 'Customer', 'Type', 'No.', 'Date / Time'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    )
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {getCurrentPageGroups().map(({ dateKey, orders: dateOrders }) => (
                  <React.Fragment key={dateKey}>
                    <tr className="bg-gray-100 border-y border-gray-200">
                      <td colSpan={10} className="px-4 py-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                            <FaCalendarAlt className="text-gray-500" size={12} />
                            {getDateHeaderLabel(dateKey)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {dateOrders.length} order{dateOrders.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {dateOrders.map((order) => {
                      const invNo = getOrderInvoiceNumber(order);
                      return (
                        <tr
                          key={order.order_id}
                          className={`hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                            order.is_cancelled
                              ? 'bg-red-50/40 opacity-80'
                              : order.is_in_house
                                ? 'bg-amber-50/60'
                                : ''
                          }`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap align-top">
                            <span className="text-sm font-bold text-gray-900">
                              {order.serial_number || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap align-top">
                            <div className="mb-1 flex items-center gap-1 flex-wrap">
                              {invNo ? (
                                <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                                  {invNo}
                                </span>
                              ) : (
                                <span className="text-[11px] text-gray-400 italic">
                                  Invoice not issued
                                </span>
                              )}
                              {/* ── IN-HOUSE BADGE ── */}
                              {order.is_in_house && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded"
                                  title={
                                    order.in_house_reason
                                      ? `Reason: ${order.in_house_reason}`
                                      : 'In-House'
                                  }
                                >
                                  <FaUserTie size={8} /> In-House
                                  {order.in_house_reason && (
                                    <span className="font-normal normal-case">
                                      · {order.in_house_reason}
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="text-xs font-mono text-gray-600">
                              {String(order.order_id).slice(0, 20)}
                            </div>
                            {order.booking_id && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                Booking: {order.booking_id}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="text-sm font-medium text-gray-900">
                              {order.customer_name || 'Guest'}
                            </div>
                            {order.customer_phone && (
                              <div className="text-xs text-gray-500">{order.customer_phone}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap align-top">
                            {getOrderTypeBadge(order.order_type)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap align-top">
                            {order.table_number ? (
                              <span className="text-sm text-gray-700">
                                Table {order.table_number}
                              </span>
                            ) : order.room_number ? (
                              <span className="text-sm text-gray-700">
                                Room {order.room_number}
                              </span>
                            ) : order.parcel_number ? (
                              <span className="text-sm text-gray-700 font-mono">
                                {order.parcel_number}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap align-top">
                            {order.ordered_at ? (
                              <>
                                <div className="text-sm text-gray-700">
                                  {formatOrderDate(order.ordered_at).date}
                                </div>
                                <div className="text-xs font-semibold text-gray-500">
                                  {formatOrderDate(order.ordered_at).time}
                                </div>
                              </>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right align-top">
                            <div className="text-sm font-bold text-gray-900">
                              ₹{order.total_amount?.toFixed(2) || 0}
                            </div>
                            {order.items && (
                              <div className="text-xs text-gray-400">
                                {order.items.filter((it) => !normalizeItemCancelled(it)).length}{' '}
                                items
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center align-top">
                            {getPaymentMethodBadge(order.payment_method)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center align-top">
                            {getStatusBadge(order)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center align-top">
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              <button
                                onClick={() => handleViewInvoice(order)}
                                disabled={
                                  isLoadingInvoice && selectedOrder?.order_id === order.order_id
                                }
                                className="action-btn view-btn"
                                title="View Invoice"
                              >
                                {isLoadingInvoice && selectedOrder?.order_id === order.order_id ? (
                                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <FaEye size={16} />
                                )}
                              </button>

                              <button
                                onClick={() => handlePrintKOT(order)}
                                disabled={isPrintingKOT}
                                className="action-btn kot-btn"
                                title="Print KOT"
                              >
                                {isPrintingKOT ? (
                                  <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <FaUtensils size={16} />
                                )}
                              </button>

                              {order.order_type !== 'room' && (
                                <button
                                  onClick={() => handleOpenEditGuest(order)}
                                  className="action-btn edit-btn"
                                  title="Edit Guest"
                                >
                                  <FaEdit size={16} />
                                </button>
                              )}

                              {(order.order_type === 'dining' ||
                                order.order_type === 'parcel' ||
                                order.order_type === 'room') && (
                                <button
                                  onClick={() => {
                                    setTransferOrder(order);
                                    setTransferActionType(null);
                                    setTransferRoomNumber('');
                                    setTransferBookingId('');
                                    setTransferNewTable('');
                                    setTransferMergeFrom('');
                                    setTransferMergeTo('');
                                    setTransferToRoomNumber('');
                                    setTransferToRoomReason('');
                                    setParcelMergeFrom('');
                                    setParcelMergeTo('');
                                    setShowTransferModal(true);
                                  }}
                                  className="action-btn transfer-btn"
                                  title={
                                    order.order_type === 'room'
                                      ? 'Transfer to Another Room'
                                      : order.order_type === 'parcel'
                                        ? 'Transfer / Merge Parcel'
                                        : 'Transfer'
                                  }
                                >
                                  <FaExchangeAlt size={16} />
                                </button>
                              )}

                              <button
                                onClick={() => handleDownloadPDF(order)}
                                className="action-btn download-btn"
                                title="Download PDF"
                              >
                                <FaDownload size={16} />
                              </button>

                              <button
                                onClick={() => handlePrintInvoice(order)}
                                disabled={isPrinting}
                                className="action-btn print-btn"
                                title="Print Invoice"
                              >
                                <FaPrint size={16} />
                              </button>

                              <button
                                onClick={() => {
                                  setSelectedOrder(order);
                                  setShowEmailModal(true);
                                }}
                                className="action-btn email-btn"
                                title="Email"
                              >
                                <FaEnvelope size={16} />
                              </button>

                              {!order.is_paid &&
                                order.has_billing !== false &&
                                !order.is_cancelled && (
                                  <button
                                    onClick={() => {
                                      setSelectedOrder(order);
                                      setSelectedPaymentMethod(null);
                                      setShowPaymentModal(true);
                                    }}
                                    className="action-btn payment-btn"
                                    title="Make Payment"
                                  >
                                    <FaCreditCard size={16} />
                                  </button>
                                )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredOrders.length > 0 && <Pagination />}

        {/* ========== ROOM FOLIO POPUP ========== */}
        <RoomFolioModal />

        {/* ========== AUDIT LOG MODAL ========== */}
        <AuditLogModal />

        {/* ========== EDIT GUEST ========== */}
        {showEditGuestModal && selectedOrder && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <FaUser className="text-gray-700" /> Edit Guest Details
                </h2>
                <button
                  onClick={() => {
                    setShowEditGuestModal(false);
                    setEditGuestData({
                      customer_name: '',
                      customer_phone: '',
                      customer_email: '',
                      gst_number: '',
                    });
                    setCompanySearchText('');
                    setSelectedCompany(null);
                    setShowCompanyDropdown(false);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="relative" ref={companyDropdownRef}>
                  <div className="relative">
                    <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm z-10" />
                    <input
                      type="text"
                      placeholder="Search company name..."
                      value={companySearchText}
                      onChange={(e) => {
                        setCompanySearchText(e.target.value);
                        setSelectedCompany(null);
                        if (!e.target.value) setEditGuestData((p) => ({ ...p, gst_number: '' }));
                      }}
                      onFocus={() => {
                        if (companySearchText.length > 1) setShowCompanyDropdown(true);
                      }}
                      className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
                    />
                  </div>
                  {showCompanyDropdown && filteredCompanies.length > 0 && (
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredCompanies.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => handleCompanySelect(c)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium text-sm">{c.companyname}</div>
                              {c.companyid && (
                                <div className="text-xs text-gray-400">ID: {c.companyid}</div>
                              )}
                            </div>
                            {c.gstnumber && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                GST: {c.gstnumber}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={editGuestData.customer_name}
                    onChange={handleEditGuestChange}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="customer_phone"
                    value={editGuestData.customer_phone}
                    onChange={handleEditGuestChange}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="customer_email"
                    value={editGuestData.customer_email}
                    onChange={handleEditGuestChange}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                  <input
                    type="text"
                    name="gst_number"
                    value={editGuestData.gst_number}
                    onChange={handleEditGuestChange}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
                  />
                  {selectedCompany && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ GST auto-filled from {selectedCompany.companyname}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowEditGuestModal(false);
                      setEditGuestData({
                        customer_name: '',
                        customer_phone: '',
                        customer_email: '',
                        gst_number: '',
                      });
                      setCompanySearchText('');
                      setSelectedCompany(null);
                      setShowCompanyDropdown(false);
                    }}
                    className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateGuestDetails}
                    disabled={isUpdatingGuest || !editGuestData.customer_name.trim()}
                    className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
                      isUpdatingGuest || !editGuestData.customer_name.trim()
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    {isUpdatingGuest ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                    ) : (
                      'Update Details'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== INVOICE MODAL ========== */}
        {showInvoiceModal && invoiceData && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold flex items-center gap-3">
                  <div className="p-2 bg-black rounded-lg text-white">
                    <FaFileInvoice />
                  </div>
                  Invoice Details{' '}
                  {isRoomOrder && (
                    <span className="text-sm font-normal text-gray-500">(Room Order)</span>
                  )}
                  {invoiceData.invoice_number && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      #{invoiceData.invoice_number}
                    </span>
                  )}
                </h2>
                <button
                  onClick={handleCloseInvoiceModal}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6">
                <div className="border-b border-gray-200 pb-4 mb-4">
                  <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {invoiceData.property_name || 'R&T INTERNATIONAL'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{invoiceData.property_address}</p>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                        <span>GST: {invoiceData.property_gst}</span>
                        <span>Contact: {invoiceData.property_contact}</span>
                      </div>
                    </div>
                    <div className="text-right bg-gray-50 px-6 py-4 rounded-lg">
                      <p className="text-2xl font-bold text-black">INVOICE</p>
                      <p className="text-sm font-semibold text-gray-700">
                        # {invoiceData.invoice_number}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(invoiceData.invoice_date).toLocaleDateString('en-GB')}{' '}
                        {new Date(invoiceData.invoice_date).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Customer Details</p>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-600">Name: {invoiceData.customer_name || 'Guest'}</p>
                      <p className="text-gray-600">Phone: {invoiceData.customer_phone || '-'}</p>
                      <p className="text-gray-600">Email: {invoiceData.customer_email || '-'}</p>
                      <p className="text-gray-600">GST: {invoiceData.customer_gst || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Order Details</p>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-600 font-mono text-xs">ID: {invoiceData.order_id}</p>
                      <p className="text-gray-600 capitalize">Type: {invoiceData.order_type}</p>
                      {invoiceData.room_number && (
                        <p className="text-gray-600">Room: {invoiceData.room_number}</p>
                      )}
                      {invoiceData.booking_id && (
                        <p className="text-gray-600">Booking: {invoiceData.booking_id}</p>
                      )}
                    </div>
                  </div>
                </div>
                {invoiceData.items && invoiceData.items.length > 0 && (
                  <div className="overflow-x-auto mb-4 border border-gray-200 rounded-lg">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">
                            Item
                          </th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">
                            Qty
                          </th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">
                            Unit Price
                          </th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceData.items.map((item, index) => {
                          const unitPrice = parseFloat(item.unit_price) || 0;
                          const totalAmount = parseFloat(item.total_amount) || 0;
                          const quantity = parseInt(item.quantity) || 0;
                          const isCancelled =
                            item.is_cancelled === true ||
                            item.status === 'cancelled' ||
                            item.status === 'Cancelled';
                          return (
                            <tr
                              key={index}
                              className={`border-t border-gray-100 ${isCancelled ? 'bg-red-50/50' : ''}`}
                            >
                              <td className="py-2 px-3 text-sm">
                                <span className={isCancelled ? 'line-through text-red-500' : ''}>
                                  {item.description || item.food_name || 'Item'}
                                </span>
                                {item.source_order_label && (
                                  <span
                                    className="ml-1 font-bold text-gray-500"
                                    title={`Added from ${item.source_order_label}`}
                                  >
                                    *
                                  </span>
                                )}
                              </td>
                              <td
                                className={`text-center py-2 px-3 text-sm ${isCancelled ? 'line-through text-red-400' : ''}`}
                              >
                                {quantity}
                              </td>
                              <td
                                className={`text-right py-2 px-3 text-sm ${isCancelled ? 'line-through text-red-400' : ''}`}
                              >
                                ₹{unitPrice.toFixed(2)}
                              </td>
                              <td
                                className={`text-right py-2 px-3 text-sm font-semibold ${isCancelled ? 'line-through text-red-400' : ''}`}
                              >
                                ₹{totalAmount.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── PROVENANCE ── merged-item footnotes */}
                {Array.isArray(invoiceData.source_footnotes) &&
                  invoiceData.source_footnotes.length > 0 && (
                    <div className="mb-3 px-1 text-xs italic text-gray-600 space-y-0.5">
                      {invoiceData.source_footnotes.map((src, i) => (
                        <div key={i}>* added from {src}</div>
                      ))}
                    </div>
                  )}

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-1">
                      <div className="flex justify-between py-1">
                        <span className="text-sm text-gray-600">Subtotal</span>
                        <span className="text-sm font-medium text-gray-900">
                          ₹{(parseFloat(invoiceData.subtotal) || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-sm text-gray-600">Tax</span>
                        <span className="text-sm font-medium text-gray-900">
                          ₹{(parseFloat(invoiceData.total_tax) || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t-2 border-gray-300 pt-2 mt-1">
                        <div className="flex justify-between">
                          <span className="text-lg font-bold text-gray-900">Grand Total</span>
                          <span className="text-2xl font-bold text-black">
                            ₹{(parseFloat(invoiceData.grand_total) || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== PAYMENT MODAL ========== */}
        {showPaymentModal && selectedOrder && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Make Payment</h2>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedPaymentMethod(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Order ID</p>
                  <p className="font-mono text-sm font-semibold">
                    {String(selectedOrder.order_id).slice(0, 12)}...
                  </p>
                  {selectedOrder.room_number && (
                    <p className="text-sm text-gray-600 mt-1">Room: {selectedOrder.room_number}</p>
                  )}
                  <p className="text-sm text-gray-600 mt-3">Total Amount</p>
                  <p className="text-3xl font-bold text-black">
                    ₹{selectedOrder.total_amount?.toFixed(2) || 0}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Payment Method
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handlePaymentOption('cash')}
                      className={`py-3 px-4 rounded-lg border-2 transition-all ${
                        selectedPaymentMethod === 'cash'
                          ? 'border-black bg-black text-white'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <FaMoneyBill className="text-xl mx-auto mb-1" />
                      <span className="text-sm">Cash</span>
                    </button>
                    <button
                      onClick={() => handlePaymentOption('razorpay')}
                      className={`py-3 px-4 rounded-lg border-2 transition-all ${
                        selectedPaymentMethod === 'razorpay'
                          ? 'border-black bg-black text-white'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <FaDigitalOcean className="text-xl mx-auto mb-1" />
                      <span className="text-sm">Digital</span>
                    </button>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setSelectedPaymentMethod(null);
                    }}
                    className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmPayment}
                    disabled={!selectedPaymentMethod || isProcessingPayment}
                    className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
                      !selectedPaymentMethod || isProcessingPayment
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    {isProcessingPayment ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                    ) : (
                      'Pay Now'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== EMAIL MODAL ========== */}
        {showEmailModal && selectedOrder && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Send Invoice via Email</h2>
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailRecipient('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Email
                  </label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={emailRecipient}
                    onChange={(e) => setEmailRecipient(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailRecipient('');
                    }}
                    className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSendEmail(selectedOrder)}
                    disabled={!emailRecipient || isSendingEmail}
                    className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
                      !emailRecipient || isSendingEmail ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSendingEmail ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                    ) : (
                      'Send Email'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== TRANSFER MODAL ========== */}
        {showTransferModal && transferOrder && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FaExchangeAlt className="text-purple-600" /> Transfer Options
                </h2>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setTransferOrder(null);
                    setTransferActionType(null);
                    setTransferRoomNumber('');
                    setTransferBookingId('');
                    setTransferNewTable('');
                    setTransferMergeFrom('');
                    setTransferMergeTo('');
                    setTransferToRoomNumber('');
                    setTransferToRoomReason('');
                    setParcelMergeFrom('');
                    setParcelMergeTo('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm">
                <p>
                  <strong>Order ID:</strong> {String(transferOrder.order_id || '').slice(0, 20)}...
                </p>
                <p>
                  <strong>Customer:</strong> {transferOrder.customer_name || 'Guest'}
                </p>
                <p>
                  <strong>Amount:</strong> ₹{transferOrder.total_amount?.toFixed(2) || 0}
                </p>
                {transferOrder.table_number && (
                  <p>
                    <strong>Table:</strong> {transferOrder.table_number}
                  </p>
                )}
                {transferOrder.room_number && (
                  <p>
                    <strong>Room:</strong> {transferOrder.room_number}
                  </p>
                )}
                {transferOrder.parcel_number && (
                  <p>
                    <strong>Parcel:</strong> {transferOrder.parcel_number}
                  </p>
                )}
              </div>

              {!transferActionType && (
                <div className="space-y-3">
                  {transferOrder.order_type === 'room' && (
                    <button
                      onClick={() => {
                        setTransferActionType('room-to-room');
                        setTransferToRoomNumber('');
                        setTransferToRoomReason('');
                      }}
                      className="w-full py-3 px-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg flex items-center gap-3"
                    >
                      <FaExchangeAlt className="text-purple-600" size={20} />
                      <div className="flex-1 text-left">
                        <div className="font-medium">Transfer to Another Room</div>
                        <div className="text-xs text-gray-500">
                          Move this food bill to a different room
                        </div>
                      </div>
                    </button>
                  )}

                  {transferOrder.order_type !== 'room' && (
                    <button
                      onClick={() => {
                        setTransferActionType('to-room');
                        setTransferRoomNumber('');
                        setTransferBookingId('');
                      }}
                      className="w-full py-3 px-4 bg-pink-50 hover:bg-pink-100 border border-pink-200 rounded-lg flex items-center gap-3"
                    >
                      <FaHotel className="text-pink-600" size={20} />
                      <span className="flex-1 text-left font-medium">Transfer to Room</span>
                    </button>
                  )}

                  {/* ════════════════════════════════════════════════════════════════
    ROOM → TABLE: HIDDEN for room orders
    ────────────────────────────────────────────────────────────────
    There is NO backend endpoint to move a room food order onto a
    dining table.

    Why the button would fail for room orders:
      • The "Transfer to Table" button calls
          POST /restaurant/tables/transfer-order
      • That endpoint does:
          db.restaurant_foodorder.find_unique(where={"id": order_id})
      • A room order lives in  `foodorder`, NOT `restaurant_foodorder`
      • So the lookup fails → HTTP 404 "Restaurant order not found"

    What still works for room orders:
      • Transfer to Another Room  (POST /orders/room/{id}/transfer-to-room)

    What is missing (do NOT re-enable for room orders until this endpoint
    exists):
      • POST /orders/room/{order_id}/transfer-to-table
        Would need to:
          - Load foodorder + foodorderitem + billing + food_invoice
          - Validate: not cancelled, billing not paid, target table available
          - Create restaurant_foodorder + restaurant_billing
          - Copy foodorderitem → restaurant_foodorderitem
          - Copy order_detail → restaurant_order_detail (repoint billing_id)
          - Carry food_invoice.bill_number over to restaurant_invoice
          - Cancel/delete the room foodorder + zero its billing
          - Set dining_table.status = 'occupied'
          - Write audit log + print transfer ticket

    Behaviour:
      • dining  / parcel → button is shown and works
      • room              → button is NOT rendered at all
════════════════════════════════════════════════════════════════ */}
                  {transferOrder.order_type !== 'room' && (
                    <button
                      onClick={() => {
                        setTransferActionType('to-table');
                        setTransferNewTable('');
                      }}
                      className="w-full py-3 px-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg flex items-center gap-3"
                    >
                      <MdTableBar className="text-indigo-600" size={20} />
                      <span className="flex-1 text-left font-medium">Transfer to Table</span>
                    </button>
                  )}

                  {transferOrder.order_type === 'dining' && transferOrder.table_number && (
                    <button
                      onClick={() => {
                        setTransferActionType('merge');
                        setTransferMergeFrom(transferOrder.table_number || '');
                        setTransferMergeTo('');
                      }}
                      className="w-full py-3 px-4 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg flex items-center gap-3"
                    >
                      <RiBillLine className="text-amber-600" size={20} />
                      <span className="flex-1 text-left font-medium">Merge Bills</span>
                    </button>
                  )}

                  {transferOrder.order_type === 'parcel' && (
                    <button
                      onClick={() => {
                        setTransferActionType('merge-parcels');
                        setParcelMergeFrom(transferOrder.parcel_number || '');
                        setParcelMergeTo('');
                      }}
                      className="w-full py-3 px-4 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg flex items-center gap-3"
                    >
                      <RiBillLine className="text-amber-600" size={20} />
                      <div className="flex-1 text-left">
                        <div className="font-medium">Merge Parcels</div>
                        <div className="text-xs text-gray-500">
                          Combine two parcel orders into one bill
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {transferActionType === 'room-to-room' && (
                <div className="space-y-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-800">
                    <p className="font-semibold mb-1">What happens on transfer:</p>
                    <ul className="space-y-0.5 list-disc list-inside text-purple-700">
                      <li>Order + items move to the new room's booking</li>
                      <li>
                        <strong>Invoice number stays the same</strong> — it travels with the bill
                      </li>
                      <li>Current room's folio becomes clean</li>
                      <li>Unpaid payments follow the bill</li>
                      <li>Audit log records who made the transfer</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-500">From room</span>
                      <span className="font-semibold">{transferOrder.room_number || '—'}</span>
                    </div>
                    {transferOrder.booking_id && (
                      <div className="flex justify-between mb-1">
                        <span className="text-gray-500">Booking</span>
                        <span className="font-mono">{transferOrder.booking_id}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Amount</span>
                      <span className="font-semibold">
                        ₹{(transferOrder.total_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Destination Room Number *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 202"
                      value={transferToRoomNumber}
                      onChange={(e) =>
                        setTransferToRoomNumber(e.target.value.replace(/[^0-9]/g, ''))
                      }
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-purple-500"
                      autoFocus
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Destination room must have an active Hard booking
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason (optional)
                    </label>
                    <textarea
                      placeholder="e.g., Guest moved from 101 to 202"
                      value={transferToRoomReason}
                      onChange={(e) => setTransferToRoomReason(e.target.value)}
                      rows={2}
                      maxLength={500}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-purple-500 resize-none"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setTransferActionType(null);
                        setTransferToRoomNumber('');
                        setTransferToRoomReason('');
                      }}
                      disabled={isTransferring}
                      className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleRoomToRoomTransferSubmit}
                      disabled={isTransferring || !transferToRoomNumber}
                      className={`flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2 ${
                        isTransferring || !transferToRoomNumber
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      {isTransferring ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Transferring…
                        </>
                      ) : (
                        <>
                          <FaExchangeAlt size={12} />
                          Transfer
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {transferActionType === 'to-room' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Room Number *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 209"
                      value={transferRoomNumber}
                      onChange={(e) => setTransferRoomNumber(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Booking ID (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="Enter booking ID (optional)"
                      value={transferBookingId}
                      onChange={(e) => setTransferBookingId(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setTransferActionType(null);
                        setTransferRoomNumber('');
                        setTransferBookingId('');
                      }}
                      className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      Back
                    </button>
                    <button
                      onClick={
                        transferOrder.order_type === 'parcel'
                          ? handleParcelTransferToRoomSubmit
                          : handleTransferToRoomSubmit
                      }
                      disabled={isTransferring || !transferRoomNumber}
                      className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
                        isTransferring || !transferRoomNumber ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isTransferring ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                      ) : (
                        'Transfer'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {transferActionType === 'to-table' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Table Number *
                    </label>
                    <input
                      type="text"
                      placeholder="Enter new table number"
                      value={transferNewTable}
                      onChange={(e) => setTransferNewTable(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setTransferActionType(null);
                        setTransferNewTable('');
                      }}
                      className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      Back
                    </button>
                    <button
                      onClick={
                        transferOrder.order_type === 'parcel'
                          ? handleParcelTransferToTableSubmit
                          : handleTransferToTableSubmit
                      }
                      disabled={isTransferring || !transferNewTable}
                      className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
                        isTransferring || !transferNewTable ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isTransferring ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                      ) : (
                        'Transfer'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {transferActionType === 'merge' && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                    <p className="font-semibold mb-1">What happens on merge:</p>
                    <ul className="space-y-0.5 list-disc list-inside text-amber-700">
                      <li>Source table's items move to target table</li>
                      <li>Target table becomes the single payable bill</li>
                      <li>Source table is cancelled and freed</li>
                      <li>Audit log records who merged and when</li>
                    </ul>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From Table (Source) *
                    </label>
                    <input
                      type="text"
                      value={transferMergeFrom}
                      readOnly
                      disabled
                      className="w-full px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-600 cursor-not-allowed font-mono"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Locked to the table you opened — this is the one that will be emptied and
                      merged
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To Table (Target) *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 102"
                      value={transferMergeTo}
                      onChange={(e) => setTransferMergeTo(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-amber-500"
                      autoFocus
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Enter the table number that will keep the bill
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setTransferActionType(null);
                        setTransferMergeFrom('');
                        setTransferMergeTo('');
                      }}
                      disabled={isTransferring}
                      className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleMergeBillsSubmit}
                      disabled={isTransferring || !transferMergeFrom || !transferMergeTo}
                      className={`flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium flex items-center justify-center gap-2 ${
                        isTransferring || !transferMergeFrom || !transferMergeTo
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      {isTransferring ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Merging…
                        </>
                      ) : (
                        <>
                          <RiBillLine size={12} />
                          Merge Bills
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {transferActionType === 'merge-parcels' && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                    <p className="font-semibold mb-1">What happens on merge:</p>
                    <ul className="space-y-0.5 list-disc list-inside text-amber-700">
                      <li>Source parcel's items move to target parcel</li>
                      <li>Target parcel becomes the single payable bill</li>
                      <li>Source parcel is cancelled (kept for records)</li>
                      <li>Source billing becomes MERGED (zeroed)</li>
                      <li>Merge ticket prints to kitchen</li>
                      <li>Audit log records who merged and when</li>
                    </ul>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From Parcel (Source) *
                    </label>
                    <input
                      type="text"
                      value={parcelMergeFrom}
                      readOnly
                      disabled
                      className="w-full px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-600 cursor-not-allowed font-mono"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Locked to the parcel you opened — this is the one that will be emptied and
                      cancelled
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To Parcel (Target) *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., PCL-131026-0001"
                      value={parcelMergeTo}
                      onChange={(e) => setParcelMergeTo(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-amber-500"
                      autoFocus
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Enter the parcel number that will keep the bill
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setTransferActionType(null);
                        setParcelMergeFrom('');
                        setParcelMergeTo('');
                      }}
                      disabled={isTransferring}
                      className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleParcelMergeSubmit}
                      disabled={isTransferring || !parcelMergeFrom || !parcelMergeTo}
                      className={`flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium flex items-center justify-center gap-2 ${
                        isTransferring || !parcelMergeFrom || !parcelMergeTo
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      {isTransferring ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Merging…
                        </>
                      ) : (
                        <>
                          <RiBillLine size={12} />
                          Merge Parcels
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </>
  );
}

export default OrderInvoicing;

// import React, { useState, useEffect, useRef, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import {
//   FaFileInvoice,
//   FaSearch,
//   FaEye,
//   FaPrint,
//   FaEnvelope,
//   FaMoneyBill,
//   FaDigitalOcean,
//   FaDownload,
//   FaTimes,
//   FaCheckCircle,
//   FaClock,
//   FaTable,
//   FaBox,
//   FaUser,
//   FaCalendarAlt,
//   FaCreditCard,
//   FaChevronLeft,
//   FaChevronRight,
//   FaBuilding,
//   FaSync,
//   FaHotel,
//   FaEdit,
//   FaUtensils,
//   FaExchangeAlt,
//   FaReceipt,
//   FaLayerGroup,
//   FaChevronDown,
//   FaChevronUp,
// } from 'react-icons/fa';
// import { MdTableBar } from 'react-icons/md';
// import { RiBillLine } from 'react-icons/ri';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
// const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// // ── AUTH ── read staff user id from localStorage (saved by OrderFood page)
// const USER_ID_STORAGE_KEY = 'aq_user_id';
// const TOKEN_STORAGE_KEYS = ['access_token', 'token', 'jwt', 'auth_token'];

// const getStoredToken = () => {
//   try {
//     for (const k of TOKEN_STORAGE_KEYS) {
//       const v = window.localStorage.getItem(k);
//       if (v && v.length > 10) return v;
//     }
//   } catch (e) {
//     /* noop */
//   }
//   return null;
// };

// const getStoredUserId = () => {
//   try {
//     const v = window.localStorage.getItem(USER_ID_STORAGE_KEY);
//     if (!v) return null;
//     const n = parseInt(v, 10);
//     return Number.isFinite(n) && n > 0 ? n : null;
//   } catch (e) {
//     return null;
//   }
// };

// const getAuthHeaders = () => {
//   const token = getStoredToken();
//   const headers = { accept: 'application/json' };
//   if (token) headers.Authorization = `Bearer ${token}`;
//   const uid = getStoredUserId();
//   if (uid) headers['X-User-Id'] = String(uid);
//   return headers;
// };

// const getAuthJsonHeaders = () => {
//   const headers = getAuthHeaders();
//   headers['Content-Type'] = 'application/json';
//   return headers;
// };

// // Helper: normalize an item's cancelled flag
// const normalizeItemCancelled = (item) =>
//   item.is_cancelled === true ||
//   item.status === 'cancelled' ||
//   item.status === 'Cancelled' ||
//   item.cancelled === true;

// // Map frontend date filter values to backend-expected values
// const mapDateFilterForApi = (f) => {
//   if (f === 'weekly' || f === 'monthly' || f === 'lastMonth') return 'custom';
//   return f;
// };

// function OrderInvoicing() {
//   const navigate = useNavigate();
//   const [orders, setOrders] = useState([]);
//   const [filteredOrders, setFilteredOrders] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [invoiceData, setInvoiceData] = useState(null);
//   const [showInvoiceModal, setShowInvoiceModal] = useState(false);
//   const [filterType, setFilterType] = useState('all');
//   const [filterStatus, setFilterStatus] = useState('all');
//   const [filterDate, setFilterDate] = useState('today');
//   const [customDateFrom, setCustomDateFrom] = useState('');
//   const [customDateTo, setCustomDateTo] = useState('');
//   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
//   const [showPaymentModal, setShowPaymentModal] = useState(false);
//   const [isProcessingPayment, setIsProcessingPayment] = useState(false);
//   const [isPrinting, setIsPrinting] = useState(false);
//   const [isPrintingKOT, setIsPrintingKOT] = useState(false);
//   const [isSendingEmail, setIsSendingEmail] = useState(false);
//   const [emailRecipient, setEmailRecipient] = useState('');
//   const [showEmailModal, setShowEmailModal] = useState(false);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [isRoomOrder, setIsRoomOrder] = useState(false);
//   const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

//   // Company search
//   const [companies, setCompanies] = useState([]);
//   const [companySearchText, setCompanySearchText] = useState('');
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
//   const [selectedCompany, setSelectedCompany] = useState(null);
//   const companyDropdownRef = useRef(null);

//   // Edit guest
//   const [showEditGuestModal, setShowEditGuestModal] = useState(false);
//   const [editGuestData, setEditGuestData] = useState({
//     customer_name: '',
//     customer_phone: '',
//     customer_email: '',
//     gst_number: '',
//   });
//   const [isUpdatingGuest, setIsUpdatingGuest] = useState(false);

//   // Transfer
//   const [showTransferModal, setShowTransferModal] = useState(false);
//   const [transferOrder, setTransferOrder] = useState(null);
//   const [isTransferring, setIsTransferring] = useState(false);
//   const [transferRoomNumber, setTransferRoomNumber] = useState('');
//   const [transferBookingId, setTransferBookingId] = useState('');
//   const [transferNewTable, setTransferNewTable] = useState('');
//   const [transferMergeFrom, setTransferMergeFrom] = useState('');
//   const [transferMergeTo, setTransferMergeTo] = useState('');
//   const [transferActionType, setTransferActionType] = useState(null);

//   // ── ROOM TRANSFER (room → room) ──
//   const [transferToRoomNumber, setTransferToRoomNumber] = useState('');
//   const [transferToRoomReason, setTransferToRoomReason] = useState('');

//   // ── PARCEL MERGE (parcel → parcel) ──
//   const [parcelMergeFrom, setParcelMergeFrom] = useState('');
//   const [parcelMergeTo, setParcelMergeTo] = useState('');

//   // ── AUDIT LOG ──
//   const [showAuditModal, setShowAuditModal] = useState(false);
//   const [auditLogs, setAuditLogs] = useState([]);
//   const [isLoadingAudit, setIsLoadingAudit] = useState(false);
//   const [auditFilterEventType, setAuditFilterEventType] = useState('all');

//   // ============ ROOM FOLIO POPUP STATE ============
//   const [showRoomFolioModal, setShowRoomFolioModal] = useState(false);
//   const [folioSearchQuery, setFolioSearchQuery] = useState('');
//   const [folioFilterDate, setFolioFilterDate] = useState('today');
//   const [folioCustomFrom, setFolioCustomFrom] = useState('');
//   const [folioCustomTo, setFolioCustomTo] = useState('');
//   const [expandedFolioKey, setExpandedFolioKey] = useState(null);
//   const folioSearchInputRef = useRef(null);

//   const [apiParams, setApiParams] = useState({
//     property_id: null,
//     order_type: null,
//     order_status: null,
//     date_from: null,
//     date_to: null,
//   });

//   // ============ DATE HELPERS ============
//   const getBackendDateKey = (dateString) => {
//     if (!dateString) return null;
//     if (typeof dateString === 'string') {
//       const m = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
//       if (m) return m[1];
//       const d = new Date(dateString);
//       if (isNaN(d.getTime())) return null;
//       const y = d.getFullYear();
//       const mo = String(d.getMonth() + 1).padStart(2, '0');
//       const da = String(d.getDate()).padStart(2, '0');
//       return `${y}-${mo}-${da}`;
//     }
//     if (dateString instanceof Date) {
//       if (isNaN(dateString.getTime())) return null;
//       const y = dateString.getFullYear();
//       const mo = String(dateString.getMonth() + 1).padStart(2, '0');
//       const da = String(dateString.getDate()).padStart(2, '0');
//       return `${y}-${mo}-${da}`;
//     }
//     return null;
//   };

//   const getLocalTodayKey = () => {
//     const now = new Date();
//     const y = now.getFullYear();
//     const mo = String(now.getMonth() + 1).padStart(2, '0');
//     const da = String(now.getDate()).padStart(2, '0');
//     return `${y}-${mo}-${da}`;
//   };

//   const getLocalYesterdayKey = () => {
//     const now = new Date();
//     now.setDate(now.getDate() - 1);
//     const y = now.getFullYear();
//     const mo = String(now.getMonth() + 1).padStart(2, '0');
//     const da = String(now.getDate()).padStart(2, '0');
//     return `${y}-${mo}-${da}`;
//   };

//   const addDaysToKey = (key, delta) => {
//     const [y, m, d] = key.split('-').map(Number);
//     const dt = new Date(y, m - 1, d);
//     dt.setDate(dt.getDate() + delta);
//     const yy = dt.getFullYear();
//     const mm = String(dt.getMonth() + 1).padStart(2, '0');
//     const dd = String(dt.getDate()).padStart(2, '0');
//     return `${yy}-${mm}-${dd}`;
//   };

//   const getConsolidatedDateRange = () => {
//     const todayKey = getLocalTodayKey();
//     const yesterdayKey = getLocalYesterdayKey();
//     const weekStartKey = addDaysToKey(todayKey, -6);
//     const monthStartKey = todayKey.slice(0, 8) + '01';

//     const [yStr, mStr] = todayKey.split('-');
//     const y = parseInt(yStr, 10);
//     const m = parseInt(mStr, 10);
//     const prevMonthYear = m === 1 ? y - 1 : y;
//     const prevMonth = m === 1 ? 12 : m - 1;
//     const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
//     const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
//     const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
//       lastMonthEndDate.getMonth() + 1
//     ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

//     switch (folioFilterDate) {
//       case 'today':
//         return { filter: 'today', from: null, to: null };
//       case 'yesterday':
//         return { filter: 'yesterday', from: null, to: null };
//       case 'weekly':
//         return { filter: 'custom', from: weekStartKey, to: todayKey };
//       case 'monthly':
//         return { filter: 'custom', from: monthStartKey, to: todayKey };
//       case 'lastMonth':
//         return { filter: 'custom', from: lastMonthStartKey, to: lastMonthEndKey };
//       case 'custom':
//         return {
//           filter: 'custom',
//           from: folioCustomFrom || null,
//           to: folioCustomTo || null,
//         };
//       case 'all':
//       default:
//         return { filter: 'all', from: null, to: null };
//     }
//   };

//   // Razorpay
//   useEffect(() => {
//     const loadRazorpayScript = () => {
//       const script = document.createElement('script');
//       script.src = 'https://checkout.razorpay.com/v1/checkout.js';
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => toast.error('Error loading Razorpay script.');
//       document.body.appendChild(script);
//     };
//     loadRazorpayScript();
//   }, []);

//   // Companies
//   useEffect(() => {
//     const fetchCompanies = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
//           headers: getAuthHeaders(),
//         });
//         if (response.ok) {
//           const data = await response.json();
//           setCompanies(data.companies || []);
//         }
//       } catch (error) {
//         console.error('Error fetching companies:', error);
//       }
//     };
//     fetchCompanies();
//   }, []);

//   // Persist current user id so transfers/merges can be attributed in audit log
//   useEffect(() => {
//     const fetchCurrentUser = async () => {
//       try {
//         const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
//           headers: getAuthHeaders(),
//           credentials: 'include',
//         });
//         if (response.ok) {
//           const userData = await response.json();
//           if (userData.id) {
//             try {
//               window.localStorage.setItem(USER_ID_STORAGE_KEY, String(userData.id));
//             } catch (e) {
//               /* localStorage unavailable */
//             }
//           }
//         }
//       } catch (err) {
//         console.warn('Could not fetch current user:', err);
//       }
//     };
//     fetchCurrentUser();
//   }, []);

//   useEffect(() => {
//     if (companySearchText && companySearchText.length > 1) {
//       const filtered = companies.filter((c) =>
//         c.companyname.toLowerCase().includes(companySearchText.toLowerCase())
//       );
//       setFilteredCompanies(filtered);
//       setShowCompanyDropdown(true);
//     } else {
//       setFilteredCompanies([]);
//       setShowCompanyDropdown(false);
//     }
//   }, [companySearchText, companies]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
//         setShowCompanyDropdown(false);
//       }
//     };
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, []);

//   useEffect(() => {
//     fetchAllOrders();
//   }, []);

//   const formatOrderDate = (dateString) => {
//     if (!dateString) return { date: '—', time: '—' };
//     try {
//       const match = String(dateString).match(
//         /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
//       );
//       if (match) {
//         const [, y, mo, d, hh, mm] = match;
//         const months = [
//           'Jan',
//           'Feb',
//           'Mar',
//           'Apr',
//           'May',
//           'Jun',
//           'Jul',
//           'Aug',
//           'Sep',
//           'Oct',
//           'Nov',
//           'Dec',
//         ];
//         const dateLabel = `${parseInt(d, 10)} ${months[parseInt(mo, 10) - 1]} ${y}`;
//         let hour = parseInt(hh, 10);
//         const minute = mm;
//         const ampm = hour >= 12 ? 'pm' : 'am';
//         hour = hour % 12;
//         if (hour === 0) hour = 12;
//         return {
//           date: dateLabel,
//           time: `${String(hour).padStart(2, '0')}:${minute} ${ampm}`,
//         };
//       }
//       const d = new Date(dateString);
//       if (isNaN(d.getTime())) return { date: '—', time: '—' };
//       const months = [
//         'Jan',
//         'Feb',
//         'Mar',
//         'Apr',
//         'May',
//         'Jun',
//         'Jul',
//         'Aug',
//         'Sep',
//         'Oct',
//         'Nov',
//         'Dec',
//       ];
//       let hour = d.getHours();
//       const minute = String(d.getMinutes()).padStart(2, '0');
//       const ampm = hour >= 12 ? 'pm' : 'am';
//       hour = hour % 12;
//       if (hour === 0) hour = 12;
//       return {
//         date: `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`,
//         time: `${String(hour).padStart(2, '0')}:${minute} ${ampm}`,
//       };
//     } catch {
//       return { date: '—', time: '—' };
//     }
//   };

//   const getLocalDateKey = (dateString) => getBackendDateKey(dateString) || 'Unknown Date';

//   const groupOrdersByDate = (ordersList) => {
//     const groups = {};
//     ordersList.forEach((order) => {
//       const key = getLocalDateKey(order.ordered_at);
//       if (!groups[key]) groups[key] = [];
//       groups[key].push(order);
//     });
//     Object.keys(groups).forEach((key) => {
//       groups[key].sort((a, b) =>
//         String(b.ordered_at || '').localeCompare(String(a.ordered_at || ''))
//       );
//     });
//     const sortedKeys = Object.keys(groups).sort((a, b) => {
//       if (a === 'Unknown Date') return 1;
//       if (b === 'Unknown Date') return -1;
//       return b.localeCompare(a);
//     });
//     return sortedKeys.map((key) => ({ dateKey: key, orders: groups[key] }));
//   };

//   const getDateHeaderLabel = (dateKey) => {
//     if (dateKey === 'Unknown Date') return 'Unknown Date';
//     const todayKey = getLocalTodayKey();
//     const yesterdayKey = getLocalYesterdayKey();
//     if (dateKey === todayKey) return 'Today';
//     if (dateKey === yesterdayKey) return 'Yesterday';
//     const [y, m, d] = dateKey.split('-');
//     const months = [
//       'Jan',
//       'Feb',
//       'Mar',
//       'Apr',
//       'May',
//       'Jun',
//       'Jul',
//       'Aug',
//       'Sep',
//       'Oct',
//       'Nov',
//       'Dec',
//     ];
//     return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
//   };

//   const getOrderInvoiceNumber = (order) => {
//     if (!order) return null;
//     return (
//       order.invoice_number ||
//       order.bill_number ||
//       order.billing?.invoice_number ||
//       order.billing?.bill_number ||
//       order.billings?.[0]?.invoice_number ||
//       order.billings?.[0]?.bill_number ||
//       null
//     );
//   };

//   // ============ FETCHERS ============
//   const fetchRestaurantOrders = async () => {
//     try {
//       const params = new URLSearchParams();
//       if (apiParams.property_id) params.append('property_id', apiParams.property_id);
//       if (apiParams.order_type) params.append('order_type', apiParams.order_type);
//       if (apiParams.order_status) params.append('order_status', apiParams.order_status);
//       if (apiParams.date_from) params.append('date_from', apiParams.date_from);
//       if (apiParams.date_to) params.append('date_to', apiParams.date_to);

//       const url = `${BQ_BASE_URL}/bq/api/restaurant/billing/orders/details?${params.toString()}`;
//       const response = await fetch(url, { headers: getAuthHeaders() });

//       if (response.ok) {
//         const data = await response.json();
//         const transformedOrders = (data.orders || []).map((order) => {
//           let paymentMethod = 'Not Paid';
//           if (order.billings && order.billings.length > 0) {
//             const billing = order.billings[0];
//             if (billing.payments && billing.payments.length > 0) {
//               const payment = billing.payments[0];
//               paymentMethod = payment.method || payment.payment_method || 'Not Paid';
//               paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
//             } else if (billing.is_paid) paymentMethod = 'Paid';
//           }
//           const orderedAt =
//             order.ordered_at || order.billings?.[0]?.created_date || new Date().toISOString();
//           const normalizedItems = (order.order_details || []).map((it) => ({
//             ...it,
//             is_cancelled: normalizeItemCancelled(it),
//           }));
//           const cancelled =
//             order.is_cancelled === true ||
//             order.order_status === 'cancelled' ||
//             order.order_status === 'Cancelled';

//           return {
//             order_id: order.order_id,
//             serial_number: order.serial_number != null ? String(order.serial_number) : null,
//             order_type: order.order_type,
//             order_status: order.order_status,
//             is_cancelled: cancelled,
//             customer_name: order.customer_name || 'Guest',
//             customer_phone: order.customer_phone || null,
//             customer_email: order.customer_email || null,
//             gst_number: order.gst_number || null,
//             platform: order.platform || 'direct',
//             total_amount: order.financials?.total_amount || order.net_payable || 0,
//             subtotal: order.financials?.subtotal || 0,
//             tax_amount: order.financials?.tax_amount || 0,
//             discount_amount: order.financials?.discount_amount || 0,
//             service_charge: order.financials?.service_charge || 0,
//             total_paid: order.total_paid || 0,
//             net_payable: order.net_payable || 0,
//             is_fully_paid: order.is_fully_paid || false,
//             is_paid: order.is_fully_paid || false,
//             ordered_at: orderedAt,
//             items: normalizedItems,
//             billings: order.billings || [],
//             billing_id: order.billings?.[0]?.billing_id || null,
//             billing_data: order,
//             financials: order.financials,
//             payment_method: paymentMethod,
//             room_number: order.room_number || null,
//             table_number: order.table_number || null,
//             parcel_number: order.parcel_number || null,
//             booking_id: order.booking_id || null,
//             kot_number: order.kot_number || null,
//             has_billing: true,
//             invoice_number: order.billings?.[0]?.invoice_number || null,
//           };
//         });
//         return { orders: transformedOrders };
//       }
//       return null;
//     } catch (error) {
//       console.error('Error fetching restaurant orders:', error);
//       return null;
//     }
//   };

//   const fetchRoomOrders = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         headers: getAuthHeaders(),
//       });
//       if (response.ok) {
//         const data = await response.json();
//         return data
//           .filter((order) => order.order_type === 'room')
//           .map((order) => {
//             const hasBilling = order.billing !== null;
//             const isPaid = hasBilling ? order.billing.is_paid : false;
//             const billingId = hasBilling ? order.billing.billing_id : null;
//             let paymentMethod = 'Not Paid';
//             if (order.payments && order.payments.length > 0) {
//               paymentMethod = order.payments[0].payment_method || 'Not Paid';
//               paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
//             } else if (isPaid) paymentMethod = 'Paid';

//             const orderedAt = order.created_at || order.ordered_at || new Date().toISOString();
//             const normalizedItems = (order.items || []).map((it) => ({
//               ...it,
//               is_cancelled: normalizeItemCancelled(it),
//             }));
//             const cancelled =
//               order.is_cancelled === true ||
//               order.status === 'cancelled' ||
//               order.status === 'Cancelled';

//             return {
//               order_id: order.id,
//               serial_number: order.serial_number != null ? String(order.serial_number) : null,
//               order_type: 'room',
//               order_status: order.status || 'pending',
//               is_cancelled: cancelled,
//               customer_name: order.customer_name || 'Guest',
//               customer_phone: order.phone_number || null,
//               customer_email: null,
//               table_number: order.table_number || null,
//               parcel_number: order.parcel_number || null,
//               room_number: order.room_number || null,
//               kot_number: null,
//               total_amount: order.totalprice || 0,
//               is_paid: isPaid,
//               ordered_at: orderedAt,
//               items: normalizedItems,
//               billing: order.billing || null,
//               billing_id: billingId,
//               booking_id: order.booking_id || null,
//               special_request: order.special_request || null,
//               gst_number: null,
//               has_billing: hasBilling,
//               payment_method: paymentMethod,
//               billings: order.billing ? [order.billing] : [],
//               payments: order.payments || [],
//               invoice_number: order.bill_number || null,
//             };
//           });
//       }
//       return [];
//     } catch (error) {
//       console.error('Error fetching room orders:', error);
//       return [];
//     }
//   };

//   const fetchAllOrders = async () => {
//     setLoading(true);
//     try {
//       const restaurantResult = await fetchRestaurantOrders();
//       const restaurantOrders = restaurantResult?.orders || [];
//       const roomOrders = await fetchRoomOrders();
//       const allOrders = [...restaurantOrders, ...roomOrders];
//       setOrders(allOrders);
//       setFilteredOrders(allOrders);
//       setCurrentPage(1);
//     } catch (error) {
//       console.error('Error fetching orders:', error);
//       toast.error('Failed to load orders');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ============ MAIN TABLE FILTERS ============
//   useEffect(() => {
//     let filtered = orders;

//     if (searchQuery) {
//       const query = String(searchQuery).toLowerCase();
//       filtered = filtered.filter((order) => {
//         const serial = order.serial_number != null ? String(order.serial_number).toLowerCase() : '';
//         const orderId = order.order_id != null ? String(order.order_id).toLowerCase() : '';
//         const customerName =
//           order.customer_name != null ? String(order.customer_name).toLowerCase() : '';
//         const tableNumber =
//           order.table_number != null ? String(order.table_number).toLowerCase() : '';
//         const parcelNumber =
//           order.parcel_number != null ? String(order.parcel_number).toLowerCase() : '';
//         const kotNumber = order.kot_number != null ? String(order.kot_number).toLowerCase() : '';
//         const roomNumber = order.room_number != null ? String(order.room_number).toLowerCase() : '';
//         const bookingId = order.booking_id != null ? String(order.booking_id).toLowerCase() : '';
//         const invoiceNo = String(getOrderInvoiceNumber(order) || '').toLowerCase();
//         return (
//           orderId.includes(query) ||
//           serial.includes(query) ||
//           customerName.includes(query) ||
//           tableNumber.includes(query) ||
//           parcelNumber.includes(query) ||
//           kotNumber.includes(query) ||
//           roomNumber.includes(query) ||
//           bookingId.includes(query) ||
//           invoiceNo.includes(query)
//         );
//       });
//     }

//     if (filterType !== 'all') filtered = filtered.filter((o) => o.order_type === filterType);

//     if (filterStatus !== 'all') {
//       if (filterStatus === 'pending') {
//         filtered = filtered.filter(
//           (o) => !o.is_cancelled && (o.order_status === 'pending' || o.order_status === 'Pending')
//         );
//       } else if (filterStatus === 'served') {
//         filtered = filtered.filter(
//           (o) => !o.is_cancelled && (o.order_status === 'served' || o.order_status === 'Served')
//         );
//       } else if (filterStatus === 'paid') filtered = filtered.filter((o) => o.is_paid === true);
//       else if (filterStatus === 'unpaid') filtered = filtered.filter((o) => o.is_paid === false);
//       else if (filterStatus === 'cancelled') {
//         filtered = filtered.filter(
//           (o) =>
//             o.is_cancelled === true ||
//             o.order_status === 'cancelled' ||
//             o.order_status === 'Cancelled'
//         );
//       }
//     }

//     if (filterDate !== 'all') {
//       const todayKey = getLocalTodayKey();
//       const yesterdayKey = getLocalYesterdayKey();
//       const weekStartKey = addDaysToKey(todayKey, -6);
//       const monthStartKey = todayKey.slice(0, 8) + '01';

//       const [yStr, mStr] = todayKey.split('-');
//       const y = parseInt(yStr, 10);
//       const m = parseInt(mStr, 10);
//       const prevMonthYear = m === 1 ? y - 1 : y;
//       const prevMonth = m === 1 ? 12 : m - 1;
//       const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
//       const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
//       const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
//         lastMonthEndDate.getMonth() + 1
//       ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

//       filtered = filtered.filter((order) => {
//         if (filterDate === 'today') {
//           const orderDateStr = getBackendDateKey(order.ordered_at);
//           return orderDateStr === todayKey;
//         }
//         if (!order.ordered_at) return false;
//         const orderDateStr = getBackendDateKey(order.ordered_at);
//         if (!orderDateStr) return false;
//         if (filterDate === 'yesterday') return orderDateStr === yesterdayKey;
//         if (filterDate === 'weekly')
//           return orderDateStr >= weekStartKey && orderDateStr <= todayKey;
//         if (filterDate === 'monthly')
//           return orderDateStr >= monthStartKey && orderDateStr <= todayKey;
//         if (filterDate === 'lastMonth')
//           return orderDateStr >= lastMonthStartKey && orderDateStr <= lastMonthEndKey;
//         if (filterDate === 'custom') {
//           if (!customDateFrom && !customDateTo) return true;
//           let ok = true;
//           if (customDateFrom) ok = ok && orderDateStr >= customDateFrom;
//           if (customDateTo) ok = ok && orderDateStr <= customDateTo;
//           return ok;
//         }
//         return true;
//       });
//     }

//     setFilteredOrders(filtered);
//     const groupCount = groupOrdersByDate(filtered).length;
//     setTotalPages(Math.max(1, Math.ceil(groupCount / 5)));
//     setCurrentPage(1);
//   }, [searchQuery, filterType, filterStatus, filterDate, customDateFrom, customDateTo, orders]);

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };
//   const handleNextPage = () => {
//     if (currentPage < totalPages) {
//       setCurrentPage(currentPage + 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };
//   const handlePrevPage = () => {
//     if (currentPage > 1) {
//       setCurrentPage(currentPage - 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const getCurrentPageGroups = () => {
//     const allGroups = groupOrdersByDate(filteredOrders);
//     const GROUPS_PER_PAGE = 5;
//     const startIndex = (currentPage - 1) * GROUPS_PER_PAGE;
//     return allGroups.slice(startIndex, startIndex + GROUPS_PER_PAGE);
//   };

//   // ============ ROOM FOLIOS BUILDER ============
//   const buildRoomFolios = (sourceOrders, opts = {}) => {
//     const { searchQuery = '', dateFilter = 'all', customFrom = '', customTo = '' } = opts;

//     const folioMap = {};

//     sourceOrders
//       .filter((o) => o.order_type === 'room' && !o.is_cancelled)
//       .forEach((o) => {
//         const roomKey = o.room_number || 'NOROOM';
//         const bookingKey = o.booking_id || 'NOBOOKING';
//         const key = `${roomKey}__${bookingKey}`;

//         if (!folioMap[key]) {
//           folioMap[key] = {
//             key,
//             room_number: o.room_number,
//             booking_id: o.booking_id,
//             customer_name: o.customer_name || 'Guest',
//             customer_phone: o.customer_phone || null,
//             orders: [],
//             total_amount: 0,
//             earliest_time: o.ordered_at,
//             latest_time: o.ordered_at,
//             invoice_numbers: [],
//           };
//         }

//         const f = folioMap[key];
//         f.orders.push(o);
//         f.total_amount += parseFloat(o.total_amount) || 0;

//         if (String(o.ordered_at) < String(f.earliest_time)) f.earliest_time = o.ordered_at;
//         if (String(o.ordered_at) > String(f.latest_time)) f.latest_time = o.ordered_at;

//         if (o.customer_name && o.customer_name !== 'Guest') f.customer_name = o.customer_name;
//         if (o.customer_phone) f.customer_phone = o.customer_phone;

//         if (o.invoice_number) {
//           f.invoice_numbers.push(String(o.invoice_number));
//         } else if (o.bill_number) {
//           f.invoice_numbers.push(String(o.bill_number));
//         } else if (o.billing && (o.billing.invoice_number || o.billing.bill_number)) {
//           f.invoice_numbers.push(String(o.billing.invoice_number || o.billing.bill_number));
//         } else if (
//           o.billings &&
//           o.billings[0] &&
//           (o.billings[0].invoice_number || o.billings[0].bill_number)
//         ) {
//           f.invoice_numbers.push(String(o.billings[0].invoice_number || o.billings[0].bill_number));
//         }
//       });

//     let folios = Object.values(folioMap).map((f) => {
//       f.orders.sort((a, b) => String(a.ordered_at || '').localeCompare(String(b.ordered_at || '')));
//       f.order_count = f.orders.length;

//       const invs = f.invoice_numbers.filter(Boolean);
//       if (invs.length === 0) {
//         f.invoice_label = null;
//         f.invoice_range = null;
//       } else if (invs.length === 1) {
//         f.invoice_label = invs[0];
//         f.invoice_range = invs[0];
//       } else {
//         const parsed = invs
//           .map((i) => {
//             const m = String(i).match(/(\d+)$/);
//             return m ? { full: i, num: parseInt(m[1], 10) } : null;
//           })
//           .filter(Boolean);

//         if (parsed.length >= 2) {
//           parsed.sort((a, b) => a.num - b.num);
//           const first = parsed[0].full;
//           const last = parsed[parsed.length - 1].full;
//           const prefixMatch = first.match(/^(.*?)(\d+)$/);
//           if (prefixMatch) {
//             const prefix = prefixMatch[1];
//             const label = `${prefix}${parsed[0].num} → ${prefix}${parsed[parsed.length - 1].num}`;
//             f.invoice_label = label;
//             f.invoice_range = { first, last, prefix };
//           } else {
//             f.invoice_label = `${first} → ${last}`;
//             f.invoice_range = { first, last };
//           }
//         } else {
//           f.invoice_label = `${invs[0]} → ${invs[invs.length - 1]}`;
//           f.invoice_range = { first: invs[0], last: invs[invs.length - 1] };
//         }
//       }

//       return f;
//     });

//     if (searchQuery && searchQuery.trim()) {
//       const q = searchQuery.toLowerCase();
//       folios = folios.filter((f) => {
//         if (
//           String(f.room_number || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         if (
//           String(f.booking_id || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         if (
//           String(f.customer_name || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         if (
//           String(f.customer_phone || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         if (
//           String(f.invoice_label || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         return f.orders.some((o) =>
//           String(o.order_id || '')
//             .toLowerCase()
//             .includes(q)
//         );
//       });
//     }

//     if (dateFilter !== 'all') {
//       const todayKey = getLocalTodayKey();
//       const yesterdayKey = getLocalYesterdayKey();
//       const weekStartKey = addDaysToKey(todayKey, -6);
//       const monthStartKey = todayKey.slice(0, 8) + '01';

//       const [yStr, mStr] = todayKey.split('-');
//       const y = parseInt(yStr, 10);
//       const m = parseInt(mStr, 10);
//       const prevMonthYear = m === 1 ? y - 1 : y;
//       const prevMonth = m === 1 ? 12 : m - 1;
//       const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
//       const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
//       const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
//         lastMonthEndDate.getMonth() + 1
//       ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

//       folios = folios
//         .map((f) => {
//           const inRangeOrders = f.orders.filter((o) => {
//             const orderDateStr = getBackendDateKey(o.ordered_at);
//             if (!orderDateStr) return false;
//             if (dateFilter === 'today') return orderDateStr === todayKey;
//             if (dateFilter === 'yesterday') return orderDateStr === yesterdayKey;
//             if (dateFilter === 'weekly')
//               return orderDateStr >= weekStartKey && orderDateStr <= todayKey;
//             if (dateFilter === 'monthly')
//               return orderDateStr >= monthStartKey && orderDateStr <= todayKey;
//             if (dateFilter === 'lastMonth')
//               return orderDateStr >= lastMonthStartKey && orderDateStr <= lastMonthEndKey;
//             if (dateFilter === 'custom') {
//               let ok = true;
//               if (customFrom) ok = ok && orderDateStr >= customFrom;
//               if (customTo) ok = ok && orderDateStr <= customTo;
//               return ok;
//             }
//             return true;
//           });

//           if (inRangeOrders.length === 0) return null;

//           const total = inRangeOrders.reduce((s, o) => s + (parseFloat(o.total_amount) || 0), 0);

//           const earliest = inRangeOrders.reduce(
//             (min, o) => (String(o.ordered_at) < String(min) ? o.ordered_at : min),
//             inRangeOrders[0].ordered_at
//           );
//           const latest = inRangeOrders.reduce(
//             (max, o) => (String(o.ordered_at) > String(max) ? o.ordered_at : max),
//             inRangeOrders[0].ordered_at
//           );

//           return {
//             ...f,
//             orders: inRangeOrders,
//             order_count: inRangeOrders.length,
//             total_amount: total,
//             earliest_time: earliest,
//             latest_time: latest,
//           };
//         })
//         .filter(Boolean);
//     }

//     folios.sort((a, b) => {
//       const ra = String(a.room_number || '').padStart(10, '0');
//       const rb = String(b.room_number || '').padStart(10, '0');
//       return ra.localeCompare(rb);
//     });

//     return folios;
//   };

//   const roomFolios = useMemo(
//     () =>
//       buildRoomFolios(orders, {
//         searchQuery: folioSearchQuery,
//         dateFilter: folioFilterDate,
//         customFrom: folioCustomFrom,
//         customTo: folioCustomTo,
//       }),
//     [orders, folioSearchQuery, folioFilterDate, folioCustomFrom, folioCustomTo]
//   );

//   const folioSummary = useMemo(() => {
//     const total = roomFolios.reduce((s, f) => s + f.total_amount, 0);
//     const rooms = roomFolios.length;
//     const ordersCount = roomFolios.reduce((s, f) => s + f.order_count, 0);
//     const invoicesCount = roomFolios.reduce((s, f) => s + f.invoice_numbers.length, 0);
//     return { total, rooms, ordersCount, invoicesCount };
//   }, [roomFolios]);

//   // ============ EDIT GUEST ============
//   const handleCompanySelect = (company) => {
//     setSelectedCompany(company);
//     setCompanySearchText(company.companyname);
//     setEditGuestData((prev) => ({
//       ...prev,
//       customer_name: company.companyname,
//       gst_number: company.gstnumber || '',
//     }));
//     setShowCompanyDropdown(false);
//   };

//   const handleOpenEditGuest = (order) => {
//     if (order.order_type === 'room') {
//       toast.info('Edit guest details is only available for restaurant and parcel orders');
//       return;
//     }
//     setSelectedOrder(order);
//     setEditGuestData({
//       customer_name: order.customer_name || '',
//       customer_phone: order.customer_phone || '',
//       customer_email: order.customer_email || '',
//       gst_number: order.gst_number || '',
//     });
//     setCompanySearchText('');
//     setSelectedCompany(null);
//     setShowCompanyDropdown(false);
//     setShowEditGuestModal(true);
//   };

//   const handleEditGuestChange = (e) => {
//     const { name, value } = e.target;
//     setEditGuestData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleUpdateGuestDetails = async () => {
//     if (!selectedOrder) return;
//     setIsUpdatingGuest(true);
//     try {
//       const url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${selectedOrder.order_id}/customer-details`;
//       const payload = {
//         customer_name: editGuestData.customer_name || 'Guest',
//         customer_phone: editGuestData.customer_phone || '',
//         customer_email: editGuestData.customer_email || '',
//         gst_number: editGuestData.gst_number || '',
//       };
//       const response = await fetch(url, {
//         method: 'PATCH',
//         headers: getAuthJsonHeaders(),
//         body: JSON.stringify(payload),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success('Customer details updated successfully!');
//         setShowEditGuestModal(false);
//         await fetchAllOrders();
//       } else {
//         throw new Error(data.message || data.detail || 'Failed to update customer details');
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Failed to update customer details');
//     } finally {
//       setIsUpdatingGuest(false);
//     }
//   };

//   // ============ INVOICE ============
//   const fetchDetailedInvoiceData = async (orderId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/billing/order/${orderId}`, {
//         headers: getAuthHeaders(),
//       });
//       if (response.ok) return await response.json();
//       return null;
//     } catch (error) {
//       console.error('Error fetching detailed billing data:', error);
//       return null;
//     }
//   };

//   const fetchInvoiceData = async (order) => {
//     setIsLoadingInvoice(true);
//     try {
//       if (order.order_type === 'room') {
//         const url = `${BQ_BASE_URL}/bq/api/food-invoice/data/${order.order_id}`;
//         const response = await fetch(url, { headers: getAuthHeaders() });
//         if (response.ok) {
//           const data = await response.json();
//           const rawItems = data.items || data.order_details || [];
//           const normalizedItems = rawItems.map((it) => ({
//             ...it,
//             is_cancelled: normalizeItemCancelled(it),
//           }));
//           return {
//             ...data,
//             items: normalizedItems,
//             room_number: order.room_number || data.room_number || null,
//             table_number: order.table_number || data.table_number || null,
//             booking_id: order.booking_id || data.booking_id || null,
//             kot_number: order.kot_number || data.kot_number || null,
//             parcel_number: order.parcel_number || data.parcel_number || null,
//           };
//         }
//         toast.error('Failed to fetch room invoice data');
//         return null;
//       } else {
//         const billingData = await fetchDetailedInvoiceData(order.order_id);
//         if (!billingData) {
//           toast.error('Failed to fetch billing details');
//           return null;
//         }
//         const allBillings = billingData.billings || [];
//         const primaryBilling = allBillings.find((b) => b.invoice_number) || allBillings[0] || {};
//         const rawItems = billingData.order_details || [];
//         const normalizedItems = rawItems.map((it) => ({
//           ...it,
//           is_cancelled: normalizeItemCancelled(it),
//         }));
//         return {
//           invoice_number: primaryBilling.invoice_number ?? 'Not yet issued',
//           invoice_date:
//             primaryBilling.generated_at || primaryBilling.paid_at || new Date().toISOString(),
//           order_id: billingData.order_id,
//           order_type: billingData.order_type,
//           is_cancelled:
//             billingData.is_cancelled === true ||
//             billingData.order_status === 'cancelled' ||
//             billingData.order_status === 'Cancelled',
//           customer_name: billingData.customer_name || 'Guest',
//           customer_phone: billingData.customer_phone || '-',
//           customer_email: billingData.customer_email || '-',
//           customer_gst: billingData.gst_number || '-',
//           items: normalizedItems,
//           subtotal: billingData.financials?.subtotal || 0,
//           total_tax: billingData.financials?.tax_amount || 0,
//           total_discount: billingData.financials?.discount_amount || 0,
//           total_service: billingData.financials?.service_charge || 0,
//           grand_total: billingData.financials?.total_amount || billingData.net_payable || 0,
//           total_paid: billingData.total_paid || 0,
//           balance_due: billingData.net_payable || 0,
//           amount_in_words: `${billingData.net_payable || 0} Rupees Only`,
//           payment_method: billingData.billings?.[0]?.payments?.[0]?.method || 'Not Paid Yet',
//           property_name: billingData.property?.name || 'R&T INTERNATIONAL',
//           property_address:
//             billingData.property?.address || 'Taluka Khed Chiplun Area, Lote, Maharashtra, 415722',
//           property_contact: billingData.property?.contact || '8698732336',
//           property_email: billingData.property?.email || 'reception@hotelpagoda.com',
//           property_gst: billingData.property?.gst || '27AAAFR6160L1ZZ',
//           billing_id: billingData.billings?.[0]?.billing_id || null,
//           billings: billingData.billings || [],
//           payments: billingData.billings?.[0]?.payments || [],
//           financials: billingData.financials,
//           room_number: order.room_number || billingData.room_number || null,
//           table_number: order.table_number || billingData.table_number || null,
//           parcel_number: order.parcel_number || billingData.parcel_number || null,
//           booking_id: order.booking_id || billingData.booking_id || null,
//           kot_number: order.kot_number || billingData.kot_number || null,
//         };
//       }
//     } catch (error) {
//       console.error('Error fetching invoice:', error);
//       toast.error('Failed to load invoice');
//       return null;
//     } finally {
//       setIsLoadingInvoice(false);
//     }
//   };

//   const handleViewInvoice = async (order) => {
//     setInvoiceData(null);
//     setShowInvoiceModal(false);
//     setIsRoomOrder(false);
//     setSelectedOrder(order);
//     try {
//       const data = await fetchInvoiceData(order);
//       if (data) {
//         setInvoiceData(data);
//         setIsRoomOrder(order.order_type === 'room');
//         setTimeout(() => setShowInvoiceModal(true), 100);
//       } else {
//         toast.error('Could not load invoice data');
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error('Error loading invoice');
//     }
//   };

//   const handleCloseInvoiceModal = () => {
//     setShowInvoiceModal(false);
//     setTimeout(() => {
//       setInvoiceData(null);
//       setIsRoomOrder(false);
//       setSelectedOrder(null);
//     }, 300);
//   };

//   // ============ PAYMENT ============
//   const fetchBillingInfo = async (orderId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/billing/order/${orderId}`, {
//         headers: getAuthHeaders(),
//       });
//       if (response.ok) {
//         const data = await response.json();
//         if (data.billings && data.billings.length > 0) {
//           const billing = data.billings[0];
//           return {
//             billing_id: billing.billing_id,
//             billing_data: data,
//             final_amount: billing.final_amount || data.net_payable || 0,
//             is_paid: billing.is_paid || false,
//           };
//         }
//         if (data.financials) {
//           return {
//             billing_id: null,
//             billing_data: data,
//             final_amount: data.net_payable || data.financials.total_amount || 0,
//             is_paid: false,
//             needs_billing_creation: true,
//           };
//         }
//         return null;
//       }
//       return null;
//     } catch (error) {
//       console.error('Error fetching billing info:', error);
//       return null;
//     }
//   };

//   const handleDownloadPDF = async (order) => {
//     try {
//       let url;
//       if (order.order_type === 'room')
//         url = `${BQ_BASE_URL}/bq/api/food-invoice/pdf/${order.order_id}`;
//       else url = `${BQ_BASE_URL}/bq/api/restaurant/invoice/pdf/${order.order_id}`;

//       const response = await fetch(url, { headers: getAuthHeaders() });
//       if (response.ok) {
//         const blob = await response.blob();
//         const urlObj = window.URL.createObjectURL(blob);
//         const a = document.createElement('a');
//         a.href = urlObj;
//         const contentDisposition = response.headers.get('content-disposition');
//         const filename = contentDisposition
//           ? contentDisposition.split('filename=')[1].replace(/"/g, '')
//           : `invoice_${order.order_id}.pdf`;
//         a.download = filename;
//         document.body.appendChild(a);
//         a.click();
//         window.URL.revokeObjectURL(urlObj);
//         document.body.removeChild(a);
//         toast.success('Invoice downloaded successfully');
//       } else {
//         toast.error('Failed to download invoice');
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to download invoice');
//     }
//   };

//   // ============ KOT ============
//   const handlePrintKOTRoom = async (order) => {
//     if (!order.order_id) return toast.error('Order ID not found');
//     setIsPrintingKOT(true);
//     try {
//       const url = `${BQ_BASE_URL}/bq/api/food/print/kot/${order.order_id}`;
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: getAuthHeaders(),
//         body: '',
//       });
//       const data = await response.json();
//       if (response.ok) toast.success(`KOT sent to kitchen printer! KOT: ${data.kot_number || ''}`);
//       else toast.error(data.message || 'Failed to print KOT');
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to print KOT');
//     } finally {
//       setIsPrintingKOT(false);
//     }
//   };

//   const handlePrintKOTRestaurant = async (order) => {
//     if (!order.order_id) return toast.error('Order ID not found');
//     setIsPrintingKOT(true);
//     try {
//       const url = `${BQ_BASE_URL}/bq/api/restaurant/print/kot/${order.order_id}`;
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: getAuthHeaders(),
//         body: '',
//       });
//       const data = await response.json();
//       if (response.ok) {
//         const batchInfo =
//           data.batches && data.batches.length > 0
//             ? `KOT: ${data.batches.map((b) => b.kot_number).join(', ')}`
//             : '';
//         toast.success(`KOT sent to kitchen printer! ${batchInfo}`);
//       } else toast.error(data.message || 'Failed to print KOT');
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to print KOT');
//     } finally {
//       setIsPrintingKOT(false);
//     }
//   };

//   const handlePrintKOT = async (order) => {
//     if (order.order_type === 'room') await handlePrintKOTRoom(order);
//     else await handlePrintKOTRestaurant(order);
//   };

//   const handlePrintInvoice = async (order) => {
//     setIsPrinting(true);
//     try {
//       let url;
//       if (order.order_type === 'room') url = `${BQ_BASE_URL}/bq/api/food/print/${order.order_id}`;
//       else url = `${BQ_BASE_URL}/bq/api/restaurant/print/${order.order_id}`;

//       const response = await fetch(url, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: '',
//       });
//       const data = await response.json();
//       if (response.ok) toast.success('Invoice sent to printer successfully!');
//       else toast.error(data.message || 'Failed to print invoice');
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to print invoice');
//     } finally {
//       setIsPrinting(false);
//     }
//   };

//   const handleSendEmail = async (order) => {
//     if (!emailRecipient) return toast.error('Please enter recipient email');
//     setIsSendingEmail(true);
//     try {
//       let url;
//       if (order.order_type === 'room') {
//         url = `${BQ_BASE_URL}/bq/api/food-invoice/email/${order.order_id}?recipient_email=${encodeURIComponent(
//           emailRecipient
//         )}&cc_hotel=true`;
//       } else {
//         url = `${BQ_BASE_URL}/bq/api/restaurant/invoice/email/${order.order_id}?recipient_email=${encodeURIComponent(
//           emailRecipient
//         )}&cc_hotel=true`;
//       }
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: '',
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success('Invoice email sent successfully!');
//         setShowEmailModal(false);
//         setEmailRecipient('');
//       } else toast.error(data.message || 'Failed to send email');
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to send email');
//     } finally {
//       setIsSendingEmail(false);
//     }
//   };

//   const handleRoomCashPayment = async (order) => {
//     if (order.is_paid) {
//       toast.info('This order is already paid');
//       setShowPaymentModal(false);
//       setSelectedPaymentMethod(null);
//       return;
//     }
//     if (!order.booking_id) return toast.error('Booking ID not found for this room order');
//     if (!order.has_billing && !order.billing_id)
//       return toast.error('No billing record found. Please bill the order first.');

//     let billingId = order.billing?.billing_id || order.billing_id;
//     if (!billingId) {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//           headers: getAuthHeaders(),
//         });
//         if (response.ok) {
//           const data = await response.json();
//           const orderData = data.find((o) => o.id === order.order_id);
//           if (orderData && orderData.billing) {
//             billingId = orderData.billing.billing_id;
//             order.billing = orderData.billing;
//             order.billing_id = billingId;
//             order.has_billing = true;
//           }
//         }
//       } catch (e) {
//         console.error(e);
//       }
//     }
//     if (!billingId) return toast.error('No billing ID found.');

//     setIsProcessingPayment(true);
//     try {
//       const paymentData = {
//         booking_id: order.booking_id,
//         billing_id: billingId,
//         amount: order.total_amount,
//       };
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: JSON.stringify(paymentData),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success('Payment successful!');
//         setShowPaymentModal(false);
//         setSelectedPaymentMethod(null);
//         await fetchAllOrders();
//         handleCloseInvoiceModal();
//       } else throw new Error(data.detail || 'Payment failed');
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Failed to process cash payment');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handleRoomRazorpayPayment = async (order) => {
//     if (!razorpayLoaded) return toast.error('Razorpay script is not loaded yet.');
//     if (order.is_paid) {
//       toast.info('This order is already paid');
//       setShowPaymentModal(false);
//       setSelectedPaymentMethod(null);
//       return;
//     }
//     if (!order.booking_id) return toast.error('Booking ID not found for this room order');
//     if (!order.has_billing && !order.billing_id)
//       return toast.error('No billing record found. Please bill the order first.');

//     let billingId = order.billing?.billing_id || order.billing_id;
//     if (!billingId) {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//           headers: getAuthHeaders(),
//         });
//         if (response.ok) {
//           const data = await response.json();
//           const orderData = data.find((o) => o.id === order.order_id);
//           if (orderData && orderData.billing) {
//             billingId = orderData.billing.billing_id;
//             order.billing = orderData.billing;
//             order.billing_id = billingId;
//             order.has_billing = true;
//           }
//         }
//       } catch (e) {
//         console.error(e);
//       }
//     }
//     if (!billingId) return toast.error('No billing ID found.');

//     setIsProcessingPayment(true);
//     try {
//       const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${order.booking_id}&strict_target=true`;
//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: '',
//       });
//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to create payment order');
//       }
//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: orderData.amount,
//           currency: 'INR',
//           name: 'Hotel Restaurant',
//           description: `Room Order - Room ${order.room_number || 'N/A'}`,
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//             };
//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
//                 {
//                   method: 'POST',
//                   headers: getAuthJsonHeaders(),
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );
//               if (verificationResponse.ok) {
//                 toast.success('Payment successful and verified!');
//                 setShowPaymentModal(false);
//                 setSelectedPaymentMethod(null);
//                 await fetchAllOrders();
//                 handleCloseInvoiceModal();
//               } else {
//                 const errorData = await verificationResponse.json();
//                 toast.error(errorData.detail || 'Payment verification failed.');
//               }
//             } catch (error) {
//               console.error(error);
//               toast.error('Error verifying payment.');
//             }
//           },
//           prefill: {
//             name: order.customer_name || 'Guest',
//             email: order.customer_email || 'guest@example.com',
//             contact: order.customer_phone || '9999999999',
//           },
//           theme: { color: '#3399cc' },
//         };
//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       } else throw new Error('No Razorpay order ID received');
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Error processing payment.');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handleRestaurantCashPayment = async (order) => {
//     setIsProcessingPayment(true);
//     try {
//       const billingInfo = await fetchBillingInfo(order.order_id);
//       if (!billingInfo) {
//         toast.error('Could not retrieve billing information');
//         return;
//       }
//       if (!billingInfo.billing_id) {
//         toast.error('No billing record found.');
//         return;
//       }
//       if (billingInfo.is_paid) {
//         toast.info('This order is already paid');
//         setShowPaymentModal(false);
//         setSelectedPaymentMethod(null);
//         await fetchAllOrders();
//         return;
//       }
//       const paymentData = {
//         order_id: order.order_id,
//         billing_id: billingInfo.billing_id,
//         amount: billingInfo.final_amount,
//         notes: `Cash payment for order ${order.order_id}`,
//       };
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/payments/cash`, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: JSON.stringify(paymentData),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`Payment successful! Change due: ₹${data.change_due || 0}`);
//         setShowPaymentModal(false);
//         setSelectedPaymentMethod(null);
//         await fetchAllOrders();
//         handleCloseInvoiceModal();
//       } else throw new Error(data.detail || 'Payment failed');
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Failed to process cash payment');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handleRestaurantRazorpayPayment = async (order) => {
//     if (!razorpayLoaded) return toast.error('Razorpay script is not loaded yet.');
//     setIsProcessingPayment(true);
//     try {
//       const billingInfo = await fetchBillingInfo(order.order_id);
//       if (!billingInfo || !billingInfo.billing_id) {
//         toast.error('No billing record found.');
//         return;
//       }
//       if (billingInfo.is_paid) {
//         toast.info('This order is already paid');
//         setShowPaymentModal(false);
//         setSelectedPaymentMethod(null);
//         await fetchAllOrders();
//         return;
//       }
//       const apiUrl = `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/create-order`;
//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: JSON.stringify({
//           order_id: order.order_id,
//           billing_id: billingInfo.billing_id,
//         }),
//       });
//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to create payment order');
//       }
//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: orderData.amount,
//           currency: 'INR',
//           name: 'Restaurant',
//           description: `Order ${order.order_id}`,
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//             };
//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/verify`,
//                 {
//                   method: 'POST',
//                   headers: getAuthJsonHeaders(),
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );
//               if (verificationResponse.ok) {
//                 toast.success('Payment successful and verified!');
//                 setShowPaymentModal(false);
//                 setSelectedPaymentMethod(null);
//                 await fetchAllOrders();
//                 handleCloseInvoiceModal();
//               } else toast.error('Payment verification failed.');
//             } catch (error) {
//               console.error(error);
//               toast.error('Error verifying payment.');
//             }
//           },
//           prefill: {
//             name: order.customer_name || 'Guest',
//             email: order.customer_email || 'guest@example.com',
//             contact: order.customer_phone || '9999999999',
//           },
//           theme: { color: '#3399cc' },
//         };
//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Error processing payment.');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handlePaymentOption = (method) => {
//     setSelectedPaymentMethod(method);
//     setShowPaymentModal(true);
//   };

//   const confirmPayment = async () => {
//     if (!selectedOrder) return;
//     if (selectedOrder.is_paid) {
//       toast.info('This order is already paid');
//       setShowPaymentModal(false);
//       setSelectedPaymentMethod(null);
//       return;
//     }
//     if (selectedOrder.order_type === 'room') {
//       if (selectedPaymentMethod === 'cash') await handleRoomCashPayment(selectedOrder);
//       else if (selectedPaymentMethod === 'razorpay') await handleRoomRazorpayPayment(selectedOrder);
//     } else {
//       if (selectedPaymentMethod === 'cash') await handleRestaurantCashPayment(selectedOrder);
//       else if (selectedPaymentMethod === 'razorpay')
//         await handleRestaurantRazorpayPayment(selectedOrder);
//     }
//   };

//   // ============ TRANSFER (table → room / parcel → room) ============
//   const handleTransferToRoomSubmit = async () => {
//     if (!transferRoomNumber) return toast.error('Please enter room number');
//     setIsTransferring(true);
//     try {
//       const payload = { room_number: transferRoomNumber };
//       if (transferBookingId && transferBookingId.trim() !== '')
//         payload.booking_id = transferBookingId.trim();
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/transfer-to-room`,
//         {
//           method: 'POST',
//           headers: getAuthJsonHeaders(),
//           body: JSON.stringify(payload),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Transferred to room successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferRoomNumber('');
//         setTransferBookingId('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.detail || data.message || 'Transfer failed');
//     } catch (error) {
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   const handleParcelTransferToRoomSubmit = async () => {
//     if (!transferRoomNumber) return toast.error('Please enter room number');
//     setIsTransferring(true);
//     try {
//       const payload = { room_number: transferRoomNumber };
//       if (transferBookingId && transferBookingId.trim() !== '')
//         payload.booking_id = transferBookingId.trim();
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/parcel-transfer-to-room`,
//         {
//           method: 'POST',
//           headers: getAuthJsonHeaders(),
//           body: JSON.stringify(payload),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Parcel transferred to room successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferRoomNumber('');
//         setTransferBookingId('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.detail || data.message || 'Transfer failed');
//     } catch (error) {
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   const handleTransferToTableSubmit = async () => {
//     if (!transferNewTable) return toast.error('Please enter a table number');
//     setIsTransferring(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/transfer-order`, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: JSON.stringify({
//           order_id: transferOrder.order_id,
//           from_table_number: transferOrder.table_number || transferOrder.room_number,
//           to_table_number: transferNewTable,
//         }),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Transferred to table successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferNewTable('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.message || 'Transfer failed');
//     } catch (error) {
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   const handleParcelTransferToTableSubmit = async () => {
//     if (!transferNewTable) return toast.error('Please enter a table number');
//     setIsTransferring(true);
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/parcel-transfer-to-table`,
//         {
//           method: 'POST',
//           headers: getAuthJsonHeaders(),
//           body: JSON.stringify({ table_number: transferNewTable }),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Parcel transferred to table successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferNewTable('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.detail || data.message || 'Transfer failed');
//     } catch (error) {
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   const handleMergeBillsSubmit = async () => {
//     if (!transferMergeFrom || !transferMergeTo) return toast.error('Please fill in all fields');
//     if (transferMergeFrom === transferMergeTo)
//       return toast.error('Source and target tables cannot be same');

//     setIsTransferring(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/merge`, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: JSON.stringify({
//           from_table_number: transferMergeFrom,
//           to_table_number: transferMergeTo,
//         }),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Merged successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferMergeFrom('');
//         setTransferMergeTo('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.message || 'Merge failed');
//     } catch (error) {
//       toast.error(error.message || 'Merge failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   // ── ROOM → ROOM TRANSFER ──
//   const handleRoomToRoomTransferSubmit = async () => {
//     if (!transferToRoomNumber || !transferToRoomNumber.trim()) {
//       toast.error('Please enter destination room number');
//       return;
//     }
//     if (String(transferToRoomNumber).trim() === String(transferOrder?.room_number || '').trim()) {
//       toast.error('Destination room is the same as source room');
//       return;
//     }

//     setIsTransferring(true);
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/orders/room/${transferOrder.order_id}/transfer-to-room`,
//         {
//           method: 'POST',
//           headers: getAuthJsonHeaders(),
//           body: JSON.stringify({
//             to_room_number: String(transferToRoomNumber).trim(),
//             to_booking_id: null,
//             reason: transferToRoomReason.trim() || null,
//           }),
//         }
//       );

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Room transfer successful!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferToRoomNumber('');
//         setTransferToRoomReason('');
//         setTransferOrder(null);
//         await fetchAllOrders();
//       } else {
//         toast.error(data.detail || data.message || 'Transfer failed');
//       }
//     } catch (error) {
//       console.error('Room transfer error:', error);
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   // ── PARCEL → PARCEL MERGE ──
//   const handleParcelMergeSubmit = async () => {
//     if (!parcelMergeFrom || !parcelMergeTo) {
//       toast.error('Please fill in both parcel numbers');
//       return;
//     }
//     if (parcelMergeFrom === parcelMergeTo) {
//       toast.error('Source and target parcels cannot be the same');
//       return;
//     }

//     setIsTransferring(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/parcels/merge`, {
//         method: 'POST',
//         headers: getAuthJsonHeaders(),
//         body: JSON.stringify({
//           from_parcel_number: parcelMergeFrom,
//           to_parcel_number: parcelMergeTo,
//         }),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Parcels merged successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setParcelMergeFrom('');
//         setParcelMergeTo('');
//         setTransferOrder(null);
//         await fetchAllOrders();
//       } else {
//         toast.error(data.detail || data.message || 'Merge failed');
//       }
//     } catch (error) {
//       console.error('Parcel merge error:', error);
//       toast.error(error.message || 'Merge failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   // ── AUDIT LOG FETCH ──
//   const fetchAuditLogs = async (eventType = 'all') => {
//     setIsLoadingAudit(true);
//     try {
//       const params = new URLSearchParams({ take: '100' });
//       if (eventType && eventType !== 'all') {
//         params.append('event_type', eventType);
//       }
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/audit/logs?${params.toString()}`, {
//         headers: getAuthHeaders(),
//       });
//       if (response.ok) {
//         const data = await response.json();
//         setAuditLogs(data.data || []);
//       } else {
//         toast.error('Failed to load audit logs');
//         setAuditLogs([]);
//       }
//     } catch (error) {
//       console.error('Audit fetch error:', error);
//       toast.error('Failed to load audit logs');
//       setAuditLogs([]);
//     } finally {
//       setIsLoadingAudit(false);
//     }
//   };

//   // ============ BADGES ============
//   const getStatusBadge = (order) => {
//     const cancelled =
//       order.is_cancelled === true ||
//       order.order_status === 'cancelled' ||
//       order.order_status === 'Cancelled';
//     if (cancelled) {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
//           <FaTimes className="mr-1" size={10} /> Cancelled
//         </span>
//       );
//     }
//     if (order.is_paid) {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
//           <FaCheckCircle className="mr-1" size={10} /> Paid
//         </span>
//       );
//     }
//     if (order.order_status === 'served' || order.order_status === 'Served') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
//           <FaClock className="mr-1" size={10} /> Served
//         </span>
//       );
//     }
//     return (
//       <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
//         <FaClock className="mr-1" size={10} /> Pending
//       </span>
//     );
//   };

//   const getOrderTypeBadge = (type) => {
//     if (type === 'dining') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
//           <FaTable className="mr-1" size={10} /> Dining
//         </span>
//       );
//     } else if (type === 'parcel') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
//           <FaBox className="mr-1" size={10} /> Parcel
//         </span>
//       );
//     } else if (type === 'room') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200">
//           <FaHotel className="mr-1" size={10} /> Room
//         </span>
//       );
//     }
//     return null;
//   };

//   const getPaymentMethodBadge = (method) => {
//     if (!method || method === 'Not Paid' || method === 'Not Paid Yet') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
//           <FaMoneyBill className="mr-1" size={10} /> Not Paid
//         </span>
//       );
//     }
//     const methodColors = {
//       Cash: 'bg-green-100 text-green-800 border-green-200',
//       Netbanking: 'bg-blue-100 text-blue-800 border-blue-200',
//       Card: 'bg-purple-100 text-purple-800 border-purple-200',
//       UPI: 'bg-cyan-100 text-cyan-800 border-cyan-200',
//       Razorpay: 'bg-indigo-100 text-indigo-800 border-indigo-200',
//       Paid: 'bg-green-100 text-green-800 border-green-200',
//     };
//     const colorClass = methodColors[method] || 'bg-gray-100 text-gray-700 border-gray-200';
//     return (
//       <span
//         className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}
//       >
//         <FaMoneyBill className="mr-1" size={10} /> {method}
//       </span>
//     );
//   };

//   // ============ LOADING ============
//   const LoadingSkeleton = () => (
//     <div className="animate-pulse">
//       <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//         <table className="min-w-full">
//           <thead className="bg-gray-50">
//             <tr>
//               {[...Array(10)].map((_, i) => (
//                 <th key={i} className="px-6 py-3">
//                   <div className="h-4 bg-gray-200 rounded"></div>
//                 </th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {[...Array(5)].map((_, i) => (
//               <tr key={i} className="border-t border-gray-100">
//                 {[...Array(10)].map((_, j) => (
//                   <td key={j} className="px-6 py-4">
//                     <div className="h-4 bg-gray-200 rounded w-20"></div>
//                   </td>
//                 ))}
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );

//   // ============ PAGINATION ============
//   const Pagination = () => {
//     if (totalPages <= 1) return null;
//     const pageNumbers = [];
//     const maxVisiblePages = 5;
//     let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
//     let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
//     if (endPage - startPage < maxVisiblePages - 1)
//       startPage = Math.max(1, endPage - maxVisiblePages + 1);
//     for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

//     return (
//       <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl mt-4">
//         <div className="flex-1 flex justify-between sm:hidden">
//           <button
//             onClick={handlePrevPage}
//             disabled={currentPage === 1}
//             className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg disabled:opacity-50"
//           >
//             Previous
//           </button>
//           <button
//             onClick={handleNextPage}
//             disabled={currentPage === totalPages}
//             className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg disabled:opacity-50"
//           >
//             Next
//           </button>
//         </div>
//         <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
//           <div>
//             <p className="text-sm text-gray-700">
//               Page <span className="font-medium">{currentPage}</span> of{' '}
//               <span className="font-medium">{totalPages}</span> (date groups)
//             </p>
//           </div>
//           <div>
//             <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
//               <button
//                 onClick={handlePrevPage}
//                 disabled={currentPage === 1}
//                 className="relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium bg-white border border-gray-300 disabled:opacity-50"
//               >
//                 <FaChevronLeft className="h-4 w-4" />
//               </button>
//               {pageNumbers.map((page) => (
//                 <button
//                   key={page}
//                   onClick={() => handlePageChange(page)}
//                   className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
//                     currentPage === page
//                       ? 'z-10 bg-black text-white border-black'
//                       : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
//                   }`}
//                 >
//                   {page}
//                 </button>
//               ))}
//               <button
//                 onClick={handleNextPage}
//                 disabled={currentPage === totalPages}
//                 className="relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium bg-white border border-gray-300 disabled:opacity-50"
//               >
//                 <FaChevronRight className="h-4 w-4" />
//               </button>
//             </nav>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   // ============================================================
//   // ============ ROOM FOLIO POPUP COMPONENT ====================
//   // ============================================================
//   const RoomFolioModal = () => {
//     if (!showRoomFolioModal) return null;

//     const toggleExpand = (key) => setExpandedFolioKey((prev) => (prev === key ? null : key));

//     return (
//       <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
//         <div className="bg-gray-50 rounded-2xl w-full max-w-6xl max-h-[95vh] flex flex-col shadow-2xl overflow-hidden">
//           <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shrink-0">
//             <div className="flex items-center gap-3">
//               <div className="p-2 bg-gradient-to-br from-rose-500 to-rose-700 rounded-lg text-white">
//                 <FaLayerGroup />
//               </div>
//               <div>
//                 <h2 className="text-xl font-bold text-gray-900">
//                   Room Folio — Consolidated Billing
//                 </h2>
//                 <p className="text-xs text-gray-500">
//                   All room food orders grouped by Room + Booking
//                 </p>
//               </div>
//             </div>
//             <button
//               onClick={() => {
//                 setShowRoomFolioModal(false);
//                 setExpandedFolioKey(null);
//                 if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
//                 setFolioSearchQuery('');
//               }}
//               className="p-2 hover:bg-gray-100 rounded-lg transition-all"
//             >
//               <FaTimes className="w-5 h-5 text-gray-600" />
//             </button>
//           </div>

//           <div className="bg-white border-b border-gray-200 px-6 py-3 shrink-0">
//             <div className="flex flex-wrap gap-2 items-center">
//               <div className="flex items-center gap-2 flex-1 min-w-[260px]">
//                 <div className="relative flex-1">
//                   <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
//                   <input
//                     ref={folioSearchInputRef}
//                     type="text"
//                     placeholder="Search room, booking, guest, invoice, order id..."
//                     defaultValue=""
//                     onKeyDown={(e) => {
//                       if (e.key === 'Enter') {
//                         setFolioSearchQuery(e.currentTarget.value);
//                       }
//                     }}
//                     className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-black focus:ring-1 focus:ring-black"
//                   />
//                 </div>
//                 <button
//                   onClick={() => {
//                     const val = folioSearchInputRef.current?.value || '';
//                     setFolioSearchQuery(val);
//                   }}
//                   className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium flex items-center gap-1.5 shrink-0"
//                 >
//                   <FaSearch size={12} /> Search
//                 </button>
//               </div>

//               <div className="relative">
//                 <select
//                   value={folioFilterDate}
//                   onChange={(e) => setFolioFilterDate(e.target.value)}
//                   className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm appearance-none pr-8 focus:border-black focus:ring-1 focus:ring-black"
//                 >
//                   <option value="all">📅 All Time</option>
//                   <option value="today">📆 Today</option>
//                   <option value="yesterday">📆 Yesterday</option>
//                   <option value="weekly">📅 This Week</option>
//                   <option value="monthly">📅 This Month</option>
//                   <option value="lastMonth">📅 Last Month</option>
//                   <option value="custom">🗓️ Custom Range</option>
//                 </select>
//                 <FaCalendarAlt
//                   className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
//                   size={12}
//                 />
//               </div>

//               {folioFilterDate === 'custom' && (
//                 <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
//                   <label className="text-xs text-gray-500">From</label>
//                   <input
//                     type="date"
//                     value={folioCustomFrom}
//                     onChange={(e) => setFolioCustomFrom(e.target.value)}
//                     className="bg-transparent text-sm focus:outline-none"
//                   />
//                   <span className="text-gray-400">—</span>
//                   <label className="text-xs text-gray-500">To</label>
//                   <input
//                     type="date"
//                     value={folioCustomTo}
//                     onChange={(e) => setFolioCustomTo(e.target.value)}
//                     className="bg-transparent text-sm focus:outline-none"
//                   />
//                 </div>
//               )}

//               <button
//                 onClick={() => {
//                   if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
//                   setFolioSearchQuery('');
//                   setFolioFilterDate('today');
//                   setFolioCustomFrom('');
//                   setFolioCustomTo('');
//                 }}
//                 className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700"
//               >
//                 Clear
//               </button>

//               <button
//                 onClick={fetchAllOrders}
//                 className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm"
//               >
//                 <FaSync className={loading ? 'animate-spin' : ''} />
//               </button>
//             </div>

//             <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
//               <div className="bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
//                 <p className="text-[10px] uppercase text-rose-600 font-semibold">Rooms</p>
//                 <p className="text-lg font-bold text-rose-900">{folioSummary.rooms}</p>
//               </div>
//               <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
//                 <p className="text-[10px] uppercase text-blue-600 font-semibold">Total Orders</p>
//                 <p className="text-lg font-bold text-blue-900">{folioSummary.ordersCount}</p>
//               </div>
//               <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
//                 <p className="text-[10px] uppercase text-emerald-600 font-semibold">Total Amount</p>
//                 <p className="text-lg font-bold text-emerald-900">
//                   ₹{folioSummary.total.toFixed(2)}
//                 </p>
//               </div>
//               <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
//                 <p className="text-[10px] uppercase text-amber-600 font-semibold">Total Invoices</p>
//                 <p className="text-lg font-bold text-amber-900">{folioSummary.invoicesCount}</p>
//               </div>
//             </div>
//           </div>

//           <div className="flex-1 overflow-y-auto p-4">
//             {loading ? (
//               <div className="space-y-3">
//                 {[...Array(3)].map((_, i) => (
//                   <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
//                     <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
//                     <div className="h-3 bg-gray-200 rounded w-1/4"></div>
//                   </div>
//                 ))}
//               </div>
//             ) : roomFolios.length === 0 ? (
//               <div className="text-center py-16 bg-white rounded-xl">
//                 <FaHotel className="text-5xl text-gray-300 mx-auto mb-4" />
//                 <h3 className="text-lg font-semibold text-gray-700 mb-1">No Room Folios Found</h3>
//                 <p className="text-sm text-gray-400">No room orders match the selected filters</p>
//               </div>
//             ) : (
//               <div className="space-y-3">
//                 {roomFolios.map((folio) => {
//                   const isOpen = expandedFolioKey === folio.key;

//                   return (
//                     <div
//                       key={folio.key}
//                       className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
//                     >
//                       <div
//                         onClick={() => toggleExpand(folio.key)}
//                         className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-all"
//                       >
//                         <div className="flex items-center gap-4 flex-1 min-w-0">
//                           <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500 to-rose-700 text-white flex flex-col items-center justify-center shrink-0 shadow-sm">
//                             <FaHotel size={12} />
//                             <span className="text-sm font-bold leading-tight">
//                               {folio.room_number || '—'}
//                             </span>
//                           </div>

//                           <div className="min-w-0 flex-1">
//                             <div className="flex items-center gap-2 flex-wrap">
//                               <span className="font-bold text-gray-900">
//                                 Room {folio.room_number || 'N/A'}
//                               </span>
//                               {folio.booking_id && (
//                                 <span className="text-[11px] text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
//                                   {folio.booking_id}
//                                 </span>
//                               )}
//                             </div>

//                             <div className="text-sm text-gray-600 truncate mt-0.5">
//                               {folio.customer_name}
//                               {folio.customer_phone && (
//                                 <span className="text-gray-400"> · {folio.customer_phone}</span>
//                               )}
//                             </div>

//                             {folio.invoice_label && (
//                               <div className="mt-1 flex items-center gap-1 text-[11px]">
//                                 <FaFileInvoice className="text-gray-400" size={10} />
//                                 <span className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
//                                   {folio.invoice_label}
//                                 </span>
//                                 <span className="text-gray-400">
//                                   ({folio.invoice_numbers.length} invoice
//                                   {folio.invoice_numbers.length !== 1 ? 's' : ''})
//                                 </span>
//                               </div>
//                             )}
//                           </div>
//                         </div>

//                         <div className="flex items-center gap-5 shrink-0">
//                           <div className="text-right hidden sm:block">
//                             <div className="text-xs text-gray-500">
//                               {folio.order_count} order{folio.order_count !== 1 ? 's' : ''}
//                             </div>
//                             <div className="text-[11px] text-gray-400">
//                               {formatOrderDate(folio.earliest_time).time} —{' '}
//                               {formatOrderDate(folio.latest_time).time}
//                             </div>
//                           </div>

//                           <div className="text-right">
//                             <div className="text-lg font-bold text-gray-900">
//                               ₹{folio.total_amount.toFixed(2)}
//                             </div>
//                           </div>

//                           <div
//                             className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform ${
//                               isOpen
//                                 ? 'bg-black text-white rotate-180'
//                                 : 'bg-gray-100 text-gray-600'
//                             }`}
//                           >
//                             <FaChevronDown size={12} />
//                           </div>
//                         </div>
//                       </div>

//                       {isOpen && (
//                         <div className="border-t border-gray-100 bg-gray-50/70">
//                           <div className="overflow-x-auto">
//                             <table className="w-full">
//                               <thead className="bg-gray-100">
//                                 <tr>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     #
//                                   </th>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     Time
//                                   </th>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     Order ID
//                                   </th>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     Invoice No.
//                                   </th>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     Items
//                                   </th>
//                                   <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase">
//                                     Amount
//                                   </th>
//                                   <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-600 uppercase">
//                                     Status
//                                   </th>
//                                   <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-600 uppercase">
//                                     Actions
//                                   </th>
//                                 </tr>
//                               </thead>
//                               <tbody className="bg-white">
//                                 {folio.orders.map((o, idx) => {
//                                   const invNo =
//                                     o.invoice_number ||
//                                     o.bill_number ||
//                                     o.billing?.invoice_number ||
//                                     o.billing?.bill_number ||
//                                     o.billings?.[0]?.invoice_number ||
//                                     o.billings?.[0]?.bill_number ||
//                                     '—';
//                                   return (
//                                     <tr key={o.order_id} className="border-t border-gray-100">
//                                       <td className="px-3 py-2 text-xs text-gray-500">{idx + 1}</td>
//                                       <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
//                                         {formatOrderDate(o.ordered_at).time}
//                                       </td>
//                                       <td className="px-3 py-2 text-xs font-mono text-gray-600">
//                                         {String(o.order_id).slice(0, 14)}…
//                                       </td>
//                                       <td className="px-3 py-2 text-xs font-mono text-gray-700">
//                                         {invNo !== '—' ? (
//                                           <span className="bg-gray-100 px-1.5 py-0.5 rounded">
//                                             {invNo}
//                                           </span>
//                                         ) : (
//                                           <span className="text-gray-400">Not issued</span>
//                                         )}
//                                       </td>
//                                       <td className="px-3 py-2 text-xs text-gray-600">
//                                         {o.items?.length || 0} item
//                                         {o.items?.length !== 1 ? 's' : ''}
//                                       </td>
//                                       <td className="px-3 py-2 text-xs font-semibold text-right text-gray-900">
//                                         ₹{(parseFloat(o.total_amount) || 0).toFixed(2)}
//                                       </td>
//                                       <td className="px-3 py-2 text-center">{getStatusBadge(o)}</td>
//                                       <td className="px-3 py-2 text-center">
//                                         <div className="flex items-center justify-center gap-1">
//                                           <button
//                                             onClick={(e) => {
//                                               e.stopPropagation();
//                                               handleDownloadPDF(o);
//                                             }}
//                                             className="p-1.5 rounded hover:bg-purple-50 text-purple-600"
//                                             title="Download Invoice"
//                                           >
//                                             <FaDownload size={13} />
//                                           </button>
//                                           <button
//                                             onClick={(e) => {
//                                               e.stopPropagation();
//                                               handlePrintInvoice(o);
//                                             }}
//                                             className="p-1.5 rounded hover:bg-amber-50 text-amber-600"
//                                             title="Print Invoice"
//                                           >
//                                             <FaPrint size={13} />
//                                           </button>
//                                         </div>
//                                       </td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                               <tfoot className="bg-gray-100 border-t-2 border-gray-300">
//                                 <tr>
//                                   <td
//                                     colSpan={5}
//                                     className="px-3 py-3 text-right text-sm font-bold text-gray-800"
//                                   >
//                                     Consolidated Total ({folio.order_count} orders)
//                                   </td>
//                                   <td className="px-3 py-3 text-right text-base font-bold text-gray-900">
//                                     ₹{folio.total_amount.toFixed(2)}
//                                   </td>
//                                   <td colSpan={2}></td>
//                                 </tr>
//                               </tfoot>
//                             </table>
//                           </div>

//                           <div className="flex flex-wrap gap-2 p-3 border-t border-gray-200 bg-white">
//                             <button
//                               onClick={async (e) => {
//                                 e.stopPropagation();
//                                 try {
//                                   const range = getConsolidatedDateRange();
//                                   const params = new URLSearchParams({
//                                     booking_id: folio.booking_id,
//                                     date_filter: mapDateFilterForApi(range.filter),
//                                   });
//                                   if (folio.room_number)
//                                     params.append('room_number', folio.room_number);

//                                   if (range.filter === 'custom') {
//                                     if (range.from)
//                                       params.append('date_from', `${range.from}T00:00:00`);
//                                     if (range.to) params.append('date_to', `${range.to}T23:59:59`);
//                                   }

//                                   const url = `${BQ_BASE_URL}/bq/api/food-invoice/consolidated/pdf?${params.toString()}`;
//                                   const res = await fetch(url, { headers: getAuthHeaders() });
//                                   if (!res.ok) {
//                                     const err = await res.json().catch(() => ({}));
//                                     throw new Error(
//                                       err.detail || 'Failed to generate consolidated PDF'
//                                     );
//                                   }
//                                   const blob = await res.blob();
//                                   const a = document.createElement('a');
//                                   a.href = window.URL.createObjectURL(blob);
//                                   a.download = `Consolidated_Invoice_Room${folio.room_number || ''}_${folio.booking_id}_${folioFilterDate}.pdf`;
//                                   document.body.appendChild(a);
//                                   a.click();
//                                   window.URL.revokeObjectURL(a.href);
//                                   document.body.removeChild(a);
//                                   toast.success('Consolidated invoice downloaded');
//                                 } catch (err) {
//                                   console.error(err);
//                                   toast.error(err.message);
//                                 }
//                               }}
//                               className="px-3 py-2 bg-black text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1.5"
//                             >
//                               <FaFileInvoice size={12} /> Download Consolidated Invoice
//                             </button>

//                             <button
//                               onClick={async (e) => {
//                                 e.stopPropagation();
//                                 try {
//                                   const range = getConsolidatedDateRange();
//                                   const params = new URLSearchParams({
//                                     booking_id: folio.booking_id,
//                                     date_filter: mapDateFilterForApi(range.filter),
//                                   });
//                                   if (folio.room_number)
//                                     params.append('room_number', folio.room_number);

//                                   if (range.filter === 'custom') {
//                                     if (range.from)
//                                       params.append('date_from', `${range.from}T00:00:00`);
//                                     if (range.to) params.append('date_to', `${range.to}T23:59:59`);
//                                   }

//                                   const url = `${BQ_BASE_URL}/bq/api/food/print/consolidated/print?${params.toString()}`;
//                                   const res = await fetch(url, {
//                                     method: 'POST',
//                                     headers: getAuthHeaders(),
//                                   });
//                                   const data = await res.json();

//                                   if (res.ok && data.queued) {
//                                     toast.success(
//                                       `Consolidated slip sent to printer (${data.bills_included?.length || 0} bills)`
//                                     );
//                                   } else {
//                                     toast.error(data.detail || 'Print failed');
//                                   }
//                                 } catch (err) {
//                                   console.error(err);
//                                   toast.error('Failed to print consolidated invoice');
//                                 }
//                               }}
//                               className="px-3 py-2 border border-gray-200 rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1.5"
//                             >
//                               <FaPrint size={12} /> Print Consolidated
//                             </button>
//                           </div>
//                         </div>
//                       )}
//                     </div>
//                   );
//                 })}
//               </div>
//             )}
//           </div>

//           <div className="bg-white border-t border-gray-200 px-6 py-3 flex justify-between items-center shrink-0">
//             <p className="text-xs text-gray-500">
//               Showing <span className="font-semibold">{roomFolios.length}</span> room folio
//               {roomFolios.length !== 1 ? 's' : ''}
//             </p>
//             <button
//               onClick={() => {
//                 setShowRoomFolioModal(false);
//                 setExpandedFolioKey(null);
//                 if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
//                 setFolioSearchQuery('');
//               }}
//               className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   // ============================================================
//   // ============ AUDIT LOG MODAL COMPONENT =====================
//   // ============================================================
//   const AuditLogModal = () => {
//     if (!showAuditModal) return null;

//     return (
//       <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//         <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
//           <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shrink-0">
//             <div className="flex items-center gap-3">
//               <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg text-white">
//                 <FaExchangeAlt />
//               </div>
//               <div>
//                 <h2 className="text-xl font-bold text-gray-900">Audit Log</h2>
//                 <p className="text-xs text-gray-500">
//                   Who did what, when, and why — for accountability
//                 </p>
//               </div>
//             </div>
//             <button
//               onClick={() => setShowAuditModal(false)}
//               className="p-2 hover:bg-gray-100 rounded-lg"
//             >
//               <FaTimes className="w-5 h-5 text-gray-600" />
//             </button>
//           </div>

//           <div className="bg-white border-b border-gray-200 px-6 py-3 shrink-0 flex flex-wrap gap-3 items-center">
//             <select
//               value={auditFilterEventType}
//               onChange={(e) => {
//                 setAuditFilterEventType(e.target.value);
//                 fetchAuditLogs(e.target.value);
//               }}
//               className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-purple-500"
//             >
//               <option value="all">All Events</option>
//               <option value="room_order_transferred">Room Transfers</option>
//               <option value="inhouse_order_created">In-House Orders</option>
//               <option value="parcel_orders_merged">Parcel Merges</option>
//             </select>
//             <button
//               onClick={() => fetchAuditLogs(auditFilterEventType)}
//               className="px-4 py-2 bg-black text-white rounded-lg text-sm flex items-center gap-2"
//             >
//               <FaSync className={isLoadingAudit ? 'animate-spin' : ''} />
//               Refresh
//             </button>
//             <span className="text-xs text-gray-500 ml-auto">
//               Showing {auditLogs.length} entries
//             </span>
//           </div>

//           <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
//             {isLoadingAudit ? (
//               <div className="space-y-3">
//                 {[...Array(4)].map((_, i) => (
//                   <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
//                     <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
//                     <div className="h-3 bg-gray-200 rounded w-1/2"></div>
//                   </div>
//                 ))}
//               </div>
//             ) : auditLogs.length === 0 ? (
//               <div className="text-center py-16 bg-white rounded-xl">
//                 <FaExchangeAlt className="text-5xl text-gray-300 mx-auto mb-4" />
//                 <h3 className="text-lg font-semibold text-gray-700 mb-1">No Audit Entries</h3>
//                 <p className="text-sm text-gray-400">No events match the selected filter</p>
//               </div>
//             ) : (
//               <div className="space-y-3">
//                 {auditLogs.map((log) => (
//                   <div
//                     key={log.id}
//                     className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow"
//                   >
//                     <div className="flex justify-between items-start gap-4">
//                       <div className="flex-1 min-w-0">
//                         <div className="flex items-center gap-2 flex-wrap mb-1">
//                           <span className="font-semibold text-gray-900">
//                             {log.action || log.event_type || 'Unknown action'}
//                           </span>
//                           {log.is_transfer && (
//                             <span className="text-[10px] uppercase bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">
//                               Transfer
//                             </span>
//                           )}
//                           {log.is_in_house && (
//                             <span className="text-[10px] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
//                               In-House
//                             </span>
//                           )}
//                           {log.is_parcel_merge && (
//                             <span className="text-[10px] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
//                               Parcel Merge
//                             </span>
//                           )}
//                         </div>

//                         <div className="text-xs text-gray-500 space-y-0.5">
//                           {log.from_room && log.to_room && (
//                             <div>
//                               <span className="text-gray-400">Route:</span>{' '}
//                               <span className="font-mono font-semibold text-gray-700">
//                                 Room {log.from_room} → Room {log.to_room}
//                               </span>
//                             </div>
//                           )}
//                           {log.from_parcel_number && log.to_parcel_number && (
//                             <div>
//                               <span className="text-gray-400">Parcels:</span>{' '}
//                               <span className="font-mono font-semibold text-gray-700">
//                                 {log.from_parcel_number} → {log.to_parcel_number}
//                               </span>
//                             </div>
//                           )}
//                           {log.source_amount > 0 && log.target_amount_after_merge > 0 && (
//                             <div>
//                               <span className="text-gray-400">Amounts:</span>{' '}
//                               <span className="font-semibold text-gray-700">
//                                 ₹{log.source_amount.toFixed(2)} → ₹
//                                 {log.target_amount_after_merge.toFixed(2)}
//                               </span>
//                             </div>
//                           )}
//                           {log.invoice_number && (
//                             <div>
//                               <span className="text-gray-400">Bill:</span>{' '}
//                               <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
//                                 {log.invoice_number}
//                               </span>
//                             </div>
//                           )}
//                           {log.reason && (
//                             <div>
//                               <span className="text-gray-400">Reason:</span> {log.reason}
//                             </div>
//                           )}
//                           {log.comment && <div className="italic">{log.comment}</div>}
//                         </div>
//                       </div>

//                       <div className="text-right shrink-0">
//                         <div className="text-xs text-gray-500 mb-1">{log.occurred_at}</div>
//                         {log.created_by_user ? (
//                           <div className="flex items-center gap-1.5 justify-end">
//                             <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[10px] font-bold">
//                               {(log.created_by_user.full_name || '?').charAt(0).toUpperCase()}
//                             </div>
//                             <div className="text-right">
//                               <div className="text-xs font-semibold text-gray-700">
//                                 {log.created_by_user.full_name || 'Unknown'}
//                               </div>
//                               <div className="text-[10px] text-gray-400">
//                                 {log.created_by_user.role || ''}
//                               </div>
//                             </div>
//                           </div>
//                         ) : (
//                           <div className="text-xs text-gray-400 italic">System</div>
//                         )}
//                         {log.amount > 0 && (
//                           <div className="text-sm font-bold text-gray-900 mt-1">
//                             ₹{log.amount.toFixed(2)}
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           <div className="bg-white border-t border-gray-200 px-6 py-3 flex justify-end shrink-0">
//             <button
//               onClick={() => setShowAuditModal(false)}
//               className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <style>{`
//         .action-btn {
//           padding: 6px;
//           border-radius: 6px;
//           transition: all 0.2s;
//           display: inline-flex;
//           align-items: center;
//           justify-content: center;
//           background: transparent;
//           border: none;
//           cursor: pointer;
//           position: relative;
//         }
//         .action-btn:hover { background: #f3f4f6; transform: scale(1.1); }
//         .action-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
//         .action-btn.view-btn { color: #3b82f6; }
//         .action-btn.download-btn { color: #8b5cf6; }
//         .action-btn.print-btn { color: #f59e0b; }
//         .action-btn.kot-btn { color: #8B5CF6; }
//         .action-btn.email-btn { color: #ef4444; }
//         .action-btn.payment-btn { color: #10b981; }
//         .action-btn.edit-btn { color: #6b7280; }
//         .action-btn.transfer-btn { color: #8B5CF6; }
//         .action-btn.merge-btn { color: #EC4899; }
//       `}</style>
//       <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
//         <header className="flex justify-between items-center mb-3 bg-white p-3">
//           <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
//           <div className="flex space-x-6">
//             <button
//               onClick={() =>
//                 navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')
//               }
//               className="btn"
//             >
//               Order Overview
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
//               className="btn"
//             >
//               Order Food
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
//               className="btn"
//             >
//               Dining Tables
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
//               className="btn"
//             >
//               Kitchen Display System(KDS)
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
//               className="btn font-bold text-black"
//             >
//               Billing & Invoicing
//             </button>
//           </div>
//         </header>

//         {/* Stats */}
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Total Revenue</p>
//             <p className="text-2xl font-bold text-gray-900">
//               ₹{filteredOrders.reduce((s, o) => s + (o.total_amount || 0), 0).toFixed(2)}
//             </p>
//           </div>
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Total Orders</p>
//             <p className="text-2xl font-bold text-gray-900">{filteredOrders.length}</p>
//           </div>
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Pending</p>
//             <p className="text-2xl font-bold text-amber-600">
//               {
//                 filteredOrders.filter(
//                   (o) =>
//                     !o.is_cancelled &&
//                     !o.is_paid &&
//                     (o.order_status === 'pending' || o.order_status === 'Pending')
//                 ).length
//               }
//             </p>
//           </div>
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Served</p>
//             <p className="text-2xl font-bold text-blue-600">
//               {
//                 filteredOrders.filter(
//                   (o) =>
//                     !o.is_cancelled && (o.order_status === 'served' || o.order_status === 'Served')
//                 ).length
//               }
//             </p>
//           </div>
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Paid</p>
//             <p className="text-2xl font-bold text-green-600">
//               {filteredOrders.filter((o) => o.is_paid).length}
//             </p>
//           </div>
//         </div>

//         {/* Filters */}
//         <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
//           <div className="flex flex-col sm:flex-row gap-4">
//             <div className="flex-1 relative">
//               <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
//               <input
//                 type="text"
//                 placeholder="Search by Serial No, Order ID, Invoice No, Customer, Table, Room, Parcel, KOT, Booking..."
//                 value={searchQuery}
//                 onChange={(e) => setSearchQuery(e.target.value)}
//                 className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black"
//               />
//             </div>
//             <div className="flex flex-wrap gap-3 items-center">
//               <button
//                 onClick={() => {
//                   setShowRoomFolioModal(true);
//                   setExpandedFolioKey(null);
//                   setFolioSearchQuery('');
//                 }}
//                 className="px-4 py-2 bg-gradient-to-r from-rose-600 to-rose-700 text-white rounded-lg hover:from-rose-700 hover:to-rose-800 transition-all flex items-center gap-2 text-sm font-semibold shadow-sm"
//                 title="Open consolidated room billing"
//               >
//                 <FaLayerGroup />
//                 Room Folio
//                 <span className="bg-white/25 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
//                   {orders.filter((o) => o.order_type === 'room').length}
//                 </span>
//               </button>

//               {/* <button
//                 onClick={() => {
//                   setShowAuditModal(true);
//                   fetchAuditLogs('all');
//                 }}
//                 className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all flex items-center gap-2 text-sm font-semibold shadow-sm"
//                 title="View audit log"
//               >
//                 <FaExchangeAlt />
//                 Audit Log
//               </button> */}

//               <div className="relative">
//                 <select
//                   value={filterDate}
//                   onChange={(e) => setFilterDate(e.target.value)}
//                   className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm min-w-[150px]"
//                 >
//                   <option value="all">📅 All Time</option>
//                   <option value="today">📆 Today</option>
//                   <option value="yesterday">📆 Yesterday</option>
//                   <option value="weekly">📅 This Week</option>
//                   <option value="monthly">📅 This Month</option>
//                   <option value="lastMonth">📅 Last Month</option>
//                   <option value="custom">🗓️ Custom Range</option>
//                 </select>
//                 <FaCalendarAlt
//                   className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
//                   size={12}
//                 />
//               </div>

//               {filterDate === 'custom' && (
//                 <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
//                   <label className="text-xs text-gray-500 font-medium">From</label>
//                   <input
//                     type="date"
//                     value={customDateFrom}
//                     onChange={(e) => setCustomDateFrom(e.target.value)}
//                     className="bg-transparent text-sm focus:outline-none"
//                   />
//                   <span className="text-gray-400">—</span>
//                   <label className="text-xs text-gray-500 font-medium">To</label>
//                   <input
//                     type="date"
//                     value={customDateTo}
//                     onChange={(e) => setCustomDateTo(e.target.value)}
//                     className="bg-transparent text-sm focus:outline-none"
//                   />
//                 </div>
//               )}

//               <select
//                 value={filterType}
//                 onChange={(e) => setFilterType(e.target.value)}
//                 className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm"
//               >
//                 <option value="all">All Types</option>
//                 <option value="dining">Dining</option>
//                 <option value="parcel">Parcel</option>
//                 <option value="room">Room</option>
//               </select>

//               <select
//                 value={filterStatus}
//                 onChange={(e) => setFilterStatus(e.target.value)}
//                 className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm"
//               >
//                 <option value="all">All Status</option>
//                 <option value="pending">Pending</option>
//                 <option value="served">Served</option>
//                 <option value="unpaid">Unpaid</option>
//                 <option value="paid">Paid</option>
//                 <option value="cancelled">Cancelled</option>
//               </select>

//               <button
//                 onClick={() => {
//                   setSearchQuery('');
//                   setFilterType('all');
//                   setFilterStatus('all');
//                   setFilterDate('today');
//                   setCustomDateFrom('');
//                   setCustomDateTo('');
//                 }}
//                 className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700"
//               >
//                 Clear
//               </button>

//               <button
//                 onClick={fetchAllOrders}
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm"
//               >
//                 <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Table */}
//         {loading ? (
//           <LoadingSkeleton />
//         ) : filteredOrders.length === 0 ? (
//           <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
//             <FaFileInvoice className="text-5xl text-gray-300 mx-auto mb-4" />
//             <h3 className="text-xl font-semibold text-gray-700 mb-2">No Orders Found</h3>
//             <p className="text-gray-400">No orders match your current filters</p>
//           </div>
//         ) : (
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
//             <table className="min-w-full divide-y divide-gray-200">
//               <thead className="bg-gray-50">
//                 <tr>
//                   {['S.No', 'OrderID / Invoice No.', 'Customer', 'Type', 'No.', 'Date / Time'].map(
//                     (h) => (
//                       <th
//                         key={h}
//                         className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
//                       >
//                         {h}
//                       </th>
//                     )
//                   )}
//                   <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
//                     Total
//                   </th>
//                   <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
//                     Payment
//                   </th>
//                   <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
//                     Status
//                   </th>
//                   <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
//                     Actions
//                   </th>
//                 </tr>
//               </thead>
//               <tbody className="bg-white">
//                 {getCurrentPageGroups().map(({ dateKey, orders: dateOrders }) => (
//                   <React.Fragment key={dateKey}>
//                     <tr className="bg-gray-100 border-y border-gray-200">
//                       <td colSpan={10} className="px-4 py-2">
//                         <div className="flex items-center justify-between">
//                           <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
//                             <FaCalendarAlt className="text-gray-500" size={12} />
//                             {getDateHeaderLabel(dateKey)}
//                           </span>
//                           <span className="text-xs text-gray-500">
//                             {dateOrders.length} order{dateOrders.length !== 1 ? 's' : ''}
//                           </span>
//                         </div>
//                       </td>
//                     </tr>
//                     {dateOrders.map((order) => {
//                       const invNo = getOrderInvoiceNumber(order);
//                       return (
//                         <tr
//                           key={order.order_id}
//                           className={`hover:bg-gray-50 transition-colors border-b border-gray-100 ${
//                             order.is_cancelled ? 'bg-red-50/40 opacity-80' : ''
//                           }`}
//                         >
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             <span className="text-sm font-bold text-gray-900">
//                               {order.serial_number || '—'}
//                             </span>
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             <div className="mb-1">
//                               {invNo ? (
//                                 <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
//                                   {invNo}
//                                 </span>
//                               ) : (
//                                 <span className="text-[11px] text-gray-400 italic">
//                                   Invoice not issued
//                                 </span>
//                               )}
//                             </div>
//                             <div className="text-xs font-mono text-gray-600">
//                               {String(order.order_id).slice(0, 20)}
//                             </div>
//                             {order.booking_id && (
//                               <div className="text-xs text-gray-400 mt-0.5">
//                                 Booking: {order.booking_id}
//                               </div>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 align-top">
//                             <div className="text-sm font-medium text-gray-900">
//                               {order.customer_name || 'Guest'}
//                             </div>
//                             {order.customer_phone && (
//                               <div className="text-xs text-gray-500">{order.customer_phone}</div>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             {getOrderTypeBadge(order.order_type)}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             {order.table_number ? (
//                               <span className="text-sm text-gray-700">
//                                 Table {order.table_number}
//                               </span>
//                             ) : order.room_number ? (
//                               <span className="text-sm text-gray-700">
//                                 Room {order.room_number}
//                               </span>
//                             ) : order.parcel_number ? (
//                               <span className="text-sm text-gray-700 font-mono">
//                                 {order.parcel_number}
//                               </span>
//                             ) : (
//                               <span className="text-sm text-gray-400">—</span>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             {order.ordered_at ? (
//                               <>
//                                 <div className="text-sm text-gray-700">
//                                   {formatOrderDate(order.ordered_at).date}
//                                 </div>
//                                 <div className="text-xs font-semibold text-gray-500">
//                                   {formatOrderDate(order.ordered_at).time}
//                                 </div>
//                               </>
//                             ) : (
//                               <span className="text-sm text-gray-400">—</span>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap text-right align-top">
//                             <div className="text-sm font-bold text-gray-900">
//                               ₹{order.total_amount?.toFixed(2) || 0}
//                             </div>
//                             {order.items && (
//                               <div className="text-xs text-gray-400">
//                                 {order.items.length} items
//                               </div>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap text-center align-top">
//                             {getPaymentMethodBadge(order.payment_method)}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap text-center align-top">
//                             {getStatusBadge(order)}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap text-center align-top">
//                             <div className="flex items-center justify-center gap-1 flex-wrap">
//                               <button
//                                 onClick={() => handleViewInvoice(order)}
//                                 disabled={
//                                   isLoadingInvoice && selectedOrder?.order_id === order.order_id
//                                 }
//                                 className="action-btn view-btn"
//                                 title="View Invoice"
//                               >
//                                 {isLoadingInvoice && selectedOrder?.order_id === order.order_id ? (
//                                   <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
//                                 ) : (
//                                   <FaEye size={16} />
//                                 )}
//                               </button>

//                               <button
//                                 onClick={() => handlePrintKOT(order)}
//                                 disabled={isPrintingKOT}
//                                 className="action-btn kot-btn"
//                                 title="Print KOT"
//                               >
//                                 {isPrintingKOT ? (
//                                   <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
//                                 ) : (
//                                   <FaUtensils size={16} />
//                                 )}
//                               </button>

//                               {order.order_type !== 'room' && (
//                                 <button
//                                   onClick={() => handleOpenEditGuest(order)}
//                                   className="action-btn edit-btn"
//                                   title="Edit Guest"
//                                 >
//                                   <FaEdit size={16} />
//                                 </button>
//                               )}

//                               {(order.order_type === 'dining' ||
//                                 order.order_type === 'parcel' ||
//                                 order.order_type === 'room') && (
//                                 <button
//                                   onClick={() => {
//                                     setTransferOrder(order);
//                                     setTransferActionType(null);
//                                     setTransferRoomNumber('');
//                                     setTransferBookingId('');
//                                     setTransferNewTable('');
//                                     setTransferMergeFrom('');
//                                     setTransferMergeTo('');
//                                     setTransferToRoomNumber('');
//                                     setTransferToRoomReason('');
//                                     setParcelMergeFrom('');
//                                     setParcelMergeTo('');
//                                     setShowTransferModal(true);
//                                   }}
//                                   className="action-btn transfer-btn"
//                                   title={
//                                     order.order_type === 'room'
//                                       ? 'Transfer to Another Room'
//                                       : order.order_type === 'parcel'
//                                         ? 'Transfer / Merge Parcel'
//                                         : 'Transfer'
//                                   }
//                                 >
//                                   <FaExchangeAlt size={16} />
//                                 </button>
//                               )}

//                               <button
//                                 onClick={() => handleDownloadPDF(order)}
//                                 className="action-btn download-btn"
//                                 title="Download PDF"
//                               >
//                                 <FaDownload size={16} />
//                               </button>

//                               <button
//                                 onClick={() => handlePrintInvoice(order)}
//                                 disabled={isPrinting}
//                                 className="action-btn print-btn"
//                                 title="Print Invoice"
//                               >
//                                 <FaPrint size={16} />
//                               </button>

//                               <button
//                                 onClick={() => {
//                                   setSelectedOrder(order);
//                                   setShowEmailModal(true);
//                                 }}
//                                 className="action-btn email-btn"
//                                 title="Email"
//                               >
//                                 <FaEnvelope size={16} />
//                               </button>

//                               {!order.is_paid &&
//                                 order.has_billing !== false &&
//                                 !order.is_cancelled && (
//                                   <button
//                                     onClick={() => {
//                                       setSelectedOrder(order);
//                                       setSelectedPaymentMethod(null);
//                                       setShowPaymentModal(true);
//                                     }}
//                                     className="action-btn payment-btn"
//                                     title="Make Payment"
//                                   >
//                                     <FaCreditCard size={16} />
//                                   </button>
//                                 )}
//                             </div>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </React.Fragment>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {!loading && filteredOrders.length > 0 && <Pagination />}

//         {/* ========== ROOM FOLIO POPUP ========== */}
//         <RoomFolioModal />

//         {/* ========== AUDIT LOG MODAL ========== */}
//         <AuditLogModal />

//         {/* ========== EDIT GUEST ========== */}
//         {showEditGuestModal && selectedOrder && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
//               <div className="flex justify-between items-center mb-6">
//                 <h2 className="text-2xl font-bold flex items-center gap-3">
//                   <FaUser className="text-gray-700" /> Edit Guest Details
//                 </h2>
//                 <button
//                   onClick={() => {
//                     setShowEditGuestModal(false);
//                     setEditGuestData({
//                       customer_name: '',
//                       customer_phone: '',
//                       customer_email: '',
//                       gst_number: '',
//                     });
//                     setCompanySearchText('');
//                     setSelectedCompany(null);
//                     setShowCompanyDropdown(false);
//                   }}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>

//               <div className="space-y-4">
//                 <div className="relative" ref={companyDropdownRef}>
//                   <div className="relative">
//                     <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm z-10" />
//                     <input
//                       type="text"
//                       placeholder="Search company name..."
//                       value={companySearchText}
//                       onChange={(e) => {
//                         setCompanySearchText(e.target.value);
//                         setSelectedCompany(null);
//                         if (!e.target.value) setEditGuestData((p) => ({ ...p, gst_number: '' }));
//                       }}
//                       onFocus={() => {
//                         if (companySearchText.length > 1) setShowCompanyDropdown(true);
//                       }}
//                       className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                     />
//                   </div>
//                   {showCompanyDropdown && filteredCompanies.length > 0 && (
//                     <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
//                       {filteredCompanies.map((c) => (
//                         <button
//                           key={c.id}
//                           onClick={() => handleCompanySelect(c)}
//                           className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
//                         >
//                           <div className="flex justify-between items-center">
//                             <div>
//                               <div className="font-medium text-sm">{c.companyname}</div>
//                               {c.companyid && (
//                                 <div className="text-xs text-gray-400">ID: {c.companyid}</div>
//                               )}
//                             </div>
//                             {c.gstnumber && (
//                               <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
//                                 GST: {c.gstnumber}
//                               </span>
//                             )}
//                           </div>
//                         </button>
//                       ))}
//                     </div>
//                   )}
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Customer Name *
//                   </label>
//                   <input
//                     type="text"
//                     name="customer_name"
//                     value={editGuestData.customer_name}
//                     onChange={handleEditGuestChange}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Phone Number
//                   </label>
//                   <input
//                     type="tel"
//                     name="customer_phone"
//                     value={editGuestData.customer_phone}
//                     onChange={handleEditGuestChange}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Email Address
//                   </label>
//                   <input
//                     type="email"
//                     name="customer_email"
//                     value={editGuestData.customer_email}
//                     onChange={handleEditGuestChange}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
//                   <input
//                     type="text"
//                     name="gst_number"
//                     value={editGuestData.gst_number}
//                     onChange={handleEditGuestChange}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                   {selectedCompany && (
//                     <p className="text-xs text-green-600 mt-1">
//                       ✓ GST auto-filled from {selectedCompany.companyname}
//                     </p>
//                   )}
//                 </div>

//                 <div className="flex gap-3 pt-4">
//                   <button
//                     onClick={() => {
//                       setShowEditGuestModal(false);
//                       setEditGuestData({
//                         customer_name: '',
//                         customer_phone: '',
//                         customer_email: '',
//                         gst_number: '',
//                       });
//                       setCompanySearchText('');
//                       setSelectedCompany(null);
//                       setShowCompanyDropdown(false);
//                     }}
//                     className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={handleUpdateGuestDetails}
//                     disabled={isUpdatingGuest || !editGuestData.customer_name.trim()}
//                     className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                       isUpdatingGuest || !editGuestData.customer_name.trim()
//                         ? 'opacity-50 cursor-not-allowed'
//                         : ''
//                     }`}
//                   >
//                     {isUpdatingGuest ? (
//                       <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                     ) : (
//                       'Update Details'
//                     )}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ========== INVOICE MODAL ========== */}
//         {showInvoiceModal && invoiceData && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 overflow-y-auto">
//             <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
//               <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
//                 <h2 className="text-xl font-bold flex items-center gap-3">
//                   <div className="p-2 bg-black rounded-lg text-white">
//                     <FaFileInvoice />
//                   </div>
//                   Invoice Details{' '}
//                   {isRoomOrder && (
//                     <span className="text-sm font-normal text-gray-500">(Room Order)</span>
//                   )}
//                   {invoiceData.invoice_number && (
//                     <span className="text-sm font-normal text-gray-500 ml-2">
//                       #{invoiceData.invoice_number}
//                     </span>
//                   )}
//                 </h2>
//                 <button
//                   onClick={handleCloseInvoiceModal}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>
//               <div className="p-6">
//                 <div className="border-b border-gray-200 pb-4 mb-4">
//                   <div className="flex flex-col md:flex-row justify-between items-start gap-4">
//                     <div>
//                       <h3 className="text-2xl font-bold text-gray-900">
//                         {invoiceData.property_name || 'R&T INTERNATIONAL'}
//                       </h3>
//                       <p className="text-sm text-gray-600 mt-1">{invoiceData.property_address}</p>
//                       <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
//                         <span>GST: {invoiceData.property_gst}</span>
//                         <span>Contact: {invoiceData.property_contact}</span>
//                       </div>
//                     </div>
//                     <div className="text-right bg-gray-50 px-6 py-4 rounded-lg">
//                       <p className="text-2xl font-bold text-black">INVOICE</p>
//                       <p className="text-sm font-semibold text-gray-700">
//                         # {invoiceData.invoice_number}
//                       </p>
//                       <p className="text-sm text-gray-500">
//                         {new Date(invoiceData.invoice_date).toLocaleDateString('en-GB')}{' '}
//                         {new Date(invoiceData.invoice_date).toLocaleTimeString('en-US', {
//                           hour: '2-digit',
//                           minute: '2-digit',
//                           hour12: true,
//                         })}
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//                   <div className="bg-gray-50 rounded-lg p-4">
//                     <p className="text-sm font-semibold text-gray-700 mb-2">Customer Details</p>
//                     <div className="space-y-1 text-sm">
//                       <p className="text-gray-600">Name: {invoiceData.customer_name || 'Guest'}</p>
//                       <p className="text-gray-600">Phone: {invoiceData.customer_phone || '-'}</p>
//                       <p className="text-gray-600">Email: {invoiceData.customer_email || '-'}</p>
//                       <p className="text-gray-600">GST: {invoiceData.customer_gst || 'N/A'}</p>
//                     </div>
//                   </div>
//                   <div className="bg-gray-50 rounded-lg p-4">
//                     <p className="text-sm font-semibold text-gray-700 mb-2">Order Details</p>
//                     <div className="space-y-1 text-sm">
//                       <p className="text-gray-600 font-mono text-xs">ID: {invoiceData.order_id}</p>
//                       <p className="text-gray-600 capitalize">Type: {invoiceData.order_type}</p>
//                       {invoiceData.room_number && (
//                         <p className="text-gray-600">Room: {invoiceData.room_number}</p>
//                       )}
//                       {invoiceData.booking_id && (
//                         <p className="text-gray-600">Booking: {invoiceData.booking_id}</p>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//                 {invoiceData.items && invoiceData.items.length > 0 && (
//                   <div className="overflow-x-auto mb-4 border border-gray-200 rounded-lg">
//                     <table className="w-full">
//                       <thead className="bg-gray-50">
//                         <tr>
//                           <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">
//                             Item
//                           </th>
//                           <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">
//                             Qty
//                           </th>
//                           <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">
//                             Unit Price
//                           </th>
//                           <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">
//                             Amount
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {invoiceData.items.map((item, index) => {
//                           const unitPrice = parseFloat(item.unit_price) || 0;
//                           const totalAmount = parseFloat(item.total_amount) || 0;
//                           const quantity = parseInt(item.quantity) || 0;
//                           const isCancelled =
//                             item.is_cancelled === true ||
//                             item.status === 'cancelled' ||
//                             item.status === 'Cancelled';
//                           return (
//                             <tr
//                               key={index}
//                               className={`border-t border-gray-100 ${isCancelled ? 'bg-red-50/50' : ''}`}
//                             >
//                               <td className="py-2 px-3 text-sm">
//                                 <span className={isCancelled ? 'line-through text-red-500' : ''}>
//                                   {item.description || item.food_name || 'Item'}
//                                 </span>
//                               </td>
//                               <td
//                                 className={`text-center py-2 px-3 text-sm ${isCancelled ? 'line-through text-red-400' : ''}`}
//                               >
//                                 {quantity}
//                               </td>
//                               <td
//                                 className={`text-right py-2 px-3 text-sm ${isCancelled ? 'line-through text-red-400' : ''}`}
//                               >
//                                 ₹{unitPrice.toFixed(2)}
//                               </td>
//                               <td
//                                 className={`text-right py-2 px-3 text-sm font-semibold ${isCancelled ? 'line-through text-red-400' : ''}`}
//                               >
//                                 ₹{totalAmount.toFixed(2)}
//                               </td>
//                             </tr>
//                           );
//                         })}
//                       </tbody>
//                     </table>
//                   </div>
//                 )}
//                 <div className="border-t border-gray-200 pt-4">
//                   <div className="flex justify-end">
//                     <div className="w-64 space-y-1">
//                       <div className="flex justify-between py-1">
//                         <span className="text-sm text-gray-600">Subtotal</span>
//                         <span className="text-sm font-medium text-gray-900">
//                           ₹{(parseFloat(invoiceData.subtotal) || 0).toFixed(2)}
//                         </span>
//                       </div>
//                       <div className="flex justify-between py-1">
//                         <span className="text-sm text-gray-600">Tax</span>
//                         <span className="text-sm font-medium text-gray-900">
//                           ₹{(parseFloat(invoiceData.total_tax) || 0).toFixed(2)}
//                         </span>
//                       </div>
//                       <div className="border-t-2 border-gray-300 pt-2 mt-1">
//                         <div className="flex justify-between">
//                           <span className="text-lg font-bold text-gray-900">Grand Total</span>
//                           <span className="text-2xl font-bold text-black">
//                             ₹{(parseFloat(invoiceData.grand_total) || 0).toFixed(2)}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ========== PAYMENT MODAL ========== */}
//         {showPaymentModal && selectedOrder && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
//               <div className="flex justify-between items-center mb-6">
//                 <h2 className="text-2xl font-bold">Make Payment</h2>
//                 <button
//                   onClick={() => {
//                     setShowPaymentModal(false);
//                     setSelectedPaymentMethod(null);
//                   }}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>
//               <div className="space-y-6">
//                 <div className="bg-gray-50 p-4 rounded-lg">
//                   <p className="text-sm text-gray-600">Order ID</p>
//                   <p className="font-mono text-sm font-semibold">
//                     {String(selectedOrder.order_id).slice(0, 12)}...
//                   </p>
//                   {selectedOrder.room_number && (
//                     <p className="text-sm text-gray-600 mt-1">Room: {selectedOrder.room_number}</p>
//                   )}
//                   <p className="text-sm text-gray-600 mt-3">Total Amount</p>
//                   <p className="text-3xl font-bold text-black">
//                     ₹{selectedOrder.total_amount?.toFixed(2) || 0}
//                   </p>
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-3">
//                     Select Payment Method
//                   </label>
//                   <div className="grid grid-cols-2 gap-3">
//                     <button
//                       onClick={() => handlePaymentOption('cash')}
//                       className={`py-3 px-4 rounded-lg border-2 transition-all ${
//                         selectedPaymentMethod === 'cash'
//                           ? 'border-black bg-black text-white'
//                           : 'border-gray-200 hover:border-gray-400'
//                       }`}
//                     >
//                       <FaMoneyBill className="text-xl mx-auto mb-1" />
//                       <span className="text-sm">Cash</span>
//                     </button>
//                     <button
//                       onClick={() => handlePaymentOption('razorpay')}
//                       className={`py-3 px-4 rounded-lg border-2 transition-all ${
//                         selectedPaymentMethod === 'razorpay'
//                           ? 'border-black bg-black text-white'
//                           : 'border-gray-200 hover:border-gray-400'
//                       }`}
//                     >
//                       <FaDigitalOcean className="text-xl mx-auto mb-1" />
//                       <span className="text-sm">Digital</span>
//                     </button>
//                   </div>
//                 </div>
//                 <div className="flex gap-3 pt-4">
//                   <button
//                     onClick={() => {
//                       setShowPaymentModal(false);
//                       setSelectedPaymentMethod(null);
//                     }}
//                     className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={confirmPayment}
//                     disabled={!selectedPaymentMethod || isProcessingPayment}
//                     className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                       !selectedPaymentMethod || isProcessingPayment
//                         ? 'opacity-50 cursor-not-allowed'
//                         : ''
//                     }`}
//                   >
//                     {isProcessingPayment ? (
//                       <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                     ) : (
//                       'Pay Now'
//                     )}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ========== EMAIL MODAL ========== */}
//         {showEmailModal && selectedOrder && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
//               <div className="flex justify-between items-center mb-6">
//                 <h2 className="text-2xl font-bold">Send Invoice via Email</h2>
//                 <button
//                   onClick={() => {
//                     setShowEmailModal(false);
//                     setEmailRecipient('');
//                   }}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>
//               <div className="space-y-6">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-2">
//                     Recipient Email
//                   </label>
//                   <input
//                     type="email"
//                     placeholder="Enter email address"
//                     value={emailRecipient}
//                     onChange={(e) => setEmailRecipient(e.target.value)}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                 </div>
//                 <div className="flex gap-3 pt-4">
//                   <button
//                     onClick={() => {
//                       setShowEmailModal(false);
//                       setEmailRecipient('');
//                     }}
//                     className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={() => handleSendEmail(selectedOrder)}
//                     disabled={!emailRecipient || isSendingEmail}
//                     className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                       !emailRecipient || isSendingEmail ? 'opacity-50 cursor-not-allowed' : ''
//                     }`}
//                   >
//                     {isSendingEmail ? (
//                       <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                     ) : (
//                       'Send Email'
//                     )}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ========== TRANSFER MODAL ========== */}
//         {showTransferModal && transferOrder && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
//               <div className="flex justify-between items-center mb-4">
//                 <h2 className="text-xl font-bold flex items-center gap-2">
//                   <FaExchangeAlt className="text-purple-600" /> Transfer Options
//                 </h2>
//                 <button
//                   onClick={() => {
//                     setShowTransferModal(false);
//                     setTransferOrder(null);
//                     setTransferActionType(null);
//                     setTransferRoomNumber('');
//                     setTransferBookingId('');
//                     setTransferNewTable('');
//                     setTransferMergeFrom('');
//                     setTransferMergeTo('');
//                     setTransferToRoomNumber('');
//                     setTransferToRoomReason('');
//                     setParcelMergeFrom('');
//                     setParcelMergeTo('');
//                   }}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>

//               <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm">
//                 <p>
//                   <strong>Order ID:</strong> {String(transferOrder.order_id || '').slice(0, 20)}...
//                 </p>
//                 <p>
//                   <strong>Customer:</strong> {transferOrder.customer_name || 'Guest'}
//                 </p>
//                 <p>
//                   <strong>Amount:</strong> ₹{transferOrder.total_amount?.toFixed(2) || 0}
//                 </p>
//                 {transferOrder.table_number && (
//                   <p>
//                     <strong>Table:</strong> {transferOrder.table_number}
//                   </p>
//                 )}
//                 {transferOrder.room_number && (
//                   <p>
//                     <strong>Room:</strong> {transferOrder.room_number}
//                   </p>
//                 )}
//                 {transferOrder.parcel_number && (
//                   <p>
//                     <strong>Parcel:</strong> {transferOrder.parcel_number}
//                   </p>
//                 )}
//               </div>

//               {!transferActionType && (
//                 <div className="space-y-3">
//                   {/* ROOM → ANOTHER ROOM (only for room orders) */}
//                   {transferOrder.order_type === 'room' && (
//                     <button
//                       onClick={() => {
//                         setTransferActionType('room-to-room');
//                         setTransferToRoomNumber('');
//                         setTransferToRoomReason('');
//                       }}
//                       className="w-full py-3 px-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg flex items-center gap-3"
//                     >
//                       <FaExchangeAlt className="text-purple-600" size={20} />
//                       <div className="flex-1 text-left">
//                         <div className="font-medium">Transfer to Another Room</div>
//                         <div className="text-xs text-gray-500">
//                           Move this food bill to a different room
//                         </div>
//                       </div>
//                     </button>
//                   )}

//                   {/* TABLE → ROOM / PARCEL → ROOM (not for room orders) */}
//                   {transferOrder.order_type !== 'room' && (
//                     <button
//                       onClick={() => {
//                         setTransferActionType('to-room');
//                         setTransferRoomNumber('');
//                         setTransferBookingId('');
//                       }}
//                       className="w-full py-3 px-4 bg-pink-50 hover:bg-pink-100 border border-pink-200 rounded-lg flex items-center gap-3"
//                     >
//                       <FaHotel className="text-pink-600" size={20} />
//                       <span className="flex-1 text-left font-medium">Transfer to Room</span>
//                     </button>
//                   )}

//                   <button
//                     onClick={() => {
//                       setTransferActionType('to-table');
//                       setTransferNewTable('');
//                     }}
//                     className="w-full py-3 px-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg flex items-center gap-3"
//                   >
//                     <MdTableBar className="text-indigo-600" size={20} />
//                     <span className="flex-1 text-left font-medium">Transfer to Table</span>
//                   </button>

//                   {transferOrder.order_type === 'dining' && transferOrder.table_number && (
//                     <button
//                       onClick={() => {
//                         setTransferActionType('merge');
//                         setTransferMergeFrom(transferOrder.table_number || '');
//                         setTransferMergeTo('');
//                       }}
//                       className="w-full py-3 px-4 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg flex items-center gap-3"
//                     >
//                       <RiBillLine className="text-amber-600" size={20} />
//                       <span className="flex-1 text-left font-medium">Merge Bills</span>
//                     </button>
//                   )}

//                   {/* PARCEL → PARCEL MERGE (only for parcel orders) */}
//                   {transferOrder.order_type === 'parcel' && (
//                     <button
//                       onClick={() => {
//                         setTransferActionType('merge-parcels');
//                         setParcelMergeFrom(transferOrder.parcel_number || '');
//                         setParcelMergeTo('');
//                       }}
//                       className="w-full py-3 px-4 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg flex items-center gap-3"
//                     >
//                       <RiBillLine className="text-amber-600" size={20} />
//                       <div className="flex-1 text-left">
//                         <div className="font-medium">Merge Parcels</div>
//                         <div className="text-xs text-gray-500">
//                           Combine two parcel orders into one bill
//                         </div>
//                       </div>
//                     </button>
//                   )}
//                 </div>
//               )}

//               {transferActionType === 'room-to-room' && (
//                 <div className="space-y-4">
//                   <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-800">
//                     <p className="font-semibold mb-1">What happens on transfer:</p>
//                     <ul className="space-y-0.5 list-disc list-inside text-purple-700">
//                       <li>Order + items move to the new room's booking</li>
//                       <li>
//                         <strong>Invoice number stays the same</strong> — it travels with the bill
//                       </li>
//                       <li>Current room's folio becomes clean</li>
//                       <li>Unpaid payments follow the bill</li>
//                       <li>Audit log records who made the transfer</li>
//                     </ul>
//                   </div>

//                   <div className="bg-gray-50 rounded-lg p-3 text-xs">
//                     <div className="flex justify-between mb-1">
//                       <span className="text-gray-500">From room</span>
//                       <span className="font-semibold">{transferOrder.room_number || '—'}</span>
//                     </div>
//                     {transferOrder.booking_id && (
//                       <div className="flex justify-between mb-1">
//                         <span className="text-gray-500">Booking</span>
//                         <span className="font-mono">{transferOrder.booking_id}</span>
//                       </div>
//                     )}
//                     <div className="flex justify-between">
//                       <span className="text-gray-500">Amount</span>
//                       <span className="font-semibold">
//                         ₹{(transferOrder.total_amount || 0).toFixed(2)}
//                       </span>
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       Destination Room Number *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., 202"
//                       value={transferToRoomNumber}
//                       onChange={(e) =>
//                         setTransferToRoomNumber(e.target.value.replace(/[^0-9]/g, ''))
//                       }
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-purple-500"
//                       autoFocus
//                     />
//                     <p className="text-xs text-gray-400 mt-1">
//                       Destination room must have an active Hard booking
//                     </p>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       Reason (optional)
//                     </label>
//                     <textarea
//                       placeholder="e.g., Guest moved from 101 to 202"
//                       value={transferToRoomReason}
//                       onChange={(e) => setTransferToRoomReason(e.target.value)}
//                       rows={2}
//                       maxLength={500}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-purple-500 resize-none"
//                     />
//                   </div>

//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => {
//                         setTransferActionType(null);
//                         setTransferToRoomNumber('');
//                         setTransferToRoomReason('');
//                       }}
//                       disabled={isTransferring}
//                       className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
//                     >
//                       Back
//                     </button>
//                     <button
//                       onClick={handleRoomToRoomTransferSubmit}
//                       disabled={isTransferring || !transferToRoomNumber}
//                       className={`flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2 ${
//                         isTransferring || !transferToRoomNumber
//                           ? 'opacity-50 cursor-not-allowed'
//                           : ''
//                       }`}
//                     >
//                       {isTransferring ? (
//                         <>
//                           <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
//                           Transferring…
//                         </>
//                       ) : (
//                         <>
//                           <FaExchangeAlt size={12} />
//                           Transfer
//                         </>
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               )}

//               {transferActionType === 'to-room' && (
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       Room Number *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., 209"
//                       value={transferRoomNumber}
//                       onChange={(e) => setTransferRoomNumber(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                       autoFocus
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       Booking ID (Optional)
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="Enter booking ID (optional)"
//                       value={transferBookingId}
//                       onChange={(e) => setTransferBookingId(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                     />
//                   </div>
//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => {
//                         setTransferActionType(null);
//                         setTransferRoomNumber('');
//                         setTransferBookingId('');
//                       }}
//                       className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                     >
//                       Back
//                     </button>
//                     <button
//                       onClick={
//                         transferOrder.order_type === 'parcel'
//                           ? handleParcelTransferToRoomSubmit
//                           : handleTransferToRoomSubmit
//                       }
//                       disabled={isTransferring || !transferRoomNumber}
//                       className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                         isTransferring || !transferRoomNumber ? 'opacity-50 cursor-not-allowed' : ''
//                       }`}
//                     >
//                       {isTransferring ? (
//                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                       ) : (
//                         'Transfer'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               )}

//               {transferActionType === 'to-table' && (
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       New Table Number *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="Enter new table number"
//                       value={transferNewTable}
//                       onChange={(e) => setTransferNewTable(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                       autoFocus
//                     />
//                   </div>
//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => {
//                         setTransferActionType(null);
//                         setTransferNewTable('');
//                       }}
//                       className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                     >
//                       Back
//                     </button>
//                     <button
//                       onClick={
//                         transferOrder.order_type === 'parcel'
//                           ? handleParcelTransferToTableSubmit
//                           : handleTransferToTableSubmit
//                       }
//                       disabled={isTransferring || !transferNewTable}
//                       className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                         isTransferring || !transferNewTable ? 'opacity-50 cursor-not-allowed' : ''
//                       }`}
//                     >
//                       {isTransferring ? (
//                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                       ) : (
//                         'Transfer'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               )}

//               {transferActionType === 'merge' && (
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       From Table (Source) *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., 101"
//                       value={transferMergeFrom}
//                       onChange={(e) => setTransferMergeFrom(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                       autoFocus
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       To Table (Target) *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., 102"
//                       value={transferMergeTo}
//                       onChange={(e) => setTransferMergeTo(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                     />
//                   </div>
//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => {
//                         setTransferActionType(null);
//                         setTransferMergeFrom('');
//                         setTransferMergeTo('');
//                       }}
//                       className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                     >
//                       Back
//                     </button>
//                     <button
//                       onClick={handleMergeBillsSubmit}
//                       disabled={isTransferring || !transferMergeFrom || !transferMergeTo}
//                       className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                         isTransferring || !transferMergeFrom || !transferMergeTo
//                           ? 'opacity-50 cursor-not-allowed'
//                           : ''
//                       }`}
//                     >
//                       {isTransferring ? (
//                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                       ) : (
//                         'Merge'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               )}

//               {transferActionType === 'merge-parcels' && (
//                 <div className="space-y-4">
//                   <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
//                     <p className="font-semibold mb-1">What happens on merge:</p>
//                     <ul className="space-y-0.5 list-disc list-inside text-amber-700">
//                       <li>Source parcel's items move to target parcel</li>
//                       <li>Target parcel becomes the single payable bill</li>
//                       <li>Source parcel is cancelled (kept for records)</li>
//                       <li>Source billing becomes MERGED (zeroed)</li>
//                       <li>Merge ticket prints to kitchen</li>
//                       <li>Audit log records who merged and when</li>
//                     </ul>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       From Parcel (Source) *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., PCL-131026-0005"
//                       value={parcelMergeFrom}
//                       onChange={(e) => setParcelMergeFrom(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-amber-500"
//                       autoFocus
//                     />
//                     <p className="text-xs text-gray-400 mt-1">
//                       The parcel that will be emptied and cancelled
//                     </p>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       To Parcel (Target) *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., PCL-131026-0001"
//                       value={parcelMergeTo}
//                       onChange={(e) => setParcelMergeTo(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-amber-500"
//                     />
//                     <p className="text-xs text-gray-400 mt-1">
//                       The parcel that keeps the bill (target)
//                     </p>
//                   </div>

//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => {
//                         setTransferActionType(null);
//                         setParcelMergeFrom('');
//                         setParcelMergeTo('');
//                       }}
//                       disabled={isTransferring}
//                       className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
//                     >
//                       Back
//                     </button>
//                     <button
//                       onClick={handleParcelMergeSubmit}
//                       disabled={isTransferring || !parcelMergeFrom || !parcelMergeTo}
//                       className={`flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium flex items-center justify-center gap-2 ${
//                         isTransferring || !parcelMergeFrom || !parcelMergeTo
//                           ? 'opacity-50 cursor-not-allowed'
//                           : ''
//                       }`}
//                     >
//                       {isTransferring ? (
//                         <>
//                           <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
//                           Merging…
//                         </>
//                       ) : (
//                         <>
//                           <RiBillLine size={12} />
//                           Merge Parcels
//                         </>
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}

//         <ToastContainer
//           position="bottom-right"
//           autoClose={3000}
//           hideProgressBar={false}
//           newestOnTop
//           closeOnClick
//           rtl={false}
//           pauseOnFocusLoss
//           draggable
//           pauseOnHover
//           theme="light"
//         />
//       </div>
//     </>
//   );
// }

// export default OrderInvoicing;

// import React, { useState, useEffect, useRef, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import {
//   FaFileInvoice,
//   FaSearch,
//   FaEye,
//   FaPrint,
//   FaEnvelope,
//   FaMoneyBill,
//   FaDigitalOcean,
//   FaDownload,
//   FaTimes,
//   FaCheckCircle,
//   FaClock,
//   FaTable,
//   FaBox,
//   FaUser,
//   FaCalendarAlt,
//   FaCreditCard,
//   FaChevronLeft,
//   FaChevronRight,
//   FaBuilding,
//   FaSync,
//   FaHotel,
//   FaEdit,
//   FaUtensils,
//   FaExchangeAlt,
//   FaReceipt,
//   FaLayerGroup,
//   FaChevronDown,
//   FaChevronUp,
// } from 'react-icons/fa';
// import { MdTableBar } from 'react-icons/md';
// import { RiBillLine } from 'react-icons/ri';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
// const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// // Helper: normalize an item's cancelled flag
// const normalizeItemCancelled = (item) =>
//   item.is_cancelled === true ||
//   item.status === 'cancelled' ||
//   item.status === 'Cancelled' ||
//   item.cancelled === true;

// // Map frontend date filter values to backend-expected values
// const mapDateFilterForApi = (f) => {
//   if (f === 'weekly' || f === 'monthly' || f === 'lastMonth') return 'custom';
//   return f; // today | yesterday | all | custom
// };

// function OrderInvoicing() {
//   const navigate = useNavigate();
//   const [orders, setOrders] = useState([]);
//   const [filteredOrders, setFilteredOrders] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [invoiceData, setInvoiceData] = useState(null);
//   const [showInvoiceModal, setShowInvoiceModal] = useState(false);
//   const [filterType, setFilterType] = useState('all');
//   const [filterStatus, setFilterStatus] = useState('all');
//   const [filterDate, setFilterDate] = useState('today');
//   const [customDateFrom, setCustomDateFrom] = useState('');
//   const [customDateTo, setCustomDateTo] = useState('');
//   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
//   const [showPaymentModal, setShowPaymentModal] = useState(false);
//   const [isProcessingPayment, setIsProcessingPayment] = useState(false);
//   const [isPrinting, setIsPrinting] = useState(false);
//   const [isPrintingKOT, setIsPrintingKOT] = useState(false);
//   const [isSendingEmail, setIsSendingEmail] = useState(false);
//   const [emailRecipient, setEmailRecipient] = useState('');
//   const [showEmailModal, setShowEmailModal] = useState(false);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [isRoomOrder, setIsRoomOrder] = useState(false);
//   const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

//   // Company search
//   const [companies, setCompanies] = useState([]);
//   const [companySearchText, setCompanySearchText] = useState('');
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
//   const [selectedCompany, setSelectedCompany] = useState(null);
//   const companyDropdownRef = useRef(null);

//   // Edit guest
//   const [showEditGuestModal, setShowEditGuestModal] = useState(false);
//   const [editGuestData, setEditGuestData] = useState({
//     customer_name: '',
//     customer_phone: '',
//     customer_email: '',
//     gst_number: '',
//   });
//   const [isUpdatingGuest, setIsUpdatingGuest] = useState(false);

//   // Transfer
//   const [showTransferModal, setShowTransferModal] = useState(false);
//   const [transferOrder, setTransferOrder] = useState(null);
//   const [isTransferring, setIsTransferring] = useState(false);
//   const [transferRoomNumber, setTransferRoomNumber] = useState('');
//   const [transferBookingId, setTransferBookingId] = useState('');
//   const [transferNewTable, setTransferNewTable] = useState('');
//   const [transferMergeFrom, setTransferMergeFrom] = useState('');
//   const [transferMergeTo, setTransferMergeTo] = useState('');
//   const [transferActionType, setTransferActionType] = useState(null);

//   // ============ ROOM FOLIO POPUP STATE ============
//   const [showRoomFolioModal, setShowRoomFolioModal] = useState(false);
//   const [folioSearchQuery, setFolioSearchQuery] = useState('');
//   const [folioFilterDate, setFolioFilterDate] = useState('today');
//   const [folioCustomFrom, setFolioCustomFrom] = useState('');
//   const [folioCustomTo, setFolioCustomTo] = useState('');
//   const [expandedFolioKey, setExpandedFolioKey] = useState(null);
//   const folioSearchInputRef = useRef(null);

//   const [apiParams, setApiParams] = useState({
//     property_id: null,
//     order_type: null,
//     order_status: null,
//     date_from: null,
//     date_to: null,
//   });

//   // ============ DATE HELPERS ============
//   const getBackendDateKey = (dateString) => {
//     if (!dateString) return null;
//     if (typeof dateString === 'string') {
//       const m = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
//       if (m) return m[1];
//       const d = new Date(dateString);
//       if (isNaN(d.getTime())) return null;
//       const y = d.getFullYear();
//       const mo = String(d.getMonth() + 1).padStart(2, '0');
//       const da = String(d.getDate()).padStart(2, '0');
//       return `${y}-${mo}-${da}`;
//     }
//     if (dateString instanceof Date) {
//       if (isNaN(dateString.getTime())) return null;
//       const y = dateString.getFullYear();
//       const mo = String(dateString.getMonth() + 1).padStart(2, '0');
//       const da = String(dateString.getDate()).padStart(2, '0');
//       return `${y}-${mo}-${da}`;
//     }
//     return null;
//   };

//   const getLocalTodayKey = () => {
//     const now = new Date();
//     const y = now.getFullYear();
//     const mo = String(now.getMonth() + 1).padStart(2, '0');
//     const da = String(now.getDate()).padStart(2, '0');
//     return `${y}-${mo}-${da}`;
//   };

//   const getLocalYesterdayKey = () => {
//     const now = new Date();
//     now.setDate(now.getDate() - 1);
//     const y = now.getFullYear();
//     const mo = String(now.getMonth() + 1).padStart(2, '0');
//     const da = String(now.getDate()).padStart(2, '0');
//     return `${y}-${mo}-${da}`;
//   };

//   const addDaysToKey = (key, delta) => {
//     const [y, m, d] = key.split('-').map(Number);
//     const dt = new Date(y, m - 1, d);
//     dt.setDate(dt.getDate() + delta);
//     const yy = dt.getFullYear();
//     const mm = String(dt.getMonth() + 1).padStart(2, '0');
//     const dd = String(dt.getDate()).padStart(2, '0');
//     return `${yy}-${mm}-${dd}`;
//   };

//   const getConsolidatedDateRange = () => {
//     const todayKey = getLocalTodayKey();
//     const yesterdayKey = getLocalYesterdayKey();
//     const weekStartKey = addDaysToKey(todayKey, -6);
//     const monthStartKey = todayKey.slice(0, 8) + '01';

//     const [yStr, mStr] = todayKey.split('-');
//     const y = parseInt(yStr, 10);
//     const m = parseInt(mStr, 10);
//     const prevMonthYear = m === 1 ? y - 1 : y;
//     const prevMonth = m === 1 ? 12 : m - 1;
//     const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
//     const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
//     const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
//       lastMonthEndDate.getMonth() + 1
//     ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

//     switch (folioFilterDate) {
//       case 'today':
//         return { filter: 'today', from: null, to: null };
//       case 'yesterday':
//         return { filter: 'yesterday', from: null, to: null };
//       case 'weekly':
//         return { filter: 'custom', from: weekStartKey, to: todayKey };
//       case 'monthly':
//         return { filter: 'custom', from: monthStartKey, to: todayKey };
//       case 'lastMonth':
//         return { filter: 'custom', from: lastMonthStartKey, to: lastMonthEndKey };
//       case 'custom':
//         return {
//           filter: 'custom',
//           from: folioCustomFrom || null,
//           to: folioCustomTo || null,
//         };
//       case 'all':
//       default:
//         return { filter: 'all', from: null, to: null };
//     }
//   };

//   // Razorpay
//   useEffect(() => {
//     const loadRazorpayScript = () => {
//       const script = document.createElement('script');
//       script.src = 'https://checkout.razorpay.com/v1/checkout.js';
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => toast.error('Error loading Razorpay script.');
//       document.body.appendChild(script);
//     };
//     loadRazorpayScript();
//   }, []);

//   // Companies
//   useEffect(() => {
//     const fetchCompanies = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
//           headers: { accept: 'application/json' },
//         });
//         if (response.ok) {
//           const data = await response.json();
//           setCompanies(data.companies || []);
//         }
//       } catch (error) {
//         console.error('Error fetching companies:', error);
//       }
//     };
//     fetchCompanies();
//   }, []);

//   useEffect(() => {
//     if (companySearchText && companySearchText.length > 1) {
//       const filtered = companies.filter((c) =>
//         c.companyname.toLowerCase().includes(companySearchText.toLowerCase())
//       );
//       setFilteredCompanies(filtered);
//       setShowCompanyDropdown(true);
//     } else {
//       setFilteredCompanies([]);
//       setShowCompanyDropdown(false);
//     }
//   }, [companySearchText, companies]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
//         setShowCompanyDropdown(false);
//       }
//     };
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, []);

//   useEffect(() => {
//     fetchAllOrders();
//   }, []);

//   const formatOrderDate = (dateString) => {
//     if (!dateString) return { date: '—', time: '—' };
//     try {
//       const match = String(dateString).match(
//         /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
//       );
//       if (match) {
//         const [, y, mo, d, hh, mm] = match;
//         const months = [
//           'Jan',
//           'Feb',
//           'Mar',
//           'Apr',
//           'May',
//           'Jun',
//           'Jul',
//           'Aug',
//           'Sep',
//           'Oct',
//           'Nov',
//           'Dec',
//         ];
//         const dateLabel = `${parseInt(d, 10)} ${months[parseInt(mo, 10) - 1]} ${y}`;
//         let hour = parseInt(hh, 10);
//         const minute = mm;
//         const ampm = hour >= 12 ? 'pm' : 'am';
//         hour = hour % 12;
//         if (hour === 0) hour = 12;
//         return {
//           date: dateLabel,
//           time: `${String(hour).padStart(2, '0')}:${minute} ${ampm}`,
//         };
//       }
//       const d = new Date(dateString);
//       if (isNaN(d.getTime())) return { date: '—', time: '—' };
//       const months = [
//         'Jan',
//         'Feb',
//         'Mar',
//         'Apr',
//         'May',
//         'Jun',
//         'Jul',
//         'Aug',
//         'Sep',
//         'Oct',
//         'Nov',
//         'Dec',
//       ];
//       let hour = d.getHours();
//       const minute = String(d.getMinutes()).padStart(2, '0');
//       const ampm = hour >= 12 ? 'pm' : 'am';
//       hour = hour % 12;
//       if (hour === 0) hour = 12;
//       return {
//         date: `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`,
//         time: `${String(hour).padStart(2, '0')}:${minute} ${ampm}`,
//       };
//     } catch {
//       return { date: '—', time: '—' };
//     }
//   };

//   const getLocalDateKey = (dateString) => getBackendDateKey(dateString) || 'Unknown Date';

//   const groupOrdersByDate = (ordersList) => {
//     const groups = {};
//     ordersList.forEach((order) => {
//       const key = getLocalDateKey(order.ordered_at);
//       if (!groups[key]) groups[key] = [];
//       groups[key].push(order);
//     });
//     Object.keys(groups).forEach((key) => {
//       groups[key].sort((a, b) =>
//         String(b.ordered_at || '').localeCompare(String(a.ordered_at || ''))
//       );
//     });
//     const sortedKeys = Object.keys(groups).sort((a, b) => {
//       if (a === 'Unknown Date') return 1;
//       if (b === 'Unknown Date') return -1;
//       return b.localeCompare(a);
//     });
//     return sortedKeys.map((key) => ({ dateKey: key, orders: groups[key] }));
//   };

//   const getDateHeaderLabel = (dateKey) => {
//     if (dateKey === 'Unknown Date') return 'Unknown Date';
//     const todayKey = getLocalTodayKey();
//     const yesterdayKey = getLocalYesterdayKey();
//     if (dateKey === todayKey) return 'Today';
//     if (dateKey === yesterdayKey) return 'Yesterday';
//     const [y, m, d] = dateKey.split('-');
//     const months = [
//       'Jan',
//       'Feb',
//       'Mar',
//       'Apr',
//       'May',
//       'Jun',
//       'Jul',
//       'Aug',
//       'Sep',
//       'Oct',
//       'Nov',
//       'Dec',
//     ];
//     return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
//   };

//   const getOrderInvoiceNumber = (order) => {
//     if (!order) return null;
//     return (
//       order.invoice_number || // restaurant: billings[0].invoice_number mapped in
//       order.bill_number || // room: raw bill_number from admin_food_order
//       order.billing?.invoice_number ||
//       order.billing?.bill_number ||
//       order.billings?.[0]?.invoice_number ||
//       order.billings?.[0]?.bill_number ||
//       null
//     );
//   };
//   // ============ FETCHERS ============
//   const fetchRestaurantOrders = async () => {
//     try {
//       const params = new URLSearchParams();
//       if (apiParams.property_id) params.append('property_id', apiParams.property_id);
//       if (apiParams.order_type) params.append('order_type', apiParams.order_type);
//       if (apiParams.order_status) params.append('order_status', apiParams.order_status);
//       if (apiParams.date_from) params.append('date_from', apiParams.date_from);
//       if (apiParams.date_to) params.append('date_to', apiParams.date_to);

//       const url = `${BQ_BASE_URL}/bq/api/restaurant/billing/orders/details?${params.toString()}`;
//       const response = await fetch(url, { headers: { accept: 'application/json' } });

//       if (response.ok) {
//         const data = await response.json();
//         const transformedOrders = (data.orders || []).map((order) => {
//           let paymentMethod = 'Not Paid';
//           if (order.billings && order.billings.length > 0) {
//             const billing = order.billings[0];
//             if (billing.payments && billing.payments.length > 0) {
//               const payment = billing.payments[0];
//               paymentMethod = payment.method || payment.payment_method || 'Not Paid';
//               paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
//             } else if (billing.is_paid) paymentMethod = 'Paid';
//           }
//           const orderedAt =
//             order.ordered_at || order.billings?.[0]?.created_date || new Date().toISOString();
//           const normalizedItems = (order.order_details || []).map((it) => ({
//             ...it,
//             is_cancelled: normalizeItemCancelled(it),
//           }));
//           const cancelled =
//             order.is_cancelled === true ||
//             order.order_status === 'cancelled' ||
//             order.order_status === 'Cancelled';

//           return {
//             order_id: order.order_id,
//             serial_number: order.serial_number != null ? String(order.serial_number) : null,
//             order_type: order.order_type,
//             order_status: order.order_status,
//             is_cancelled: cancelled,
//             customer_name: order.customer_name || 'Guest',
//             customer_phone: order.customer_phone || null,
//             customer_email: order.customer_email || null,
//             gst_number: order.gst_number || null,
//             platform: order.platform || 'direct',
//             total_amount: order.financials?.total_amount || order.net_payable || 0,
//             subtotal: order.financials?.subtotal || 0,
//             tax_amount: order.financials?.tax_amount || 0,
//             discount_amount: order.financials?.discount_amount || 0,
//             service_charge: order.financials?.service_charge || 0,
//             total_paid: order.total_paid || 0,
//             net_payable: order.net_payable || 0,
//             is_fully_paid: order.is_fully_paid || false,
//             is_paid: order.is_fully_paid || false,
//             ordered_at: orderedAt,
//             items: normalizedItems,
//             billings: order.billings || [],
//             billing_id: order.billings?.[0]?.billing_id || null,
//             billing_data: order,
//             financials: order.financials,
//             payment_method: paymentMethod,
//             room_number: order.room_number || null,
//             table_number: order.table_number || null,
//             parcel_number: order.parcel_number || null,
//             booking_id: order.booking_id || null,
//             kot_number: order.kot_number || null,
//             has_billing: true,
//             invoice_number: order.billings?.[0]?.invoice_number || null,
//           };
//         });
//         return { orders: transformedOrders };
//       }
//       return null;
//     } catch (error) {
//       console.error('Error fetching restaurant orders:', error);
//       return null;
//     }
//   };

//   const fetchRoomOrders = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//         headers: { accept: 'application/json' },
//       });
//       if (response.ok) {
//         const data = await response.json();
//         return data
//           .filter((order) => order.order_type === 'room')
//           .map((order) => {
//             const hasBilling = order.billing !== null;
//             const isPaid = hasBilling ? order.billing.is_paid : false;
//             const billingId = hasBilling ? order.billing.billing_id : null;
//             let paymentMethod = 'Not Paid';
//             if (order.payments && order.payments.length > 0) {
//               paymentMethod = order.payments[0].payment_method || 'Not Paid';
//               paymentMethod = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
//             } else if (isPaid) paymentMethod = 'Paid';

//             const orderedAt = order.created_at || order.ordered_at || new Date().toISOString();
//             const normalizedItems = (order.items || []).map((it) => ({
//               ...it,
//               is_cancelled: normalizeItemCancelled(it),
//             }));
//             const cancelled =
//               order.is_cancelled === true ||
//               order.status === 'cancelled' ||
//               order.status === 'Cancelled';

//             return {
//               order_id: order.id,
//               serial_number: order.serial_number != null ? String(order.serial_number) : null,
//               order_type: 'room',
//               order_status: order.status || 'pending',
//               is_cancelled: cancelled,
//               customer_name: order.customer_name || 'Guest',
//               customer_phone: order.phone_number || null,
//               customer_email: null,
//               table_number: order.table_number || null,
//               parcel_number: order.parcel_number || null,
//               room_number: order.room_number || null,
//               kot_number: null,
//               total_amount: order.totalprice || 0,
//               is_paid: isPaid,
//               ordered_at: orderedAt,
//               items: normalizedItems,
//               billing: order.billing || null,
//               billing_id: billingId,
//               booking_id: order.booking_id || null,
//               special_request: order.special_request || null,
//               gst_number: null,
//               has_billing: hasBilling,
//               payment_method: paymentMethod,
//               billings: order.billing ? [order.billing] : [],
//               payments: order.payments || [],
//               invoice_number: order.bill_number || null,
//             };
//           });
//       }
//       return [];
//     } catch (error) {
//       console.error('Error fetching room orders:', error);
//       return [];
//     }
//   };

//   const fetchAllOrders = async () => {
//     setLoading(true);
//     try {
//       const restaurantResult = await fetchRestaurantOrders();
//       const restaurantOrders = restaurantResult?.orders || [];
//       const roomOrders = await fetchRoomOrders();
//       const allOrders = [...restaurantOrders, ...roomOrders];
//       setOrders(allOrders);
//       setFilteredOrders(allOrders);
//       setCurrentPage(1);
//     } catch (error) {
//       console.error('Error fetching orders:', error);
//       toast.error('Failed to load orders');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ============ MAIN TABLE FILTERS ============
//   useEffect(() => {
//     let filtered = orders;

//     if (searchQuery) {
//       const query = String(searchQuery).toLowerCase();
//       filtered = filtered.filter((order) => {
//         const serial = order.serial_number != null ? String(order.serial_number).toLowerCase() : '';
//         const orderId = order.order_id != null ? String(order.order_id).toLowerCase() : '';
//         const customerName =
//           order.customer_name != null ? String(order.customer_name).toLowerCase() : '';
//         const tableNumber =
//           order.table_number != null ? String(order.table_number).toLowerCase() : '';
//         const parcelNumber =
//           order.parcel_number != null ? String(order.parcel_number).toLowerCase() : '';
//         const kotNumber = order.kot_number != null ? String(order.kot_number).toLowerCase() : '';
//         const roomNumber = order.room_number != null ? String(order.room_number).toLowerCase() : '';
//         const bookingId = order.booking_id != null ? String(order.booking_id).toLowerCase() : '';
//         const invoiceNo = String(getOrderInvoiceNumber(order) || '').toLowerCase();
//         return (
//           orderId.includes(query) ||
//           serial.includes(query) ||
//           customerName.includes(query) ||
//           tableNumber.includes(query) ||
//           parcelNumber.includes(query) ||
//           kotNumber.includes(query) ||
//           roomNumber.includes(query) ||
//           bookingId.includes(query) ||
//           invoiceNo.includes(query)
//         );
//       });
//     }

//     if (filterType !== 'all') filtered = filtered.filter((o) => o.order_type === filterType);

//     if (filterStatus !== 'all') {
//       if (filterStatus === 'pending') {
//         filtered = filtered.filter(
//           (o) => !o.is_cancelled && (o.order_status === 'pending' || o.order_status === 'Pending')
//         );
//       } else if (filterStatus === 'served') {
//         filtered = filtered.filter(
//           (o) => !o.is_cancelled && (o.order_status === 'served' || o.order_status === 'Served')
//         );
//       } else if (filterStatus === 'paid') filtered = filtered.filter((o) => o.is_paid === true);
//       else if (filterStatus === 'unpaid') filtered = filtered.filter((o) => o.is_paid === false);
//       else if (filterStatus === 'cancelled') {
//         filtered = filtered.filter(
//           (o) =>
//             o.is_cancelled === true ||
//             o.order_status === 'cancelled' ||
//             o.order_status === 'Cancelled'
//         );
//       }
//     }

//     if (filterDate !== 'all') {
//       const todayKey = getLocalTodayKey();
//       const yesterdayKey = getLocalYesterdayKey();
//       const weekStartKey = addDaysToKey(todayKey, -6);
//       const monthStartKey = todayKey.slice(0, 8) + '01';

//       const [yStr, mStr] = todayKey.split('-');
//       const y = parseInt(yStr, 10);
//       const m = parseInt(mStr, 10);
//       const prevMonthYear = m === 1 ? y - 1 : y;
//       const prevMonth = m === 1 ? 12 : m - 1;
//       const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
//       const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
//       const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
//         lastMonthEndDate.getMonth() + 1
//       ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

//       filtered = filtered.filter((order) => {
//         if (filterDate === 'today') {
//           const orderDateStr = getBackendDateKey(order.ordered_at);
//           return orderDateStr === todayKey;
//         }
//         if (!order.ordered_at) return false;
//         const orderDateStr = getBackendDateKey(order.ordered_at);
//         if (!orderDateStr) return false;
//         if (filterDate === 'yesterday') return orderDateStr === yesterdayKey;
//         if (filterDate === 'weekly')
//           return orderDateStr >= weekStartKey && orderDateStr <= todayKey;
//         if (filterDate === 'monthly')
//           return orderDateStr >= monthStartKey && orderDateStr <= todayKey;
//         if (filterDate === 'lastMonth')
//           return orderDateStr >= lastMonthStartKey && orderDateStr <= lastMonthEndKey;
//         if (filterDate === 'custom') {
//           if (!customDateFrom && !customDateTo) return true;
//           let ok = true;
//           if (customDateFrom) ok = ok && orderDateStr >= customDateFrom;
//           if (customDateTo) ok = ok && orderDateStr <= customDateTo;
//           return ok;
//         }
//         return true;
//       });
//     }

//     setFilteredOrders(filtered);
//     const groupCount = groupOrdersByDate(filtered).length;
//     setTotalPages(Math.max(1, Math.ceil(groupCount / 5)));
//     setCurrentPage(1);
//   }, [searchQuery, filterType, filterStatus, filterDate, customDateFrom, customDateTo, orders]);

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };
//   const handleNextPage = () => {
//     if (currentPage < totalPages) {
//       setCurrentPage(currentPage + 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };
//   const handlePrevPage = () => {
//     if (currentPage > 1) {
//       setCurrentPage(currentPage - 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const getCurrentPageGroups = () => {
//     const allGroups = groupOrdersByDate(filteredOrders);
//     const GROUPS_PER_PAGE = 5;
//     const startIndex = (currentPage - 1) * GROUPS_PER_PAGE;
//     return allGroups.slice(startIndex, startIndex + GROUPS_PER_PAGE);
//   };

//   // ============ ROOM FOLIOS BUILDER ============
//   const buildRoomFolios = (sourceOrders, opts = {}) => {
//     const { searchQuery = '', dateFilter = 'all', customFrom = '', customTo = '' } = opts;

//     const folioMap = {};

//     sourceOrders
//       .filter((o) => o.order_type === 'room' && !o.is_cancelled)
//       .forEach((o) => {
//         const roomKey = o.room_number || 'NOROOM';
//         const bookingKey = o.booking_id || 'NOBOOKING';
//         const key = `${roomKey}__${bookingKey}`;

//         if (!folioMap[key]) {
//           folioMap[key] = {
//             key,
//             room_number: o.room_number,
//             booking_id: o.booking_id,
//             customer_name: o.customer_name || 'Guest',
//             customer_phone: o.customer_phone || null,
//             orders: [],
//             total_amount: 0,
//             earliest_time: o.ordered_at,
//             latest_time: o.ordered_at,
//             invoice_numbers: [],
//           };
//         }

//         const f = folioMap[key];
//         f.orders.push(o);
//         f.total_amount += parseFloat(o.total_amount) || 0;

//         if (String(o.ordered_at) < String(f.earliest_time)) f.earliest_time = o.ordered_at;
//         if (String(o.ordered_at) > String(f.latest_time)) f.latest_time = o.ordered_at;

//         if (o.customer_name && o.customer_name !== 'Guest') f.customer_name = o.customer_name;
//         if (o.customer_phone) f.customer_phone = o.customer_phone;

//         if (o.invoice_number) {
//           f.invoice_numbers.push(String(o.invoice_number));
//         } else if (o.bill_number) {
//           f.invoice_numbers.push(String(o.bill_number));
//         } else if (o.billing && (o.billing.invoice_number || o.billing.bill_number)) {
//           f.invoice_numbers.push(String(o.billing.invoice_number || o.billing.bill_number));
//         } else if (
//           o.billings &&
//           o.billings[0] &&
//           (o.billings[0].invoice_number || o.billings[0].bill_number)
//         ) {
//           f.invoice_numbers.push(String(o.billings[0].invoice_number || o.billings[0].bill_number));
//         }
//       });

//     let folios = Object.values(folioMap).map((f) => {
//       f.orders.sort((a, b) => String(a.ordered_at || '').localeCompare(String(b.ordered_at || '')));
//       f.order_count = f.orders.length;

//       const invs = f.invoice_numbers.filter(Boolean);
//       if (invs.length === 0) {
//         f.invoice_label = null;
//         f.invoice_range = null;
//       } else if (invs.length === 1) {
//         f.invoice_label = invs[0];
//         f.invoice_range = invs[0];
//       } else {
//         const parsed = invs
//           .map((i) => {
//             const m = String(i).match(/(\d+)$/);
//             return m ? { full: i, num: parseInt(m[1], 10) } : null;
//           })
//           .filter(Boolean);

//         if (parsed.length >= 2) {
//           parsed.sort((a, b) => a.num - b.num);
//           const first = parsed[0].full;
//           const last = parsed[parsed.length - 1].full;
//           const prefixMatch = first.match(/^(.*?)(\d+)$/);
//           if (prefixMatch) {
//             const prefix = prefixMatch[1];
//             const label = `${prefix}${parsed[0].num} → ${prefix}${parsed[parsed.length - 1].num}`;
//             f.invoice_label = label;
//             f.invoice_range = { first, last, prefix };
//           } else {
//             f.invoice_label = `${first} → ${last}`;
//             f.invoice_range = { first, last };
//           }
//         } else {
//           f.invoice_label = `${invs[0]} → ${invs[invs.length - 1]}`;
//           f.invoice_range = { first: invs[0], last: invs[invs.length - 1] };
//         }
//       }

//       return f;
//     });

//     if (searchQuery && searchQuery.trim()) {
//       const q = searchQuery.toLowerCase();
//       folios = folios.filter((f) => {
//         if (
//           String(f.room_number || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         if (
//           String(f.booking_id || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         if (
//           String(f.customer_name || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         if (
//           String(f.customer_phone || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         if (
//           String(f.invoice_label || '')
//             .toLowerCase()
//             .includes(q)
//         )
//           return true;
//         return f.orders.some((o) =>
//           String(o.order_id || '')
//             .toLowerCase()
//             .includes(q)
//         );
//       });
//     }

//     if (dateFilter !== 'all') {
//       const todayKey = getLocalTodayKey();
//       const yesterdayKey = getLocalYesterdayKey();
//       const weekStartKey = addDaysToKey(todayKey, -6);
//       const monthStartKey = todayKey.slice(0, 8) + '01';

//       const [yStr, mStr] = todayKey.split('-');
//       const y = parseInt(yStr, 10);
//       const m = parseInt(mStr, 10);
//       const prevMonthYear = m === 1 ? y - 1 : y;
//       const prevMonth = m === 1 ? 12 : m - 1;
//       const lastMonthStartKey = `${prevMonthYear}-${String(prevMonth).padStart(2, '0')}-01`;
//       const lastMonthEndDate = new Date(prevMonthYear, prevMonth, 0);
//       const lastMonthEndKey = `${lastMonthEndDate.getFullYear()}-${String(
//         lastMonthEndDate.getMonth() + 1
//       ).padStart(2, '0')}-${String(lastMonthEndDate.getDate()).padStart(2, '0')}`;

//       folios = folios
//         .map((f) => {
//           const inRangeOrders = f.orders.filter((o) => {
//             const orderDateStr = getBackendDateKey(o.ordered_at);
//             if (!orderDateStr) return false;
//             if (dateFilter === 'today') return orderDateStr === todayKey;
//             if (dateFilter === 'yesterday') return orderDateStr === yesterdayKey;
//             if (dateFilter === 'weekly')
//               return orderDateStr >= weekStartKey && orderDateStr <= todayKey;
//             if (dateFilter === 'monthly')
//               return orderDateStr >= monthStartKey && orderDateStr <= todayKey;
//             if (dateFilter === 'lastMonth')
//               return orderDateStr >= lastMonthStartKey && orderDateStr <= lastMonthEndKey;
//             if (dateFilter === 'custom') {
//               let ok = true;
//               if (customFrom) ok = ok && orderDateStr >= customFrom;
//               if (customTo) ok = ok && orderDateStr <= customTo;
//               return ok;
//             }
//             return true;
//           });

//           if (inRangeOrders.length === 0) return null;

//           const total = inRangeOrders.reduce((s, o) => s + (parseFloat(o.total_amount) || 0), 0);

//           const earliest = inRangeOrders.reduce(
//             (min, o) => (String(o.ordered_at) < String(min) ? o.ordered_at : min),
//             inRangeOrders[0].ordered_at
//           );
//           const latest = inRangeOrders.reduce(
//             (max, o) => (String(o.ordered_at) > String(max) ? o.ordered_at : max),
//             inRangeOrders[0].ordered_at
//           );

//           return {
//             ...f,
//             orders: inRangeOrders,
//             order_count: inRangeOrders.length,
//             total_amount: total,
//             earliest_time: earliest,
//             latest_time: latest,
//           };
//         })
//         .filter(Boolean);
//     }

//     folios.sort((a, b) => {
//       const ra = String(a.room_number || '').padStart(10, '0');
//       const rb = String(b.room_number || '').padStart(10, '0');
//       return ra.localeCompare(rb);
//     });

//     return folios;
//   };

//   const roomFolios = useMemo(
//     () =>
//       buildRoomFolios(orders, {
//         searchQuery: folioSearchQuery,
//         dateFilter: folioFilterDate,
//         customFrom: folioCustomFrom,
//         customTo: folioCustomTo,
//       }),
//     [orders, folioSearchQuery, folioFilterDate, folioCustomFrom, folioCustomTo]
//   );

//   const folioSummary = useMemo(() => {
//     const total = roomFolios.reduce((s, f) => s + f.total_amount, 0);
//     const rooms = roomFolios.length;
//     const ordersCount = roomFolios.reduce((s, f) => s + f.order_count, 0);
//     const invoicesCount = roomFolios.reduce((s, f) => s + f.invoice_numbers.length, 0);
//     return { total, rooms, ordersCount, invoicesCount };
//   }, [roomFolios]);

//   // ============ EDIT GUEST ============
//   const handleCompanySelect = (company) => {
//     setSelectedCompany(company);
//     setCompanySearchText(company.companyname);
//     setEditGuestData((prev) => ({
//       ...prev,
//       customer_name: company.companyname,
//       gst_number: company.gstnumber || '',
//     }));
//     setShowCompanyDropdown(false);
//   };

//   const handleOpenEditGuest = (order) => {
//     if (order.order_type === 'room') {
//       toast.info('Edit guest details is only available for restaurant and parcel orders');
//       return;
//     }
//     setSelectedOrder(order);
//     setEditGuestData({
//       customer_name: order.customer_name || '',
//       customer_phone: order.customer_phone || '',
//       customer_email: order.customer_email || '',
//       gst_number: order.gst_number || '',
//     });
//     setCompanySearchText('');
//     setSelectedCompany(null);
//     setShowCompanyDropdown(false);
//     setShowEditGuestModal(true);
//   };

//   const handleEditGuestChange = (e) => {
//     const { name, value } = e.target;
//     setEditGuestData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleUpdateGuestDetails = async () => {
//     if (!selectedOrder) return;
//     setIsUpdatingGuest(true);
//     try {
//       const url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${selectedOrder.order_id}/customer-details`;
//       const payload = {
//         customer_name: editGuestData.customer_name || 'Guest',
//         customer_phone: editGuestData.customer_phone || '',
//         customer_email: editGuestData.customer_email || '',
//         gst_number: editGuestData.gst_number || '',
//       };
//       const response = await fetch(url, {
//         method: 'PATCH',
//         headers: { accept: 'application/json', 'Content-Type': 'application/json' },
//         body: JSON.stringify(payload),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success('Customer details updated successfully!');
//         setShowEditGuestModal(false);
//         await fetchAllOrders();
//       } else {
//         throw new Error(data.message || data.detail || 'Failed to update customer details');
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Failed to update customer details');
//     } finally {
//       setIsUpdatingGuest(false);
//     }
//   };

//   // ============ INVOICE ============
//   const fetchDetailedInvoiceData = async (orderId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/billing/order/${orderId}`, {
//         headers: { accept: 'application/json' },
//       });
//       if (response.ok) return await response.json();
//       return null;
//     } catch (error) {
//       console.error('Error fetching detailed billing data:', error);
//       return null;
//     }
//   };

//   const fetchInvoiceData = async (order) => {
//     setIsLoadingInvoice(true);
//     try {
//       if (order.order_type === 'room') {
//         const url = `${BQ_BASE_URL}/bq/api/food-invoice/data/${order.order_id}`;
//         const response = await fetch(url, { headers: { accept: 'application/json' } });
//         if (response.ok) {
//           const data = await response.json();
//           const rawItems = data.items || data.order_details || [];
//           const normalizedItems = rawItems.map((it) => ({
//             ...it,
//             is_cancelled: normalizeItemCancelled(it),
//           }));
//           return {
//             ...data,
//             items: normalizedItems,
//             room_number: order.room_number || data.room_number || null,
//             table_number: order.table_number || data.table_number || null,
//             booking_id: order.booking_id || data.booking_id || null,
//             kot_number: order.kot_number || data.kot_number || null,
//             parcel_number: order.parcel_number || data.parcel_number || null,
//           };
//         }
//         toast.error('Failed to fetch room invoice data');
//         return null;
//       } else {
//         const billingData = await fetchDetailedInvoiceData(order.order_id);
//         if (!billingData) {
//           toast.error('Failed to fetch billing details');
//           return null;
//         }
//         const allBillings = billingData.billings || [];
//         const primaryBilling = allBillings.find((b) => b.invoice_number) || allBillings[0] || {};
//         const rawItems = billingData.order_details || [];
//         const normalizedItems = rawItems.map((it) => ({
//           ...it,
//           is_cancelled: normalizeItemCancelled(it),
//         }));
//         return {
//           invoice_number: primaryBilling.invoice_number ?? 'Not yet issued',
//           invoice_date:
//             primaryBilling.generated_at || primaryBilling.paid_at || new Date().toISOString(),
//           order_id: billingData.order_id,
//           order_type: billingData.order_type,
//           is_cancelled:
//             billingData.is_cancelled === true ||
//             billingData.order_status === 'cancelled' ||
//             billingData.order_status === 'Cancelled',
//           customer_name: billingData.customer_name || 'Guest',
//           customer_phone: billingData.customer_phone || '-',
//           customer_email: billingData.customer_email || '-',
//           customer_gst: billingData.gst_number || '-',
//           items: normalizedItems,
//           subtotal: billingData.financials?.subtotal || 0,
//           total_tax: billingData.financials?.tax_amount || 0,
//           total_discount: billingData.financials?.discount_amount || 0,
//           total_service: billingData.financials?.service_charge || 0,
//           grand_total: billingData.financials?.total_amount || billingData.net_payable || 0,
//           total_paid: billingData.total_paid || 0,
//           balance_due: billingData.net_payable || 0,
//           amount_in_words: `${billingData.net_payable || 0} Rupees Only`,
//           payment_method: billingData.billings?.[0]?.payments?.[0]?.method || 'Not Paid Yet',
//           property_name: billingData.property?.name || 'R&T INTERNATIONAL',
//           property_address:
//             billingData.property?.address || 'Taluka Khed Chiplun Area, Lote, Maharashtra, 415722',
//           property_contact: billingData.property?.contact || '8698732336',
//           property_email: billingData.property?.email || 'reception@hotelpagoda.com',
//           property_gst: billingData.property?.gst || '27AAAFR6160L1ZZ',
//           billing_id: billingData.billings?.[0]?.billing_id || null,
//           billings: billingData.billings || [],
//           payments: billingData.billings?.[0]?.payments || [],
//           financials: billingData.financials,
//           room_number: order.room_number || billingData.room_number || null,
//           table_number: order.table_number || billingData.table_number || null,
//           parcel_number: order.parcel_number || billingData.parcel_number || null,
//           booking_id: order.booking_id || billingData.booking_id || null,
//           kot_number: order.kot_number || billingData.kot_number || null,
//         };
//       }
//     } catch (error) {
//       console.error('Error fetching invoice:', error);
//       toast.error('Failed to load invoice');
//       return null;
//     } finally {
//       setIsLoadingInvoice(false);
//     }
//   };

//   const handleViewInvoice = async (order) => {
//     setInvoiceData(null);
//     setShowInvoiceModal(false);
//     setIsRoomOrder(false);
//     setSelectedOrder(order);
//     try {
//       const data = await fetchInvoiceData(order);
//       if (data) {
//         setInvoiceData(data);
//         setIsRoomOrder(order.order_type === 'room');
//         setTimeout(() => setShowInvoiceModal(true), 100);
//       } else {
//         toast.error('Could not load invoice data');
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error('Error loading invoice');
//     }
//   };

//   const handleCloseInvoiceModal = () => {
//     setShowInvoiceModal(false);
//     setTimeout(() => {
//       setInvoiceData(null);
//       setIsRoomOrder(false);
//       setSelectedOrder(null);
//     }, 300);
//   };

//   // ============ PAYMENT ============
//   const fetchBillingInfo = async (orderId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/billing/order/${orderId}`, {
//         headers: { accept: 'application/json' },
//       });
//       if (response.ok) {
//         const data = await response.json();
//         if (data.billings && data.billings.length > 0) {
//           const billing = data.billings[0];
//           return {
//             billing_id: billing.billing_id,
//             billing_data: data,
//             final_amount: billing.final_amount || data.net_payable || 0,
//             is_paid: billing.is_paid || false,
//           };
//         }
//         if (data.financials) {
//           return {
//             billing_id: null,
//             billing_data: data,
//             final_amount: data.net_payable || data.financials.total_amount || 0,
//             is_paid: false,
//             needs_billing_creation: true,
//           };
//         }
//         return null;
//       }
//       return null;
//     } catch (error) {
//       console.error('Error fetching billing info:', error);
//       return null;
//     }
//   };

//   const handleDownloadPDF = async (order) => {
//     try {
//       let url;
//       if (order.order_type === 'room')
//         url = `${BQ_BASE_URL}/bq/api/food-invoice/pdf/${order.order_id}`;
//       else url = `${BQ_BASE_URL}/bq/api/restaurant/invoice/pdf/${order.order_id}`;

//       const response = await fetch(url, { headers: { accept: '*/*' } });
//       if (response.ok) {
//         const blob = await response.blob();
//         const urlObj = window.URL.createObjectURL(blob);
//         const a = document.createElement('a');
//         a.href = urlObj;
//         const contentDisposition = response.headers.get('content-disposition');
//         const filename = contentDisposition
//           ? contentDisposition.split('filename=')[1].replace(/"/g, '')
//           : `invoice_${order.order_id}.pdf`;
//         a.download = filename;
//         document.body.appendChild(a);
//         a.click();
//         window.URL.revokeObjectURL(urlObj);
//         document.body.removeChild(a);
//         toast.success('Invoice downloaded successfully');
//       } else {
//         toast.error('Failed to download invoice');
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to download invoice');
//     }
//   };

//   // ============ KOT ============
//   const handlePrintKOTRoom = async (order) => {
//     if (!order.order_id) return toast.error('Order ID not found');
//     setIsPrintingKOT(true);
//     try {
//       const url = `${BQ_BASE_URL}/bq/api/food/print/kot/${order.order_id}`;
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: { accept: 'application/json' },
//         body: '',
//       });
//       const data = await response.json();
//       if (response.ok) toast.success(`KOT sent to kitchen printer! KOT: ${data.kot_number || ''}`);
//       else toast.error(data.message || 'Failed to print KOT');
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to print KOT');
//     } finally {
//       setIsPrintingKOT(false);
//     }
//   };

//   const handlePrintKOTRestaurant = async (order) => {
//     if (!order.order_id) return toast.error('Order ID not found');
//     setIsPrintingKOT(true);
//     try {
//       const url = `${BQ_BASE_URL}/bq/api/restaurant/print/kot/${order.order_id}`;
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: { accept: 'application/json' },
//         body: '',
//       });
//       const data = await response.json();
//       if (response.ok) {
//         const batchInfo =
//           data.batches && data.batches.length > 0
//             ? `KOT: ${data.batches.map((b) => b.kot_number).join(', ')}`
//             : '';
//         toast.success(`KOT sent to kitchen printer! ${batchInfo}`);
//       } else toast.error(data.message || 'Failed to print KOT');
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to print KOT');
//     } finally {
//       setIsPrintingKOT(false);
//     }
//   };

//   const handlePrintKOT = async (order) => {
//     if (order.order_type === 'room') await handlePrintKOTRoom(order);
//     else await handlePrintKOTRestaurant(order);
//   };

//   const handlePrintInvoice = async (order) => {
//     setIsPrinting(true);
//     try {
//       let url;
//       if (order.order_type === 'room') url = `${BQ_BASE_URL}/bq/api/food/print/${order.order_id}`;
//       else url = `${BQ_BASE_URL}/bq/api/restaurant/print/${order.order_id}`;

//       const response = await fetch(url, {
//         method: 'POST',
//         headers: { accept: 'application/json', 'Content-Type': 'application/json' },
//         body: '',
//       });
//       const data = await response.json();
//       if (response.ok) toast.success('Invoice sent to printer successfully!');
//       else toast.error(data.message || 'Failed to print invoice');
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to print invoice');
//     } finally {
//       setIsPrinting(false);
//     }
//   };

//   const handleSendEmail = async (order) => {
//     if (!emailRecipient) return toast.error('Please enter recipient email');
//     setIsSendingEmail(true);
//     try {
//       let url;
//       if (order.order_type === 'room') {
//         url = `${BQ_BASE_URL}/bq/api/food-invoice/email/${order.order_id}?recipient_email=${encodeURIComponent(
//           emailRecipient
//         )}&cc_hotel=true`;
//       } else {
//         url = `${BQ_BASE_URL}/bq/api/restaurant/invoice/email/${order.order_id}?recipient_email=${encodeURIComponent(
//           emailRecipient
//         )}&cc_hotel=true`;
//       }
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: { accept: 'application/json', 'Content-Type': 'application/json' },
//         body: '',
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success('Invoice email sent successfully!');
//         setShowEmailModal(false);
//         setEmailRecipient('');
//       } else toast.error(data.message || 'Failed to send email');
//     } catch (error) {
//       console.error(error);
//       toast.error('Failed to send email');
//     } finally {
//       setIsSendingEmail(false);
//     }
//   };

//   const handleRoomCashPayment = async (order) => {
//     if (order.is_paid) {
//       toast.info('This order is already paid');
//       setShowPaymentModal(false);
//       setSelectedPaymentMethod(null);
//       return;
//     }
//     if (!order.booking_id) return toast.error('Booking ID not found for this room order');
//     if (!order.has_billing && !order.billing_id)
//       return toast.error('No billing record found. Please bill the order first.');

//     let billingId = order.billing?.billing_id || order.billing_id;
//     if (!billingId) {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//           headers: { accept: 'application/json' },
//         });
//         if (response.ok) {
//           const data = await response.json();
//           const orderData = data.find((o) => o.id === order.order_id);
//           if (orderData && orderData.billing) {
//             billingId = orderData.billing.billing_id;
//             order.billing = orderData.billing;
//             order.billing_id = billingId;
//             order.has_billing = true;
//           }
//         }
//       } catch (e) {
//         console.error(e);
//       }
//     }
//     if (!billingId) return toast.error('No billing ID found.');

//     setIsProcessingPayment(true);
//     try {
//       const paymentData = {
//         booking_id: order.booking_id,
//         billing_id: billingId,
//         amount: order.total_amount,
//       };
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(paymentData),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success('Payment successful!');
//         setShowPaymentModal(false);
//         setSelectedPaymentMethod(null);
//         await fetchAllOrders();
//         handleCloseInvoiceModal();
//       } else throw new Error(data.detail || 'Payment failed');
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Failed to process cash payment');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handleRoomRazorpayPayment = async (order) => {
//     if (!razorpayLoaded) return toast.error('Razorpay script is not loaded yet.');
//     if (order.is_paid) {
//       toast.info('This order is already paid');
//       setShowPaymentModal(false);
//       setSelectedPaymentMethod(null);
//       return;
//     }
//     if (!order.booking_id) return toast.error('Booking ID not found for this room order');
//     if (!order.has_billing && !order.billing_id)
//       return toast.error('No billing record found. Please bill the order first.');

//     let billingId = order.billing?.billing_id || order.billing_id;
//     if (!billingId) {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
//           headers: { accept: 'application/json' },
//         });
//         if (response.ok) {
//           const data = await response.json();
//           const orderData = data.find((o) => o.id === order.order_id);
//           if (orderData && orderData.billing) {
//             billingId = orderData.billing.billing_id;
//             order.billing = orderData.billing;
//             order.billing_id = billingId;
//             order.has_billing = true;
//           }
//         }
//       } catch (e) {
//         console.error(e);
//       }
//     }
//     if (!billingId) return toast.error('No billing ID found.');

//     setIsProcessingPayment(true);
//     try {
//       const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${order.booking_id}&strict_target=true`;
//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//         body: '',
//       });
//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to create payment order');
//       }
//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: orderData.amount,
//           currency: 'INR',
//           name: 'Hotel Restaurant',
//           description: `Room Order - Room ${order.room_number || 'N/A'}`,
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//             };
//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
//                 {
//                   method: 'POST',
//                   headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );
//               if (verificationResponse.ok) {
//                 toast.success('Payment successful and verified!');
//                 setShowPaymentModal(false);
//                 setSelectedPaymentMethod(null);
//                 await fetchAllOrders();
//                 handleCloseInvoiceModal();
//               } else {
//                 const errorData = await verificationResponse.json();
//                 toast.error(errorData.detail || 'Payment verification failed.');
//               }
//             } catch (error) {
//               console.error(error);
//               toast.error('Error verifying payment.');
//             }
//           },
//           prefill: {
//             name: order.customer_name || 'Guest',
//             email: order.customer_email || 'guest@example.com',
//             contact: order.customer_phone || '9999999999',
//           },
//           theme: { color: '#3399cc' },
//         };
//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       } else throw new Error('No Razorpay order ID received');
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Error processing payment.');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handleRestaurantCashPayment = async (order) => {
//     setIsProcessingPayment(true);
//     try {
//       const billingInfo = await fetchBillingInfo(order.order_id);
//       if (!billingInfo) {
//         toast.error('Could not retrieve billing information');
//         return;
//       }
//       if (!billingInfo.billing_id) {
//         toast.error('No billing record found.');
//         return;
//       }
//       if (billingInfo.is_paid) {
//         toast.info('This order is already paid');
//         setShowPaymentModal(false);
//         setSelectedPaymentMethod(null);
//         await fetchAllOrders();
//         return;
//       }
//       const paymentData = {
//         order_id: order.order_id,
//         billing_id: billingInfo.billing_id,
//         amount: billingInfo.final_amount,
//         notes: `Cash payment for order ${order.order_id}`,
//       };
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/payments/cash`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(paymentData),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`Payment successful! Change due: ₹${data.change_due || 0}`);
//         setShowPaymentModal(false);
//         setSelectedPaymentMethod(null);
//         await fetchAllOrders();
//         handleCloseInvoiceModal();
//       } else throw new Error(data.detail || 'Payment failed');
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Failed to process cash payment');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handleRestaurantRazorpayPayment = async (order) => {
//     if (!razorpayLoaded) return toast.error('Razorpay script is not loaded yet.');
//     setIsProcessingPayment(true);
//     try {
//       const billingInfo = await fetchBillingInfo(order.order_id);
//       if (!billingInfo || !billingInfo.billing_id) {
//         toast.error('No billing record found.');
//         return;
//       }
//       if (billingInfo.is_paid) {
//         toast.info('This order is already paid');
//         setShowPaymentModal(false);
//         setSelectedPaymentMethod(null);
//         await fetchAllOrders();
//         return;
//       }
//       const apiUrl = `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/create-order`;
//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//         body: JSON.stringify({
//           order_id: order.order_id,
//           billing_id: billingInfo.billing_id,
//         }),
//       });
//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to create payment order');
//       }
//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: orderData.amount,
//           currency: 'INR',
//           name: 'Restaurant',
//           description: `Order ${order.order_id}`,
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//             };
//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/restaurant/payments/razorpay/verify`,
//                 {
//                   method: 'POST',
//                   headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );
//               if (verificationResponse.ok) {
//                 toast.success('Payment successful and verified!');
//                 setShowPaymentModal(false);
//                 setSelectedPaymentMethod(null);
//                 await fetchAllOrders();
//                 handleCloseInvoiceModal();
//               } else toast.error('Payment verification failed.');
//             } catch (error) {
//               console.error(error);
//               toast.error('Error verifying payment.');
//             }
//           },
//           prefill: {
//             name: order.customer_name || 'Guest',
//             email: order.customer_email || 'guest@example.com',
//             contact: order.customer_phone || '9999999999',
//           },
//           theme: { color: '#3399cc' },
//         };
//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error(error.message || 'Error processing payment.');
//     } finally {
//       setIsProcessingPayment(false);
//     }
//   };

//   const handlePaymentOption = (method) => {
//     setSelectedPaymentMethod(method);
//     setShowPaymentModal(true);
//   };

//   const confirmPayment = async () => {
//     if (!selectedOrder) return;
//     if (selectedOrder.is_paid) {
//       toast.info('This order is already paid');
//       setShowPaymentModal(false);
//       setSelectedPaymentMethod(null);
//       return;
//     }
//     if (selectedOrder.order_type === 'room') {
//       if (selectedPaymentMethod === 'cash') await handleRoomCashPayment(selectedOrder);
//       else if (selectedPaymentMethod === 'razorpay') await handleRoomRazorpayPayment(selectedOrder);
//     } else {
//       if (selectedPaymentMethod === 'cash') await handleRestaurantCashPayment(selectedOrder);
//       else if (selectedPaymentMethod === 'razorpay')
//         await handleRestaurantRazorpayPayment(selectedOrder);
//     }
//   };

//   // ============ TRANSFER ============
//   const handleTransferToRoomSubmit = async () => {
//     if (!transferRoomNumber) return toast.error('Please enter room number');
//     setIsTransferring(true);
//     try {
//       const payload = { room_number: transferRoomNumber };
//       if (transferBookingId && transferBookingId.trim() !== '')
//         payload.booking_id = transferBookingId.trim();
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/transfer-to-room`,
//         {
//           method: 'POST',
//           headers: { accept: 'application/json', 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Transferred to room successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferRoomNumber('');
//         setTransferBookingId('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.detail || data.message || 'Transfer failed');
//     } catch (error) {
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   const handleParcelTransferToRoomSubmit = async () => {
//     if (!transferRoomNumber) return toast.error('Please enter room number');
//     setIsTransferring(true);
//     try {
//       const payload = { room_number: transferRoomNumber };
//       if (transferBookingId && transferBookingId.trim() !== '')
//         payload.booking_id = transferBookingId.trim();
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/parcel-transfer-to-room`,
//         {
//           method: 'POST',
//           headers: { accept: 'application/json', 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Parcel transferred to room successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferRoomNumber('');
//         setTransferBookingId('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.detail || data.message || 'Transfer failed');
//     } catch (error) {
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   const handleTransferToTableSubmit = async () => {
//     if (!transferNewTable) return toast.error('Please enter a table number');
//     setIsTransferring(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/transfer-order`, {
//         method: 'POST',
//         headers: { accept: 'application/json', 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           order_id: transferOrder.order_id,
//           from_table_number: transferOrder.table_number || transferOrder.room_number,
//           to_table_number: transferNewTable,
//         }),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Transferred to table successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferNewTable('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.message || 'Transfer failed');
//     } catch (error) {
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   const handleParcelTransferToTableSubmit = async () => {
//     if (!transferNewTable) return toast.error('Please enter a table number');
//     setIsTransferring(true);
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/restaurant/orders/${transferOrder.order_id}/parcel-transfer-to-table`,
//         {
//           method: 'POST',
//           headers: { accept: 'application/json', 'Content-Type': 'application/json' },
//           body: JSON.stringify({ table_number: transferNewTable }),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Parcel transferred to table successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferNewTable('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.detail || data.message || 'Transfer failed');
//     } catch (error) {
//       toast.error(error.message || 'Transfer failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   const handleMergeBillsSubmit = async () => {
//     if (!transferMergeFrom || !transferMergeTo) return toast.error('Please fill in all fields');
//     if (transferMergeFrom === transferMergeTo)
//       return toast.error('Source and target tables cannot be same');

//     setIsTransferring(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/tables/merge`, {
//         method: 'POST',
//         headers: { accept: 'application/json', 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           from_table_number: transferMergeFrom,
//           to_table_number: transferMergeTo,
//         }),
//       });
//       const data = await response.json();
//       if (response.ok) {
//         toast.success(`✅ ${data.message || 'Merged successfully!'}`);
//         setShowTransferModal(false);
//         setTransferActionType(null);
//         setTransferMergeFrom('');
//         setTransferMergeTo('');
//         setTransferOrder(null);
//         fetchAllOrders();
//       } else toast.error(data.message || 'Merge failed');
//     } catch (error) {
//       toast.error(error.message || 'Merge failed');
//     } finally {
//       setIsTransferring(false);
//     }
//   };

//   // ============ BADGES ============
//   const getStatusBadge = (order) => {
//     const cancelled =
//       order.is_cancelled === true ||
//       order.order_status === 'cancelled' ||
//       order.order_status === 'Cancelled';
//     if (cancelled) {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
//           <FaTimes className="mr-1" size={10} /> Cancelled
//         </span>
//       );
//     }
//     if (order.is_paid) {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
//           <FaCheckCircle className="mr-1" size={10} /> Paid
//         </span>
//       );
//     }
//     if (order.order_status === 'served' || order.order_status === 'Served') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
//           <FaClock className="mr-1" size={10} /> Served
//         </span>
//       );
//     }
//     return (
//       <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
//         <FaClock className="mr-1" size={10} /> Pending
//       </span>
//     );
//   };

//   const getOrderTypeBadge = (type) => {
//     if (type === 'dining') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
//           <FaTable className="mr-1" size={10} /> Dining
//         </span>
//       );
//     } else if (type === 'parcel') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
//           <FaBox className="mr-1" size={10} /> Parcel
//         </span>
//       );
//     } else if (type === 'room') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200">
//           <FaHotel className="mr-1" size={10} /> Room
//         </span>
//       );
//     }
//     return null;
//   };

//   const getPaymentMethodBadge = (method) => {
//     if (!method || method === 'Not Paid' || method === 'Not Paid Yet') {
//       return (
//         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
//           <FaMoneyBill className="mr-1" size={10} /> Not Paid
//         </span>
//       );
//     }
//     const methodColors = {
//       Cash: 'bg-green-100 text-green-800 border-green-200',
//       Netbanking: 'bg-blue-100 text-blue-800 border-blue-200',
//       Card: 'bg-purple-100 text-purple-800 border-purple-200',
//       UPI: 'bg-cyan-100 text-cyan-800 border-cyan-200',
//       Razorpay: 'bg-indigo-100 text-indigo-800 border-indigo-200',
//       Paid: 'bg-green-100 text-green-800 border-green-200',
//     };
//     const colorClass = methodColors[method] || 'bg-gray-100 text-gray-700 border-gray-200';
//     return (
//       <span
//         className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}
//       >
//         <FaMoneyBill className="mr-1" size={10} /> {method}
//       </span>
//     );
//   };

//   // ============ LOADING ============
//   const LoadingSkeleton = () => (
//     <div className="animate-pulse">
//       <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//         <table className="min-w-full">
//           <thead className="bg-gray-50">
//             <tr>
//               {[...Array(10)].map((_, i) => (
//                 <th key={i} className="px-6 py-3">
//                   <div className="h-4 bg-gray-200 rounded"></div>
//                 </th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {[...Array(5)].map((_, i) => (
//               <tr key={i} className="border-t border-gray-100">
//                 {[...Array(10)].map((_, j) => (
//                   <td key={j} className="px-6 py-4">
//                     <div className="h-4 bg-gray-200 rounded w-20"></div>
//                   </td>
//                 ))}
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );

//   // ============ PAGINATION ============
//   const Pagination = () => {
//     if (totalPages <= 1) return null;
//     const pageNumbers = [];
//     const maxVisiblePages = 5;
//     let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
//     let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
//     if (endPage - startPage < maxVisiblePages - 1)
//       startPage = Math.max(1, endPage - maxVisiblePages + 1);
//     for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

//     return (
//       <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl mt-4">
//         <div className="flex-1 flex justify-between sm:hidden">
//           <button
//             onClick={handlePrevPage}
//             disabled={currentPage === 1}
//             className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg disabled:opacity-50"
//           >
//             Previous
//           </button>
//           <button
//             onClick={handleNextPage}
//             disabled={currentPage === totalPages}
//             className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg disabled:opacity-50"
//           >
//             Next
//           </button>
//         </div>
//         <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
//           <div>
//             <p className="text-sm text-gray-700">
//               Page <span className="font-medium">{currentPage}</span> of{' '}
//               <span className="font-medium">{totalPages}</span> (date groups)
//             </p>
//           </div>
//           <div>
//             <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
//               <button
//                 onClick={handlePrevPage}
//                 disabled={currentPage === 1}
//                 className="relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium bg-white border border-gray-300 disabled:opacity-50"
//               >
//                 <FaChevronLeft className="h-4 w-4" />
//               </button>
//               {pageNumbers.map((page) => (
//                 <button
//                   key={page}
//                   onClick={() => handlePageChange(page)}
//                   className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
//                     currentPage === page
//                       ? 'z-10 bg-black text-white border-black'
//                       : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
//                   }`}
//                 >
//                   {page}
//                 </button>
//               ))}
//               <button
//                 onClick={handleNextPage}
//                 disabled={currentPage === totalPages}
//                 className="relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium bg-white border border-gray-300 disabled:opacity-50"
//               >
//                 <FaChevronRight className="h-4 w-4" />
//               </button>
//             </nav>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   // ============================================================
//   // ============ ROOM FOLIO POPUP COMPONENT ====================
//   // ============================================================
//   const RoomFolioModal = () => {
//     if (!showRoomFolioModal) return null;

//     const toggleExpand = (key) => setExpandedFolioKey((prev) => (prev === key ? null : key));

//     return (
//       <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
//         <div className="bg-gray-50 rounded-2xl w-full max-w-6xl max-h-[95vh] flex flex-col shadow-2xl overflow-hidden">
//           {/* HEADER */}
//           <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shrink-0">
//             <div className="flex items-center gap-3">
//               <div className="p-2 bg-gradient-to-br from-rose-500 to-rose-700 rounded-lg text-white">
//                 <FaLayerGroup />
//               </div>
//               <div>
//                 <h2 className="text-xl font-bold text-gray-900">
//                   Room Folio — Consolidated Billing
//                 </h2>
//                 <p className="text-xs text-gray-500">
//                   All room food orders grouped by Room + Booking
//                 </p>
//               </div>
//             </div>
//             <button
//               onClick={() => {
//                 setShowRoomFolioModal(false);
//                 setExpandedFolioKey(null);
//                 if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
//                 setFolioSearchQuery('');
//               }}
//               className="p-2 hover:bg-gray-100 rounded-lg transition-all"
//             >
//               <FaTimes className="w-5 h-5 text-gray-600" />
//             </button>
//           </div>

//           {/* FILTERS BAR */}
//           <div className="bg-white border-b border-gray-200 px-6 py-3 shrink-0">
//             <div className="flex flex-wrap gap-2 items-center">
//               <div className="flex items-center gap-2 flex-1 min-w-[260px]">
//                 <div className="relative flex-1">
//                   <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
//                   <input
//                     ref={folioSearchInputRef}
//                     type="text"
//                     placeholder="Search room, booking, guest, invoice, order id..."
//                     defaultValue=""
//                     onKeyDown={(e) => {
//                       if (e.key === 'Enter') {
//                         setFolioSearchQuery(e.currentTarget.value);
//                       }
//                     }}
//                     className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-black focus:ring-1 focus:ring-black"
//                   />
//                 </div>
//                 <button
//                   onClick={() => {
//                     const val = folioSearchInputRef.current?.value || '';
//                     setFolioSearchQuery(val);
//                   }}
//                   className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium flex items-center gap-1.5 shrink-0"
//                   title="Search"
//                 >
//                   <FaSearch size={12} /> Search
//                 </button>
//               </div>

//               <div className="relative">
//                 <select
//                   value={folioFilterDate}
//                   onChange={(e) => setFolioFilterDate(e.target.value)}
//                   className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm appearance-none pr-8 focus:border-black focus:ring-1 focus:ring-black"
//                 >
//                   <option value="all">📅 All Time</option>
//                   <option value="today">📆 Today</option>
//                   <option value="yesterday">📆 Yesterday</option>
//                   <option value="weekly">📅 This Week</option>
//                   <option value="monthly">📅 This Month</option>
//                   <option value="lastMonth">📅 Last Month</option>
//                   <option value="custom">🗓️ Custom Range</option>
//                 </select>
//                 <FaCalendarAlt
//                   className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
//                   size={12}
//                 />
//               </div>

//               {folioFilterDate === 'custom' && (
//                 <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
//                   <label className="text-xs text-gray-500">From</label>
//                   <input
//                     type="date"
//                     value={folioCustomFrom}
//                     onChange={(e) => setFolioCustomFrom(e.target.value)}
//                     className="bg-transparent text-sm focus:outline-none"
//                   />
//                   <span className="text-gray-400">—</span>
//                   <label className="text-xs text-gray-500">To</label>
//                   <input
//                     type="date"
//                     value={folioCustomTo}
//                     onChange={(e) => setFolioCustomTo(e.target.value)}
//                     className="bg-transparent text-sm focus:outline-none"
//                   />
//                 </div>
//               )}

//               <button
//                 onClick={() => {
//                   if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
//                   setFolioSearchQuery('');
//                   setFolioFilterDate('today');
//                   setFolioCustomFrom('');
//                   setFolioCustomTo('');
//                 }}
//                 className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700"
//               >
//                 Clear
//               </button>

//               <button
//                 onClick={fetchAllOrders}
//                 className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm"
//               >
//                 <FaSync className={loading ? 'animate-spin' : ''} />
//               </button>
//             </div>

//             <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
//               <div className="bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
//                 <p className="text-[10px] uppercase text-rose-600 font-semibold">Rooms</p>
//                 <p className="text-lg font-bold text-rose-900">{folioSummary.rooms}</p>
//               </div>
//               <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
//                 <p className="text-[10px] uppercase text-blue-600 font-semibold">Total Orders</p>
//                 <p className="text-lg font-bold text-blue-900">{folioSummary.ordersCount}</p>
//               </div>
//               <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
//                 <p className="text-[10px] uppercase text-emerald-600 font-semibold">Total Amount</p>
//                 <p className="text-lg font-bold text-emerald-900">
//                   ₹{folioSummary.total.toFixed(2)}
//                 </p>
//               </div>
//               <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
//                 <p className="text-[10px] uppercase text-amber-600 font-semibold">Total Invoices</p>
//                 <p className="text-lg font-bold text-amber-900">{folioSummary.invoicesCount}</p>
//               </div>
//             </div>
//           </div>

//           {/* BODY — FOLIO LIST */}
//           <div className="flex-1 overflow-y-auto p-4">
//             {loading ? (
//               <div className="space-y-3">
//                 {[...Array(3)].map((_, i) => (
//                   <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
//                     <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
//                     <div className="h-3 bg-gray-200 rounded w-1/4"></div>
//                   </div>
//                 ))}
//               </div>
//             ) : roomFolios.length === 0 ? (
//               <div className="text-center py-16 bg-white rounded-xl">
//                 <FaHotel className="text-5xl text-gray-300 mx-auto mb-4" />
//                 <h3 className="text-lg font-semibold text-gray-700 mb-1">No Room Folios Found</h3>
//                 <p className="text-sm text-gray-400">No room orders match the selected filters</p>
//               </div>
//             ) : (
//               <div className="space-y-3">
//                 {roomFolios.map((folio) => {
//                   const isOpen = expandedFolioKey === folio.key;

//                   return (
//                     <div
//                       key={folio.key}
//                       className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
//                     >
//                       <div
//                         onClick={() => toggleExpand(folio.key)}
//                         className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-all"
//                       >
//                         <div className="flex items-center gap-4 flex-1 min-w-0">
//                           <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500 to-rose-700 text-white flex flex-col items-center justify-center shrink-0 shadow-sm">
//                             <FaHotel size={12} />
//                             <span className="text-sm font-bold leading-tight">
//                               {folio.room_number || '—'}
//                             </span>
//                           </div>

//                           <div className="min-w-0 flex-1">
//                             <div className="flex items-center gap-2 flex-wrap">
//                               <span className="font-bold text-gray-900">
//                                 Room {folio.room_number || 'N/A'}
//                               </span>
//                               {folio.booking_id && (
//                                 <span className="text-[11px] text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
//                                   {folio.booking_id}
//                                 </span>
//                               )}
//                             </div>

//                             <div className="text-sm text-gray-600 truncate mt-0.5">
//                               {folio.customer_name}
//                               {folio.customer_phone && (
//                                 <span className="text-gray-400"> · {folio.customer_phone}</span>
//                               )}
//                             </div>

//                             {folio.invoice_label && (
//                               <div className="mt-1 flex items-center gap-1 text-[11px]">
//                                 <FaFileInvoice className="text-gray-400" size={10} />
//                                 <span className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
//                                   {folio.invoice_label}
//                                 </span>
//                                 <span className="text-gray-400">
//                                   ({folio.invoice_numbers.length} invoice
//                                   {folio.invoice_numbers.length !== 1 ? 's' : ''})
//                                 </span>
//                               </div>
//                             )}
//                           </div>
//                         </div>

//                         <div className="flex items-center gap-5 shrink-0">
//                           <div className="text-right hidden sm:block">
//                             <div className="text-xs text-gray-500">
//                               {folio.order_count} order{folio.order_count !== 1 ? 's' : ''}
//                             </div>
//                             <div className="text-[11px] text-gray-400">
//                               {formatOrderDate(folio.earliest_time).time} —{' '}
//                               {formatOrderDate(folio.latest_time).time}
//                             </div>
//                           </div>

//                           <div className="text-right">
//                             <div className="text-lg font-bold text-gray-900">
//                               ₹{folio.total_amount.toFixed(2)}
//                             </div>
//                           </div>

//                           <div
//                             className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform ${
//                               isOpen
//                                 ? 'bg-black text-white rotate-180'
//                                 : 'bg-gray-100 text-gray-600'
//                             }`}
//                           >
//                             <FaChevronDown size={12} />
//                           </div>
//                         </div>
//                       </div>

//                       {isOpen && (
//                         <div className="border-t border-gray-100 bg-gray-50/70">
//                           <div className="overflow-x-auto">
//                             <table className="w-full">
//                               <thead className="bg-gray-100">
//                                 <tr>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     #
//                                   </th>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     Time
//                                   </th>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     Order ID
//                                   </th>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     Invoice No.
//                                   </th>
//                                   <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">
//                                     Items
//                                   </th>
//                                   <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase">
//                                     Amount
//                                   </th>
//                                   <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-600 uppercase">
//                                     Status
//                                   </th>
//                                   <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-600 uppercase">
//                                     Actions
//                                   </th>
//                                 </tr>
//                               </thead>
//                               <tbody className="bg-white">
//                                 {folio.orders.map((o, idx) => {
//                                   const invNo =
//                                     o.invoice_number ||
//                                     o.bill_number ||
//                                     o.billing?.invoice_number ||
//                                     o.billing?.bill_number ||
//                                     o.billings?.[0]?.invoice_number ||
//                                     o.billings?.[0]?.bill_number ||
//                                     '—';
//                                   return (
//                                     <tr key={o.order_id} className="border-t border-gray-100">
//                                       <td className="px-3 py-2 text-xs text-gray-500">{idx + 1}</td>
//                                       <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
//                                         {formatOrderDate(o.ordered_at).time}
//                                       </td>
//                                       <td className="px-3 py-2 text-xs font-mono text-gray-600">
//                                         {String(o.order_id).slice(0, 14)}…
//                                       </td>
//                                       <td className="px-3 py-2 text-xs font-mono text-gray-700">
//                                         {invNo !== '—' ? (
//                                           <span className="bg-gray-100 px-1.5 py-0.5 rounded">
//                                             {invNo}
//                                           </span>
//                                         ) : (
//                                           <span className="text-gray-400">Not issued</span>
//                                         )}
//                                       </td>
//                                       <td className="px-3 py-2 text-xs text-gray-600">
//                                         {o.items?.length || 0} item
//                                         {o.items?.length !== 1 ? 's' : ''}
//                                       </td>
//                                       <td className="px-3 py-2 text-xs font-semibold text-right text-gray-900">
//                                         ₹{(parseFloat(o.total_amount) || 0).toFixed(2)}
//                                       </td>
//                                       <td className="px-3 py-2 text-center">{getStatusBadge(o)}</td>
//                                       <td className="px-3 py-2 text-center">
//                                         <div className="flex items-center justify-center gap-1">
//                                           <button
//                                             onClick={(e) => {
//                                               e.stopPropagation();
//                                               handleDownloadPDF(o);
//                                             }}
//                                             className="p-1.5 rounded hover:bg-purple-50 text-purple-600"
//                                             title="Download Invoice"
//                                           >
//                                             <FaDownload size={13} />
//                                           </button>
//                                           <button
//                                             onClick={(e) => {
//                                               e.stopPropagation();
//                                               handlePrintInvoice(o);
//                                             }}
//                                             className="p-1.5 rounded hover:bg-amber-50 text-amber-600"
//                                             title="Print Invoice"
//                                           >
//                                             <FaPrint size={13} />
//                                           </button>
//                                         </div>
//                                       </td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                               <tfoot className="bg-gray-100 border-t-2 border-gray-300">
//                                 <tr>
//                                   <td
//                                     colSpan={5}
//                                     className="px-3 py-3 text-right text-sm font-bold text-gray-800"
//                                   >
//                                     Consolidated Total ({folio.order_count} orders)
//                                   </td>
//                                   <td className="px-3 py-3 text-right text-base font-bold text-gray-900">
//                                     ₹{folio.total_amount.toFixed(2)}
//                                   </td>
//                                   <td colSpan={2}></td>
//                                 </tr>
//                               </tfoot>
//                             </table>
//                           </div>

//                           <div className="flex flex-wrap gap-2 p-3 border-t border-gray-200 bg-white">
//                             <button
//                               onClick={async (e) => {
//                                 e.stopPropagation();
//                                 try {
//                                   const range = getConsolidatedDateRange();
//                                   const params = new URLSearchParams({
//                                     booking_id: folio.booking_id,
//                                     date_filter: mapDateFilterForApi(range.filter),
//                                   });
//                                   if (folio.room_number)
//                                     params.append('room_number', folio.room_number);

//                                   if (range.filter === 'custom') {
//                                     if (range.from)
//                                       params.append('date_from', `${range.from}T00:00:00`);
//                                     if (range.to) params.append('date_to', `${range.to}T23:59:59`);
//                                   }

//                                   const url = `${BQ_BASE_URL}/bq/api/food-invoice/consolidated/pdf?${params.toString()}`;
//                                   const res = await fetch(url);
//                                   if (!res.ok) {
//                                     const err = await res.json().catch(() => ({}));
//                                     throw new Error(
//                                       err.detail || 'Failed to generate consolidated PDF'
//                                     );
//                                   }
//                                   const blob = await res.blob();
//                                   const a = document.createElement('a');
//                                   a.href = window.URL.createObjectURL(blob);
//                                   a.download = `Consolidated_Invoice_Room${folio.room_number || ''}_${folio.booking_id}_${folioFilterDate}.pdf`;
//                                   document.body.appendChild(a);
//                                   a.click();
//                                   window.URL.revokeObjectURL(a.href);
//                                   document.body.removeChild(a);
//                                   toast.success('Consolidated invoice downloaded');
//                                 } catch (err) {
//                                   console.error(err);
//                                   toast.error(err.message);
//                                 }
//                               }}
//                               className="px-3 py-2 bg-black text-white rounded-lg text-xs font-medium hover:bg-gray-800 flex items-center gap-1.5"
//                             >
//                               <FaFileInvoice size={12} /> Download Consolidated Invoice
//                             </button>

//                             <button
//                               onClick={async (e) => {
//                                 e.stopPropagation();
//                                 try {
//                                   const range = getConsolidatedDateRange();
//                                   const params = new URLSearchParams({
//                                     booking_id: folio.booking_id,
//                                     date_filter: mapDateFilterForApi(range.filter),
//                                   });
//                                   if (folio.room_number)
//                                     params.append('room_number', folio.room_number);

//                                   if (range.filter === 'custom') {
//                                     if (range.from)
//                                       params.append('date_from', `${range.from}T00:00:00`);
//                                     if (range.to) params.append('date_to', `${range.to}T23:59:59`);
//                                   }

//                                   const url = `${BQ_BASE_URL}/bq/api/food/print/consolidated/print?${params.toString()}`;
//                                   const res = await fetch(url, { method: 'POST' });
//                                   const data = await res.json();

//                                   if (res.ok && data.queued) {
//                                     toast.success(
//                                       `Consolidated slip sent to printer (${data.bills_included?.length || 0} bills)`
//                                     );
//                                   } else {
//                                     toast.error(data.detail || 'Print failed');
//                                   }
//                                 } catch (err) {
//                                   console.error(err);
//                                   toast.error('Failed to print consolidated invoice');
//                                 }
//                               }}
//                               className="px-3 py-2 border border-gray-200 rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1.5"
//                             >
//                               <FaPrint size={12} /> Print Consolidated
//                             </button>
//                           </div>
//                         </div>
//                       )}
//                     </div>
//                   );
//                 })}
//               </div>
//             )}
//           </div>

//           <div className="bg-white border-t border-gray-200 px-6 py-3 flex justify-between items-center shrink-0">
//             <p className="text-xs text-gray-500">
//               Showing <span className="font-semibold">{roomFolios.length}</span> room folio
//               {roomFolios.length !== 1 ? 's' : ''}
//             </p>
//             <button
//               onClick={() => {
//                 setShowRoomFolioModal(false);
//                 setExpandedFolioKey(null);
//                 if (folioSearchInputRef.current) folioSearchInputRef.current.value = '';
//                 setFolioSearchQuery('');
//               }}
//               className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <style>{`
//         .action-btn {
//           padding: 6px;
//           border-radius: 6px;
//           transition: all 0.2s;
//           display: inline-flex;
//           align-items: center;
//           justify-content: center;
//           background: transparent;
//           border: none;
//           cursor: pointer;
//           position: relative;
//         }
//         .action-btn:hover { background: #f3f4f6; transform: scale(1.1); }
//         .action-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
//         .action-btn.view-btn { color: #3b82f6; }
//         .action-btn.download-btn { color: #8b5cf6; }
//         .action-btn.print-btn { color: #f59e0b; }
//         .action-btn.kot-btn { color: #8B5CF6; }
//         .action-btn.email-btn { color: #ef4444; }
//         .action-btn.payment-btn { color: #10b981; }
//         .action-btn.edit-btn { color: #6b7280; }
//         .action-btn.transfer-btn { color: #8B5CF6; }
//         .action-btn.merge-btn { color: #EC4899; }
//       `}</style>
//       <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
//         <header className="flex justify-between items-center mb-3 bg-white p-3">
//           <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
//           <div className="flex space-x-6">
//             <button
//               onClick={() =>
//                 navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')
//               }
//               className="btn"
//             >
//               Order Overview
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
//               className="btn"
//             >
//               Order Food
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
//               className="btn"
//             >
//               Dining Tables
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
//               className="btn"
//             >
//               Kitchen Display System(KDS)
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
//               className="btn font-bold text-black"
//             >
//               Billing & Invoicing
//             </button>
//           </div>
//         </header>

//         {/* Stats */}
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Total Revenue</p>
//             <p className="text-2xl font-bold text-gray-900">
//               ₹{filteredOrders.reduce((s, o) => s + (o.total_amount || 0), 0).toFixed(2)}
//             </p>
//           </div>
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Total Orders</p>
//             <p className="text-2xl font-bold text-gray-900">{filteredOrders.length}</p>
//           </div>
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Pending</p>
//             <p className="text-2xl font-bold text-amber-600">
//               {
//                 filteredOrders.filter(
//                   (o) =>
//                     !o.is_cancelled &&
//                     !o.is_paid &&
//                     (o.order_status === 'pending' || o.order_status === 'Pending')
//                 ).length
//               }
//             </p>
//           </div>
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Served</p>
//             <p className="text-2xl font-bold text-blue-600">
//               {
//                 filteredOrders.filter(
//                   (o) =>
//                     !o.is_cancelled && (o.order_status === 'served' || o.order_status === 'Served')
//                 ).length
//               }
//             </p>
//           </div>
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
//             <p className="text-sm text-gray-500">Paid</p>
//             <p className="text-2xl font-bold text-green-600">
//               {filteredOrders.filter((o) => o.is_paid).length}
//             </p>
//           </div>
//         </div>

//         {/* Filters */}
//         <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
//           <div className="flex flex-col sm:flex-row gap-4">
//             <div className="flex-1 relative">
//               <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
//               <input
//                 type="text"
//                 placeholder="Search by Serial No, Order ID, Invoice No, Customer, Table, Room, Parcel, KOT, Booking..."
//                 value={searchQuery}
//                 onChange={(e) => setSearchQuery(e.target.value)}
//                 className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black"
//               />
//             </div>
//             <div className="flex flex-wrap gap-3 items-center">
//               <button
//                 onClick={() => {
//                   setShowRoomFolioModal(true);
//                   setExpandedFolioKey(null);
//                   setFolioSearchQuery('');
//                 }}
//                 className="px-4 py-2 bg-gradient-to-r from-rose-600 to-rose-700 text-white rounded-lg hover:from-rose-700 hover:to-rose-800 transition-all flex items-center gap-2 text-sm font-semibold shadow-sm"
//                 title="Open consolidated room billing"
//               >
//                 <FaLayerGroup />
//                 Room Folio
//                 <span className="bg-white/25 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
//                   {orders.filter((o) => o.order_type === 'room').length}
//                 </span>
//               </button>

//               <div className="relative">
//                 <select
//                   value={filterDate}
//                   onChange={(e) => setFilterDate(e.target.value)}
//                   className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm min-w-[150px]"
//                 >
//                   <option value="all">📅 All Time</option>
//                   <option value="today">📆 Today</option>
//                   <option value="yesterday">📆 Yesterday</option>
//                   <option value="weekly">📅 This Week</option>
//                   <option value="monthly">📅 This Month</option>
//                   <option value="lastMonth">📅 Last Month</option>
//                   <option value="custom">🗓️ Custom Range</option>
//                 </select>
//                 <FaCalendarAlt
//                   className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
//                   size={12}
//                 />
//               </div>

//               {filterDate === 'custom' && (
//                 <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
//                   <label className="text-xs text-gray-500 font-medium">From</label>
//                   <input
//                     type="date"
//                     value={customDateFrom}
//                     onChange={(e) => setCustomDateFrom(e.target.value)}
//                     className="bg-transparent text-sm focus:outline-none"
//                   />
//                   <span className="text-gray-400">—</span>
//                   <label className="text-xs text-gray-500 font-medium">To</label>
//                   <input
//                     type="date"
//                     value={customDateTo}
//                     onChange={(e) => setCustomDateTo(e.target.value)}
//                     className="bg-transparent text-sm focus:outline-none"
//                   />
//                 </div>
//               )}

//               <select
//                 value={filterType}
//                 onChange={(e) => setFilterType(e.target.value)}
//                 className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm"
//               >
//                 <option value="all">All Types</option>
//                 <option value="dining">Dining</option>
//                 <option value="parcel">Parcel</option>
//                 <option value="room">Room</option>
//               </select>

//               <select
//                 value={filterStatus}
//                 onChange={(e) => setFilterStatus(e.target.value)}
//                 className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-black appearance-none pr-8 text-sm"
//               >
//                 <option value="all">All Status</option>
//                 <option value="pending">Pending</option>
//                 <option value="served">Served</option>
//                 <option value="unpaid">Unpaid</option>
//                 <option value="paid">Paid</option>
//                 <option value="cancelled">Cancelled</option>
//               </select>

//               <button
//                 onClick={() => {
//                   setSearchQuery('');
//                   setFilterType('all');
//                   setFilterStatus('all');
//                   setFilterDate('today');
//                   setCustomDateFrom('');
//                   setCustomDateTo('');
//                 }}
//                 className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700"
//               >
//                 Clear
//               </button>

//               <button
//                 onClick={fetchAllOrders}
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm"
//               >
//                 <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Table */}
//         {loading ? (
//           <LoadingSkeleton />
//         ) : filteredOrders.length === 0 ? (
//           <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
//             <FaFileInvoice className="text-5xl text-gray-300 mx-auto mb-4" />
//             <h3 className="text-xl font-semibold text-gray-700 mb-2">No Orders Found</h3>
//             <p className="text-gray-400">No orders match your current filters</p>
//           </div>
//         ) : (
//           <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
//             <table className="min-w-full divide-y divide-gray-200">
//               <thead className="bg-gray-50">
//                 <tr>
//                   {['S.No', 'OrderID / Invoice No.', 'Customer', 'Type', 'No.', 'Date / Time'].map(
//                     (h) => (
//                       <th
//                         key={h}
//                         className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
//                       >
//                         {h}
//                       </th>
//                     )
//                   )}
//                   <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
//                     Total
//                   </th>
//                   <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
//                     Payment
//                   </th>
//                   <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
//                     Status
//                   </th>
//                   <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
//                     Actions
//                   </th>
//                 </tr>
//               </thead>
//               <tbody className="bg-white">
//                 {getCurrentPageGroups().map(({ dateKey, orders: dateOrders }) => (
//                   <React.Fragment key={dateKey}>
//                     <tr className="bg-gray-100 border-y border-gray-200">
//                       <td colSpan={10} className="px-4 py-2">
//                         <div className="flex items-center justify-between">
//                           <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
//                             <FaCalendarAlt className="text-gray-500" size={12} />
//                             {getDateHeaderLabel(dateKey)}
//                           </span>
//                           <span className="text-xs text-gray-500">
//                             {dateOrders.length} order{dateOrders.length !== 1 ? 's' : ''}
//                           </span>
//                         </div>
//                       </td>
//                     </tr>
//                     {dateOrders.map((order) => {
//                       const invNo = getOrderInvoiceNumber(order);
//                       return (
//                         <tr
//                           key={order.order_id}
//                           className={`hover:bg-gray-50 transition-colors border-b border-gray-100 ${
//                             order.is_cancelled ? 'bg-red-50/40 opacity-80' : ''
//                           }`}
//                         >
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             <span className="text-sm font-bold text-gray-900">
//                               {order.serial_number || '—'}
//                             </span>
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             {/* Invoice number shown above the order id */}
//                             <div className="mb-1">
//                               {invNo ? (
//                                 <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
//                                   {invNo}
//                                 </span>
//                               ) : (
//                                 <span className="text-[11px] text-gray-400 italic">
//                                   Invoice not issued
//                                 </span>
//                               )}
//                             </div>
//                             <div className="text-xs font-mono text-gray-600">
//                               {String(order.order_id).slice(0, 20)}
//                             </div>
//                             {order.booking_id && (
//                               <div className="text-xs text-gray-400 mt-0.5">
//                                 Booking: {order.booking_id}
//                               </div>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 align-top">
//                             <div className="text-sm font-medium text-gray-900">
//                               {order.customer_name || 'Guest'}
//                             </div>
//                             {order.customer_phone && (
//                               <div className="text-xs text-gray-500">{order.customer_phone}</div>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             {getOrderTypeBadge(order.order_type)}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             {order.table_number ? (
//                               <span className="text-sm text-gray-700">
//                                 Table {order.table_number}
//                               </span>
//                             ) : order.room_number ? (
//                               <span className="text-sm text-gray-700">
//                                 Room {order.room_number}
//                               </span>
//                             ) : order.parcel_number ? (
//                               <span className="text-sm text-gray-700 font-mono">
//                                 {order.parcel_number}
//                               </span>
//                             ) : (
//                               <span className="text-sm text-gray-400">—</span>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap align-top">
//                             {order.ordered_at ? (
//                               <>
//                                 <div className="text-sm text-gray-700">
//                                   {formatOrderDate(order.ordered_at).date}
//                                 </div>
//                                 <div className="text-xs font-semibold text-gray-500">
//                                   {formatOrderDate(order.ordered_at).time}
//                                 </div>
//                               </>
//                             ) : (
//                               <span className="text-sm text-gray-400">—</span>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap text-right align-top">
//                             <div className="text-sm font-bold text-gray-900">
//                               ₹{order.total_amount?.toFixed(2) || 0}
//                             </div>
//                             {order.items && (
//                               <div className="text-xs text-gray-400">
//                                 {order.items.length} items
//                               </div>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap text-center align-top">
//                             {getPaymentMethodBadge(order.payment_method)}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap text-center align-top">
//                             {getStatusBadge(order)}
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap text-center align-top">
//                             <div className="flex items-center justify-center gap-1 flex-wrap">
//                               <button
//                                 onClick={() => handleViewInvoice(order)}
//                                 disabled={
//                                   isLoadingInvoice && selectedOrder?.order_id === order.order_id
//                                 }
//                                 className="action-btn view-btn"
//                                 title="View Invoice"
//                               >
//                                 {isLoadingInvoice && selectedOrder?.order_id === order.order_id ? (
//                                   <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
//                                 ) : (
//                                   <FaEye size={16} />
//                                 )}
//                               </button>

//                               <button
//                                 onClick={() => handlePrintKOT(order)}
//                                 disabled={isPrintingKOT}
//                                 className="action-btn kot-btn"
//                                 title="Print KOT"
//                               >
//                                 {isPrintingKOT ? (
//                                   <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
//                                 ) : (
//                                   <FaUtensils size={16} />
//                                 )}
//                               </button>

//                               {order.order_type !== 'room' && (
//                                 <button
//                                   onClick={() => handleOpenEditGuest(order)}
//                                   className="action-btn edit-btn"
//                                   title="Edit Guest"
//                                 >
//                                   <FaEdit size={16} />
//                                 </button>
//                               )}

//                               {(order.order_type === 'dining' || order.order_type === 'parcel') && (
//                                 <button
//                                   onClick={() => {
//                                     setTransferOrder(order);
//                                     setTransferActionType(null);
//                                     setTransferRoomNumber('');
//                                     setTransferBookingId('');
//                                     setTransferNewTable('');
//                                     setTransferMergeFrom('');
//                                     setTransferMergeTo('');
//                                     setShowTransferModal(true);
//                                   }}
//                                   className="action-btn transfer-btn"
//                                   title="Transfer"
//                                 >
//                                   <FaExchangeAlt size={16} />
//                                 </button>
//                               )}

//                               <button
//                                 onClick={() => handleDownloadPDF(order)}
//                                 className="action-btn download-btn"
//                                 title="Download PDF"
//                               >
//                                 <FaDownload size={16} />
//                               </button>

//                               <button
//                                 onClick={() => handlePrintInvoice(order)}
//                                 disabled={isPrinting}
//                                 className="action-btn print-btn"
//                                 title="Print Invoice"
//                               >
//                                 <FaPrint size={16} />
//                               </button>

//                               <button
//                                 onClick={() => {
//                                   setSelectedOrder(order);
//                                   setShowEmailModal(true);
//                                 }}
//                                 className="action-btn email-btn"
//                                 title="Email"
//                               >
//                                 <FaEnvelope size={16} />
//                               </button>

//                               {!order.is_paid &&
//                                 order.has_billing !== false &&
//                                 !order.is_cancelled && (
//                                   <button
//                                     onClick={() => {
//                                       setSelectedOrder(order);
//                                       setSelectedPaymentMethod(null);
//                                       setShowPaymentModal(true);
//                                     }}
//                                     className="action-btn payment-btn"
//                                     title="Make Payment"
//                                   >
//                                     <FaCreditCard size={16} />
//                                   </button>
//                                 )}
//                             </div>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </React.Fragment>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {!loading && filteredOrders.length > 0 && <Pagination />}

//         {/* ========== ROOM FOLIO POPUP ========== */}
//         <RoomFolioModal />

//         {/* ========== EDIT GUEST ========== */}
//         {showEditGuestModal && selectedOrder && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
//               <div className="flex justify-between items-center mb-6">
//                 <h2 className="text-2xl font-bold flex items-center gap-3">
//                   <FaUser className="text-gray-700" /> Edit Guest Details
//                 </h2>
//                 <button
//                   onClick={() => {
//                     setShowEditGuestModal(false);
//                     setEditGuestData({
//                       customer_name: '',
//                       customer_phone: '',
//                       customer_email: '',
//                       gst_number: '',
//                     });
//                     setCompanySearchText('');
//                     setSelectedCompany(null);
//                     setShowCompanyDropdown(false);
//                   }}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>

//               <div className="space-y-4">
//                 <div className="relative" ref={companyDropdownRef}>
//                   <div className="relative">
//                     <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm z-10" />
//                     <input
//                       type="text"
//                       placeholder="Search company name..."
//                       value={companySearchText}
//                       onChange={(e) => {
//                         setCompanySearchText(e.target.value);
//                         setSelectedCompany(null);
//                         if (!e.target.value) setEditGuestData((p) => ({ ...p, gst_number: '' }));
//                       }}
//                       onFocus={() => {
//                         if (companySearchText.length > 1) setShowCompanyDropdown(true);
//                       }}
//                       className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-black"
//                     />
//                   </div>
//                   {showCompanyDropdown && filteredCompanies.length > 0 && (
//                     <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
//                       {filteredCompanies.map((c) => (
//                         <button
//                           key={c.id}
//                           onClick={() => handleCompanySelect(c)}
//                           className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
//                         >
//                           <div className="flex justify-between items-center">
//                             <div>
//                               <div className="font-medium text-sm">{c.companyname}</div>
//                               {c.companyid && (
//                                 <div className="text-xs text-gray-400">ID: {c.companyid}</div>
//                               )}
//                             </div>
//                             {c.gstnumber && (
//                               <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
//                                 GST: {c.gstnumber}
//                               </span>
//                             )}
//                           </div>
//                         </button>
//                       ))}
//                     </div>
//                   )}
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Customer Name *
//                   </label>
//                   <input
//                     type="text"
//                     name="customer_name"
//                     value={editGuestData.customer_name}
//                     onChange={handleEditGuestChange}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Phone Number
//                   </label>
//                   <input
//                     type="tel"
//                     name="customer_phone"
//                     value={editGuestData.customer_phone}
//                     onChange={handleEditGuestChange}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Email Address
//                   </label>
//                   <input
//                     type="email"
//                     name="customer_email"
//                     value={editGuestData.customer_email}
//                     onChange={handleEditGuestChange}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
//                   <input
//                     type="text"
//                     name="gst_number"
//                     value={editGuestData.gst_number}
//                     onChange={handleEditGuestChange}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                   {selectedCompany && (
//                     <p className="text-xs text-green-600 mt-1">
//                       ✓ GST auto-filled from {selectedCompany.companyname}
//                     </p>
//                   )}
//                 </div>

//                 <div className="flex gap-3 pt-4">
//                   <button
//                     onClick={() => {
//                       setShowEditGuestModal(false);
//                       setEditGuestData({
//                         customer_name: '',
//                         customer_phone: '',
//                         customer_email: '',
//                         gst_number: '',
//                       });
//                       setCompanySearchText('');
//                       setSelectedCompany(null);
//                       setShowCompanyDropdown(false);
//                     }}
//                     className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={handleUpdateGuestDetails}
//                     disabled={isUpdatingGuest || !editGuestData.customer_name.trim()}
//                     className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                       isUpdatingGuest || !editGuestData.customer_name.trim()
//                         ? 'opacity-50 cursor-not-allowed'
//                         : ''
//                     }`}
//                   >
//                     {isUpdatingGuest ? (
//                       <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                     ) : (
//                       'Update Details'
//                     )}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ========== INVOICE MODAL ========== */}
//         {showInvoiceModal && invoiceData && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 overflow-y-auto">
//             <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
//               <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
//                 <h2 className="text-xl font-bold flex items-center gap-3">
//                   <div className="p-2 bg-black rounded-lg text-white">
//                     <FaFileInvoice />
//                   </div>
//                   Invoice Details{' '}
//                   {isRoomOrder && (
//                     <span className="text-sm font-normal text-gray-500">(Room Order)</span>
//                   )}
//                   {invoiceData.invoice_number && (
//                     <span className="text-sm font-normal text-gray-500 ml-2">
//                       #{invoiceData.invoice_number}
//                     </span>
//                   )}
//                 </h2>
//                 <button
//                   onClick={handleCloseInvoiceModal}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>
//               <div className="p-6">
//                 <div className="border-b border-gray-200 pb-4 mb-4">
//                   <div className="flex flex-col md:flex-row justify-between items-start gap-4">
//                     <div>
//                       <h3 className="text-2xl font-bold text-gray-900">
//                         {invoiceData.property_name || 'R&T INTERNATIONAL'}
//                       </h3>
//                       <p className="text-sm text-gray-600 mt-1">{invoiceData.property_address}</p>
//                       <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
//                         <span>GST: {invoiceData.property_gst}</span>
//                         <span>Contact: {invoiceData.property_contact}</span>
//                       </div>
//                     </div>
//                     <div className="text-right bg-gray-50 px-6 py-4 rounded-lg">
//                       <p className="text-2xl font-bold text-black">INVOICE</p>
//                       <p className="text-sm font-semibold text-gray-700">
//                         # {invoiceData.invoice_number}
//                       </p>
//                       <p className="text-sm text-gray-500">
//                         {new Date(invoiceData.invoice_date).toLocaleDateString('en-GB')}{' '}
//                         {new Date(invoiceData.invoice_date).toLocaleTimeString('en-US', {
//                           hour: '2-digit',
//                           minute: '2-digit',
//                           hour12: true,
//                         })}
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//                   <div className="bg-gray-50 rounded-lg p-4">
//                     <p className="text-sm font-semibold text-gray-700 mb-2">Customer Details</p>
//                     <div className="space-y-1 text-sm">
//                       <p className="text-gray-600">Name: {invoiceData.customer_name || 'Guest'}</p>
//                       <p className="text-gray-600">Phone: {invoiceData.customer_phone || '-'}</p>
//                       <p className="text-gray-600">Email: {invoiceData.customer_email || '-'}</p>
//                       <p className="text-gray-600">GST: {invoiceData.customer_gst || 'N/A'}</p>
//                     </div>
//                   </div>
//                   <div className="bg-gray-50 rounded-lg p-4">
//                     <p className="text-sm font-semibold text-gray-700 mb-2">Order Details</p>
//                     <div className="space-y-1 text-sm">
//                       <p className="text-gray-600 font-mono text-xs">ID: {invoiceData.order_id}</p>
//                       <p className="text-gray-600 capitalize">Type: {invoiceData.order_type}</p>
//                       {invoiceData.room_number && (
//                         <p className="text-gray-600">Room: {invoiceData.room_number}</p>
//                       )}
//                       {invoiceData.booking_id && (
//                         <p className="text-gray-600">Booking: {invoiceData.booking_id}</p>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//                 {invoiceData.items && invoiceData.items.length > 0 && (
//                   <div className="overflow-x-auto mb-4 border border-gray-200 rounded-lg">
//                     <table className="w-full">
//                       <thead className="bg-gray-50">
//                         <tr>
//                           <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">
//                             Item
//                           </th>
//                           <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">
//                             Qty
//                           </th>
//                           <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">
//                             Unit Price
//                           </th>
//                           <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">
//                             Amount
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {invoiceData.items.map((item, index) => {
//                           const unitPrice = parseFloat(item.unit_price) || 0;
//                           const totalAmount = parseFloat(item.total_amount) || 0;
//                           const quantity = parseInt(item.quantity) || 0;
//                           const isCancelled =
//                             item.is_cancelled === true ||
//                             item.status === 'cancelled' ||
//                             item.status === 'Cancelled';
//                           return (
//                             <tr
//                               key={index}
//                               className={`border-t border-gray-100 ${isCancelled ? 'bg-red-50/50' : ''}`}
//                             >
//                               <td className="py-2 px-3 text-sm">
//                                 <span className={isCancelled ? 'line-through text-red-500' : ''}>
//                                   {item.description || item.food_name || 'Item'}
//                                 </span>
//                               </td>
//                               <td
//                                 className={`text-center py-2 px-3 text-sm ${isCancelled ? 'line-through text-red-400' : ''}`}
//                               >
//                                 {quantity}
//                               </td>
//                               <td
//                                 className={`text-right py-2 px-3 text-sm ${isCancelled ? 'line-through text-red-400' : ''}`}
//                               >
//                                 ₹{unitPrice.toFixed(2)}
//                               </td>
//                               <td
//                                 className={`text-right py-2 px-3 text-sm font-semibold ${isCancelled ? 'line-through text-red-400' : ''}`}
//                               >
//                                 ₹{totalAmount.toFixed(2)}
//                               </td>
//                             </tr>
//                           );
//                         })}
//                       </tbody>
//                     </table>
//                   </div>
//                 )}
//                 <div className="border-t border-gray-200 pt-4">
//                   <div className="flex justify-end">
//                     <div className="w-64 space-y-1">
//                       <div className="flex justify-between py-1">
//                         <span className="text-sm text-gray-600">Subtotal</span>
//                         <span className="text-sm font-medium text-gray-900">
//                           ₹{(parseFloat(invoiceData.subtotal) || 0).toFixed(2)}
//                         </span>
//                       </div>
//                       <div className="flex justify-between py-1">
//                         <span className="text-sm text-gray-600">Tax</span>
//                         <span className="text-sm font-medium text-gray-900">
//                           ₹{(parseFloat(invoiceData.total_tax) || 0).toFixed(2)}
//                         </span>
//                       </div>
//                       <div className="border-t-2 border-gray-300 pt-2 mt-1">
//                         <div className="flex justify-between">
//                           <span className="text-lg font-bold text-gray-900">Grand Total</span>
//                           <span className="text-2xl font-bold text-black">
//                             ₹{(parseFloat(invoiceData.grand_total) || 0).toFixed(2)}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ========== PAYMENT MODAL ========== */}
//         {showPaymentModal && selectedOrder && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
//               <div className="flex justify-between items-center mb-6">
//                 <h2 className="text-2xl font-bold">Make Payment</h2>
//                 <button
//                   onClick={() => {
//                     setShowPaymentModal(false);
//                     setSelectedPaymentMethod(null);
//                   }}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>
//               <div className="space-y-6">
//                 <div className="bg-gray-50 p-4 rounded-lg">
//                   <p className="text-sm text-gray-600">Order ID</p>
//                   <p className="font-mono text-sm font-semibold">
//                     {String(selectedOrder.order_id).slice(0, 12)}...
//                   </p>
//                   {selectedOrder.room_number && (
//                     <p className="text-sm text-gray-600 mt-1">Room: {selectedOrder.room_number}</p>
//                   )}
//                   <p className="text-sm text-gray-600 mt-3">Total Amount</p>
//                   <p className="text-3xl font-bold text-black">
//                     ₹{selectedOrder.total_amount?.toFixed(2) || 0}
//                   </p>
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-3">
//                     Select Payment Method
//                   </label>
//                   <div className="grid grid-cols-2 gap-3">
//                     <button
//                       onClick={() => handlePaymentOption('cash')}
//                       className={`py-3 px-4 rounded-lg border-2 transition-all ${
//                         selectedPaymentMethod === 'cash'
//                           ? 'border-black bg-black text-white'
//                           : 'border-gray-200 hover:border-gray-400'
//                       }`}
//                     >
//                       <FaMoneyBill className="text-xl mx-auto mb-1" />
//                       <span className="text-sm">Cash</span>
//                     </button>
//                     <button
//                       onClick={() => handlePaymentOption('razorpay')}
//                       className={`py-3 px-4 rounded-lg border-2 transition-all ${
//                         selectedPaymentMethod === 'razorpay'
//                           ? 'border-black bg-black text-white'
//                           : 'border-gray-200 hover:border-gray-400'
//                       }`}
//                     >
//                       <FaDigitalOcean className="text-xl mx-auto mb-1" />
//                       <span className="text-sm">Digital</span>
//                     </button>
//                   </div>
//                 </div>
//                 <div className="flex gap-3 pt-4">
//                   <button
//                     onClick={() => {
//                       setShowPaymentModal(false);
//                       setSelectedPaymentMethod(null);
//                     }}
//                     className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={confirmPayment}
//                     disabled={!selectedPaymentMethod || isProcessingPayment}
//                     className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                       !selectedPaymentMethod || isProcessingPayment
//                         ? 'opacity-50 cursor-not-allowed'
//                         : ''
//                     }`}
//                   >
//                     {isProcessingPayment ? (
//                       <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                     ) : (
//                       'Pay Now'
//                     )}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ========== EMAIL MODAL ========== */}
//         {showEmailModal && selectedOrder && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
//               <div className="flex justify-between items-center mb-6">
//                 <h2 className="text-2xl font-bold">Send Invoice via Email</h2>
//                 <button
//                   onClick={() => {
//                     setShowEmailModal(false);
//                     setEmailRecipient('');
//                   }}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>
//               <div className="space-y-6">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-2">
//                     Recipient Email
//                   </label>
//                   <input
//                     type="email"
//                     placeholder="Enter email address"
//                     value={emailRecipient}
//                     onChange={(e) => setEmailRecipient(e.target.value)}
//                     className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                   />
//                 </div>
//                 <div className="flex gap-3 pt-4">
//                   <button
//                     onClick={() => {
//                       setShowEmailModal(false);
//                       setEmailRecipient('');
//                     }}
//                     className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={() => handleSendEmail(selectedOrder)}
//                     disabled={!emailRecipient || isSendingEmail}
//                     className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                       !emailRecipient || isSendingEmail ? 'opacity-50 cursor-not-allowed' : ''
//                     }`}
//                   >
//                     {isSendingEmail ? (
//                       <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                     ) : (
//                       'Send Email'
//                     )}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ========== TRANSFER MODAL ========== */}
//         {showTransferModal && transferOrder && (
//           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
//               <div className="flex justify-between items-center mb-4">
//                 <h2 className="text-xl font-bold flex items-center gap-2">
//                   <FaExchangeAlt className="text-purple-600" /> Transfer Options
//                 </h2>
//                 <button
//                   onClick={() => {
//                     setShowTransferModal(false);
//                     setTransferOrder(null);
//                     setTransferActionType(null);
//                     setTransferRoomNumber('');
//                     setTransferBookingId('');
//                     setTransferNewTable('');
//                     setTransferMergeFrom('');
//                     setTransferMergeTo('');
//                   }}
//                   className="p-2 hover:bg-gray-100 rounded-lg"
//                 >
//                   <FaTimes className="w-5 h-5 text-gray-500" />
//                 </button>
//               </div>

//               <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm">
//                 <p>
//                   <strong>Order ID:</strong> {String(transferOrder.order_id || '').slice(0, 20)}...
//                 </p>
//                 <p>
//                   <strong>Customer:</strong> {transferOrder.customer_name || 'Guest'}
//                 </p>
//                 <p>
//                   <strong>Amount:</strong> ₹{transferOrder.total_amount?.toFixed(2) || 0}
//                 </p>
//                 {transferOrder.table_number && (
//                   <p>
//                     <strong>Table:</strong> {transferOrder.table_number}
//                   </p>
//                 )}
//                 {transferOrder.parcel_number && (
//                   <p>
//                     <strong>Parcel:</strong> {transferOrder.parcel_number}
//                   </p>
//                 )}
//               </div>

//               {!transferActionType && (
//                 <div className="space-y-3">
//                   {transferOrder.order_type !== 'room' && (
//                     <button
//                       onClick={() => {
//                         setTransferActionType('to-room');
//                         setTransferRoomNumber('');
//                         setTransferBookingId('');
//                       }}
//                       className="w-full py-3 px-4 bg-pink-50 hover:bg-pink-100 border border-pink-200 rounded-lg flex items-center gap-3"
//                     >
//                       <FaHotel className="text-pink-600" size={20} />
//                       <span className="flex-1 text-left font-medium">Transfer to Room</span>
//                     </button>
//                   )}
//                   <button
//                     onClick={() => {
//                       setTransferActionType('to-table');
//                       setTransferNewTable('');
//                     }}
//                     className="w-full py-3 px-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg flex items-center gap-3"
//                   >
//                     <MdTableBar className="text-indigo-600" size={20} />
//                     <span className="flex-1 text-left font-medium">Transfer to Table</span>
//                   </button>
//                   {transferOrder.order_type === 'dining' && transferOrder.table_number && (
//                     <button
//                       onClick={() => {
//                         setTransferActionType('merge');
//                         setTransferMergeFrom(transferOrder.table_number || '');
//                         setTransferMergeTo('');
//                       }}
//                       className="w-full py-3 px-4 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg flex items-center gap-3"
//                     >
//                       <RiBillLine className="text-amber-600" size={20} />
//                       <span className="flex-1 text-left font-medium">Merge Bills</span>
//                     </button>
//                   )}
//                 </div>
//               )}

//               {transferActionType === 'to-room' && (
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       Room Number *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., 209"
//                       value={transferRoomNumber}
//                       onChange={(e) => setTransferRoomNumber(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                       autoFocus
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       Booking ID (Optional)
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="Enter booking ID (optional)"
//                       value={transferBookingId}
//                       onChange={(e) => setTransferBookingId(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                     />
//                   </div>
//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => {
//                         setTransferActionType(null);
//                         setTransferRoomNumber('');
//                         setTransferBookingId('');
//                       }}
//                       className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                     >
//                       Back
//                     </button>
//                     <button
//                       onClick={
//                         transferOrder.order_type === 'parcel'
//                           ? handleParcelTransferToRoomSubmit
//                           : handleTransferToRoomSubmit
//                       }
//                       disabled={isTransferring || !transferRoomNumber}
//                       className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                         isTransferring || !transferRoomNumber ? 'opacity-50 cursor-not-allowed' : ''
//                       }`}
//                     >
//                       {isTransferring ? (
//                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                       ) : (
//                         'Transfer'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               )}

//               {transferActionType === 'to-table' && (
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       New Table Number *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="Enter new table number"
//                       value={transferNewTable}
//                       onChange={(e) => setTransferNewTable(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                       autoFocus
//                     />
//                   </div>
//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => {
//                         setTransferActionType(null);
//                         setTransferNewTable('');
//                       }}
//                       className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                     >
//                       Back
//                     </button>
//                     <button
//                       onClick={
//                         transferOrder.order_type === 'parcel'
//                           ? handleParcelTransferToTableSubmit
//                           : handleTransferToTableSubmit
//                       }
//                       disabled={isTransferring || !transferNewTable}
//                       className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                         isTransferring || !transferNewTable ? 'opacity-50 cursor-not-allowed' : ''
//                       }`}
//                     >
//                       {isTransferring ? (
//                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                       ) : (
//                         'Transfer'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               )}

//               {transferActionType === 'merge' && (
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       From Table (Source) *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., 101"
//                       value={transferMergeFrom}
//                       onChange={(e) => setTransferMergeFrom(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                       autoFocus
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       To Table (Target) *
//                     </label>
//                     <input
//                       type="text"
//                       placeholder="e.g., 102"
//                       value={transferMergeTo}
//                       onChange={(e) => setTransferMergeTo(e.target.value)}
//                       className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-black"
//                     />
//                   </div>
//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => {
//                         setTransferActionType(null);
//                         setTransferMergeFrom('');
//                         setTransferMergeTo('');
//                       }}
//                       className="flex-1 py-2 border-2 border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
//                     >
//                       Back
//                     </button>
//                     <button
//                       onClick={handleMergeBillsSubmit}
//                       disabled={isTransferring || !transferMergeFrom || !transferMergeTo}
//                       className={`flex-1 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium ${
//                         isTransferring || !transferMergeFrom || !transferMergeTo
//                           ? 'opacity-50 cursor-not-allowed'
//                           : ''
//                       }`}
//                     >
//                       {isTransferring ? (
//                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
//                       ) : (
//                         'Merge'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}

//         <ToastContainer
//           position="bottom-right"
//           autoClose={3000}
//           hideProgressBar={false}
//           newestOnTop
//           closeOnClick
//           rtl={false}
//           pauseOnFocusLoss
//           draggable
//           pauseOnHover
//           theme="light"
//         />
//       </div>
//     </>
//   );
// }

// export default OrderInvoicing;
